import {
  createContext,
  startTransition,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext,
  type ChangeEvent,
  type FocusEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CloseButton, ColorInput, Menu } from "@mantine/core";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type RowSelectionState,
  type SortingState,
} from "@tanstack/react-table";
import {
  Link,
  createBrowserRouter,
  useLocation,
  useNavigate,
  useBlocker,
  useParams,
  RouterProvider,
} from "react-router-dom";

import { fetchJson, installEasyAppBridge, readSessionJson, resolveMountedBasename, writeSessionJson } from "@easy-core";
import { MediaLibraryModal, type MediaLibraryItem } from "@easy-media";
import { EditorPage, EditorHeader, EditorPanel, ImportColumnMapping, AppChrome, AppSecondaryBar, AppSurface } from "@easy-ui";
import type { NewsletterDefaultDesignSnapshot } from "@easy-types";

import "@easy-ui/legacy/appBase.css";
import "./eventPlanner.css";
import { EventCalendarView } from "./EventCalendarView";
import { NewsletterFromEventsBuilder } from "./NewsletterFromEventsBuilder";
import {
  CONNECTOR_OPTIONS,
  FACEBOOK_OPTIONS,
  PUBLICATION_STATUS_OPTIONS,
  countActiveFilters,
} from "./EventFiltersBar";
import { Icon, IconLabel } from "./eventPlannerIcons";
import {
  apiUrl,
  applyBulkEdit,
  buildEventPayload,
  compareEventSchedule,
  connectorStatusClassName,
  connectorStatusLabel,
  configuredConnectorKeys,
  connectorSettingsFromMeta,
  copyTextToClipboard,
  createDefaultCalDAVSettings,
  createDefaultEventPublicPageSettings,
  createDefaultIcsFeedSettings,
  createDefaultEventFilters,
  createEmptyBulkEditForm,
  createEmptyEventForm,
  downloadEventExport,
  downloadImportTemplate,
  eventMatchesFilters,
  composeEventAddress,
  formatEventDate,
  formatEventDateWithWeekdayShort,
  formatEventLocation,
  formatEventPrice,
  hasEnabledBulkEdit,
  hasFieldError,
  inferEventFieldErrors,
  normalizeApiError,
  normalizeEventFilters,
  normalizeImportedTime,
  normalizeInternalName,
  parseImportFile,
  importFields,
  persistNewsletterComposeDraft,
  pluralize,
  publicationStatusClassName,
  publicationStatusLabel,
  resolveFieldErrors,
  suggestNewsletterPeriod,
  statusClassName,
  toEventForm,
  validateEventForm,
  visibleEventConnectors,
  type BulkEditFieldState,
  type BulkEditFormState,
  type CalDAVSettingsRecord,
  type ConnectorSettingsRecord,
  type EventPublicPageSettingsRecord,
  type EventFilters,
  type EventFormState,
  type EventMeta,
  type EventRecord,
  type ImportPreview,
  type IcsFeedSettingsRecord,
  type SavedLocationSuggestion,
  type StatusState,
} from "./eventPlannerUtils";
import {
  buildMonthCells,
  EVENT_CALENDAR_MONTH_TITLE_FORMATTER,
  EVENT_CALENDAR_WEEKDAY_LABELS,
  monthStartFromKey,
  preferredMonthKey,
  shiftMonth,
} from "./eventPlannerCalendar";
import {
  applyFutureSplitToEvent,
  applyOccurrenceOverrideToEvent,
  buildRecurrenceEditorState,
  buildRecurrenceFromEditorState,
  createPresetRecurrence,
  detectQuickRecurrencePreset,
  formatRecurrenceDetail,
  formatRecurrenceSummary,
  hasEventRecurrence,
  hasOccurrenceScheduleChange,
  listUpcomingOccurrences,
  monthlyWeekdayLabel,
  ordinalLabel,
  RECURRENCE_MONTHLY_WEEKDAY_KEYS,
  RECURRENCE_ORDINALS,
  RECURRENCE_WEEKDAY_KEYS,
  normalizeEventRecurrence,
  resolveOccurrenceContext,
  syncRecurrenceWithSeriesForm,
  weekdayLabel,
  type RecurrenceEditorState,
  type RecurrenceOccurrence,
  type RecurrencePresetKey,
} from "./eventPlannerRecurrence";

type FlashState = {
  flash?: StatusState;
};

type EventListViewState = {
  catalogView: "table" | "calendar";
  filters: EventFilters;
  isFiltersPanelOpen: boolean;
  isImportModalOpen: boolean;
  isBulkEditModalOpen: boolean;
  isNewsletterModalOpen: boolean;
  newsletterPeriod: {
    startDate: string;
    endDate: string;
  };
  selectedEventIds: number[];
};

type EventEditorViewState = {
  isEditingDetail: boolean;
  isMediaModalOpen: boolean;
};

type BulkConnectorSyncResult = {
  event_id: number;
  connector_key: string;
  status: "synced" | "failed" | "skipped";
  message?: string;
  resource_url?: string;
  warnings?: unknown[];
};

type BulkConnectorSyncResponse = {
  requested_event_count: number;
  target_connectors: string[];
  synced_count: number;
  failed_count: number;
  skipped_count: number;
  results: BulkConnectorSyncResult[];
};

const RECURRING_EVENTS_V1_ENABLED = false;

type PendingOccurrenceSaveState = {
  nextForm: EventFormState;
  occurrence: RecurrenceOccurrence;
};

const EVENT_LIST_VIEW_STATE_STORAGE_KEY = "easy-event:list-view";

type EventPlannerRouteConfig = {
  listPath: string;
  createPath: string;
  settingsPath: string;
  detailPattern: string;
  eventDetailPath: (eventId: number | string, search?: string) => string;
  newsletterPath: string | null;
};

const EVENT_PLANNER_STANDALONE_ROUTES: EventPlannerRouteConfig = {
  listPath: "/",
  createPath: "/new",
  settingsPath: "/settings",
  detailPattern: "/events/:eventId",
  eventDetailPath: (eventId, search = "") => `/events/${eventId}${search ? `?${search}` : ""}`,
  newsletterPath: null,
};

const EVENT_PLANNER_HOSTED_ROUTES: EventPlannerRouteConfig = {
  listPath: "/events",
  createPath: "/events/new",
  settingsPath: "/settings/events",
  detailPattern: "/events/:eventId",
  eventDetailPath: (eventId, search = "") => `/events/${eventId}${search ? `?${search}` : ""}`,
  newsletterPath: "/newsletter",
};

const EventPlannerRouteContext = createContext<EventPlannerRouteConfig>(EVENT_PLANNER_STANDALONE_ROUTES);

function useEventPlannerRoutes() {
  return useContext(EventPlannerRouteContext);
}

function withEventPlannerRouteConfig(element: ReactNode, routes: EventPlannerRouteConfig) {
  return <EventPlannerRouteContext.Provider value={routes}>{element}</EventPlannerRouteContext.Provider>;
}

function rowSelectionFromIds(ids: number[]): RowSelectionState {
  return ids.reduce<RowSelectionState>((selection, eventId) => {
    if (Number.isFinite(eventId) && eventId > 0) {
      selection[String(eventId)] = true;
    }
    return selection;
  }, {});
}

function selectedIdsFromRowSelection(rowSelection: RowSelectionState) {
  return Object.entries(rowSelection)
    .filter((entry) => entry[1])
    .map(([eventId]) => Number.parseInt(eventId, 10))
    .filter((eventId) => Number.isFinite(eventId) && eventId > 0);
}

function formatBulkConnectorSyncSummary(result: BulkConnectorSyncResponse) {
  const parts: string[] = [];
  const firstWarning = result.results
    .flatMap((entry) => entry.warnings || [])
    .map((warning) => String(warning).trim())
    .find(Boolean);

  if (result.synced_count > 0) {
    parts.push(
      `${result.synced_count} synchronisation${result.synced_count > 1 ? "s" : ""} terminee${
        result.synced_count > 1 ? "s" : ""
      }`
    );
  }
  if (result.failed_count > 0) {
    parts.push(`${result.failed_count} en echec`);
  }
  if (result.skipped_count > 0) {
    parts.push(`${result.skipped_count} ignoree${result.skipped_count > 1 ? "s" : ""}`);
  }
  if (firstWarning) {
    parts.push(firstWarning);
  }

  return parts.length > 0 ? `${parts.join(", ")}.` : "Aucune synchronisation appliquee.";
}

function sortEvents(events: EventRecord[]) {
  return [...events].sort(compareEventSchedule);
}

const EVENT_SORT_OPTIONS = [
  { value: "schedule-asc", label: "Date croissante" },
  { value: "schedule-desc", label: "Date decroissante" },
  { value: "identity-asc", label: "Nom A-Z" },
  { value: "identity-desc", label: "Nom Z-A" },
  { value: "price-asc", label: "Prix croissant" },
  { value: "price-desc", label: "Prix decroissant" },
] as const;

function resolveSortingValue(sorting: SortingState) {
  const activeSort = sorting[0];
  if (!activeSort) {
    return "schedule-asc";
  }

  return `${activeSort.id}-${activeSort.desc ? "desc" : "asc"}`;
}

function resolveSortingState(value: string): SortingState {
  const [id = "schedule", direction = "asc"] = value.split("-");
  return [{ id, desc: direction === "desc" }];
}

function eventTitleTemplateMatches(event: EventRecord, normalizedQuery: string) {
  if (normalizedQuery.length < 3) {
    return false;
  }

  const normalizedTitle = normalizeInternalName(event.display_name);
  const normalizedInternal = normalizeInternalName(event.internal_name);
  return (
    normalizedTitle.includes(normalizedQuery) ||
    normalizedQuery.includes(normalizedTitle) ||
    normalizedInternal.includes(normalizedQuery)
  );
}

function resolveOptionLabel(options: ReadonlyArray<{ value: string; label: string }>, value: string) {
  return options.find((option) => option.value === value)?.label ?? value;
}

function openSelectFromFieldClick(event: ReactMouseEvent<HTMLLabelElement>) {
  if (event.target instanceof HTMLSelectElement) {
    return;
  }

  const select = event.currentTarget.querySelector("select");
  if (!select) {
    return;
  }

  select.focus();
  try {
    select.showPicker();
  } catch {
    // showPicker is unavailable in some browsers or blocked outside direct user activation.
  }
}

function CopyableDetailValue({
  value,
  emptyFallback = "-",
  className,
}: {
  value: string;
  emptyFallback?: string;
  className?: string;
}) {
  const normalizedValue = value.trim();
  const displayValue = normalizedValue || emptyFallback;
  const canCopy = Boolean(normalizedValue);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "error">("idle");
  const resetTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (resetTimerRef.current) {
        window.clearTimeout(resetTimerRef.current);
      }
    };
  }, []);

  async function handleDoubleClick() {
    if (!canCopy) {
      return;
    }

    try {
      await copyTextToClipboard(normalizedValue);
      setCopyState("copied");
    } catch (error) {
      console.warn(error);
      setCopyState("error");
    }

    if (resetTimerRef.current) {
      window.clearTimeout(resetTimerRef.current);
    }

    resetTimerRef.current = window.setTimeout(() => {
      setCopyState("idle");
      resetTimerRef.current = null;
    }, 1400);
  }

  return (
    <span
      className={`event-detail-copy-value${canCopy ? " is-copyable" : ""}${copyState !== "idle" ? ` is-${copyState}` : ""}${
        className ? ` ${className}` : ""
      }`}
      onDoubleClick={handleDoubleClick}
      title={canCopy ? "Double-cliquez pour copier" : undefined}
    >
      <span>{displayValue}</span>
      {copyState === "copied" ? <em className="event-detail-copy-feedback">Copie</em> : null}
      {copyState === "error" ? <em className="event-detail-copy-feedback">Échec</em> : null}
    </span>
  );
}

function upsertEvent(events: EventRecord[], savedEvent: EventRecord) {
  const nextEvents = events.filter((event) => event.id !== savedEvent.id);
  nextEvents.push(savedEvent);
  return sortEvents(nextEvents);
}

function buildOccurrenceEditForm(event: EventRecord, occurrence: RecurrenceOccurrence): EventFormState {
  return {
    ...toEventForm(event),
    date: occurrence.date,
    start_time: occurrence.start_time,
    end_time: occurrence.end_time,
    timezone: occurrence.timezone,
  };
}

function todayIso() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function SortableHeader({
  column,
  label,
}: {
  column: {
    getCanSort: () => boolean;
    getIsSorted: () => false | "asc" | "desc";
    toggleSorting: (desc?: boolean) => void;
  };
  label: string;
}) {
  if (!column.getCanSort()) {
    return <span>{label}</span>;
  }

  const direction = column.getIsSorted();
  const directionLabel = direction === "asc" ? "Asc" : direction === "desc" ? "Desc" : "";

  return (
    <button
      type="button"
      className="column-header-button"
      onClick={() => column.toggleSorting(direction === "asc")}
    >
      <span>{label}</span>
      <span className="column-sort-indicator">{directionLabel}</span>
    </button>
  );
}

function ConnectorCluster({ connectors }: { connectors: EventRecord["connectors"] }) {
  return (
    <div className="connector-cluster">
      {connectors.map((connector) => (
        <span key={connector.connector_key} className={connectorStatusClassName(connector.status)}>
          {connector.label} · {connectorStatusLabel(connector.status)}
        </span>
      ))}
    </div>
  );
}

function AppToolbar({
  leading,
  actions,
}: {
  leading?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="app-toolbar">
      <div className="app-toolbar-title">{leading}</div>
      {actions ? <div className="app-toolbar-actions">{actions}</div> : null}
    </header>
  );
}

function ReturnToCatalogLink({ className = "button app-toolbar-back-button" }: { className?: string }) {
  const routes = useEventPlannerRoutes();
  return (
    <Link to={routes.listPath} className={className} aria-label="Retour au catalogue" title="Retour au catalogue">
      <IconLabel icon="arrow-left" label="Retour au catalogue" />
    </Link>
  );
}

function ChronologyArrowButton({
  direction,
  targetEvent,
  onNavigate,
}: {
  direction: "previous" | "next";
  targetEvent: EventRecord | null;
  onNavigate: (eventId: number) => void;
}) {
  const tooltipLabel = direction === "previous" ? "Évènement précédent" : "Évènement suivant";
  const scheduleLabel = targetEvent
    ? [
        targetEvent.date ? formatEventDate(targetEvent.date) : "Date a definir",
        targetEvent.start_time || "Heure a definir",
      ]
        .filter(Boolean)
        .join(" · ")
    : "";

  return (
    <div className="event-chronology-arrow-wrap">
      <button
        type="button"
        className={`event-chronology-arrow-button${direction === "next" ? " is-next" : ""}`}
        aria-label={tooltipLabel}
        disabled={!targetEvent}
        onClick={() => {
          if (targetEvent) {
            onNavigate(targetEvent.id);
          }
        }}
      >
        <Icon name="arrow-left" className={`ui-icon${direction === "next" ? " is-next" : ""}`} />
      </button>
      {targetEvent ? (
        <div className={`event-chronology-tooltip${direction === "next" ? " is-next" : ""}`} role="tooltip">
          <span>{tooltipLabel}</span>
          <strong>{targetEvent.display_name}</strong>
          <span>{scheduleLabel}</span>
        </div>
      ) : null}
    </div>
  );
}

function EventChronologyNavigator({
  events,
  currentEvent,
  onNavigate,
}: {
  events: EventRecord[];
  currentEvent: EventRecord;
  onNavigate: (eventId: number) => void;
}) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const todayMarker = useMemo(() => todayIso(), []);
  const scheduledEvents = useMemo(() => events.filter((event) => Boolean(event.date.trim())), [events]);
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(() =>
    currentEvent.date ? currentEvent.date.slice(0, 7) : preferredMonthKey(scheduledEvents, todayMarker)
  );
  const [selectedDate, setSelectedDate] = useState(() => currentEvent.date || scheduledEvents[0]?.date || "");

  const currentIndex = events.findIndex((event) => event.id === currentEvent.id);
  const previousEvent = currentIndex > 0 ? events[currentIndex - 1] : null;
  const nextEvent = currentIndex >= 0 && currentIndex < events.length - 1 ? events[currentIndex + 1] : null;

  const eventsByDate = useMemo(() => {
    const grouped = new Map<string, EventRecord[]>();
    scheduledEvents.forEach((event) => {
      grouped.set(event.date, [...(grouped.get(event.date) || []), event]);
    });
    return grouped;
  }, [scheduledEvents]);

  const monthCells = useMemo(() => buildMonthCells(visibleMonth), [visibleMonth]);
  const visibleMonthStart = useMemo(() => monthStartFromKey(visibleMonth), [visibleMonth]);
  const selectedDateEvents = selectedDate ? eventsByDate.get(selectedDate) || [] : [];

  useEffect(() => {
    const nextMonth = currentEvent.date ? currentEvent.date.slice(0, 7) : preferredMonthKey(scheduledEvents, todayMarker);
    setVisibleMonth(nextMonth);
    setSelectedDate(currentEvent.date || scheduledEvents.find((event) => event.date.startsWith(nextMonth))?.date || "");
  }, [currentEvent.date, currentEvent.id, scheduledEvents, todayMarker]);

  useEffect(() => {
    if (!isSelectorOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!panelRef.current?.contains(event.target as Node)) {
        setIsSelectorOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsSelectorOpen(false);
      }
    };

    window.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isSelectorOpen]);

  function selectMonth(monthKey: string) {
    setVisibleMonth(monthKey);
    setSelectedDate(scheduledEvents.find((event) => event.date.startsWith(monthKey))?.date || "");
  }

  function handleNavigate(eventId: number) {
    setIsSelectorOpen(false);
    onNavigate(eventId);
  }

  return (
    <section className="event-chronology-panel" aria-label="Navigation chronologique">
      <div className="event-chronology-bar">
        <div className="event-chronology-controls">
          <ChronologyArrowButton direction="previous" targetEvent={previousEvent} onNavigate={handleNavigate} />
          <div className="event-chronology-picker" ref={panelRef}>
            <button
              type="button"
              className={`event-chronology-date-button${isSelectorOpen ? " is-open" : ""}`}
              aria-expanded={isSelectorOpen}
              aria-haspopup="dialog"
              aria-label="Choisir un évènement par date"
              onClick={() => setIsSelectorOpen((current) => !current)}
            >
              <span>{currentEvent.date ? formatEventDate(currentEvent.date) : "Choisir une date"}</span>
              <Icon name="chevron-down" className="ui-icon" />
            </button>

            {isSelectorOpen ? (
              <div className="event-chronology-popover" role="dialog" aria-label="Choisir un évènement par date">
                <div className="event-chronology-popover-head">
                  <button
                    type="button"
                    className="event-chronology-month-button"
                    aria-label="Mois precedent"
                    onClick={() => selectMonth(shiftMonth(visibleMonth, -1))}
                  >
                    <Icon name="arrow-left" />
                  </button>
                  <strong>{EVENT_CALENDAR_MONTH_TITLE_FORMATTER.format(visibleMonthStart)}</strong>
                  <button
                    type="button"
                    className="event-chronology-month-button"
                    aria-label="Mois suivant"
                    onClick={() => selectMonth(shiftMonth(visibleMonth, 1))}
                  >
                    <Icon name="arrow-left" className="ui-icon is-next" />
                  </button>
                </div>

                <div className="event-chronology-popover-body">
                  <div className="event-chronology-calendar-grid" role="grid" aria-label="Mini calendrier des évènements">
                    {EVENT_CALENDAR_WEEKDAY_LABELS.map((label) => (
                      <div key={label} className="event-chronology-weekday" role="columnheader">
                        {label}
                      </div>
                    ))}

                    {monthCells.map((cell) => {
                      const dayEvents = eventsByDate.get(cell.iso) || [];
                      const isCurrentEventDate = currentEvent.date === cell.iso;
                      const isSelectedDate = selectedDate === cell.iso;

                      return (
                        <button
                          key={cell.iso}
                          type="button"
                          className={[
                            "event-chronology-day",
                            cell.isCurrentMonth ? "is-current-month" : "is-outside-month",
                            dayEvents.length > 0 ? "has-events" : "",
                            isSelectedDate ? "is-selected" : "",
                            isCurrentEventDate ? "is-current-event" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          aria-label={
                            dayEvents.length > 0
                              ? `${formatEventDate(cell.iso)} · ${dayEvents.length} évènement${dayEvents.length > 1 ? "s" : ""}`
                              : formatEventDate(cell.iso)
                          }
                          disabled={dayEvents.length === 0}
                          onClick={() => setSelectedDate(cell.iso)}
                        >
                          <span>{cell.date.getDate()}</span>
                          {dayEvents.length > 0 ? <i aria-hidden="true" /> : null}
                        </button>
                      );
                    })}
                  </div>

                  <div className="event-chronology-date-events">
                    <p className="event-chronology-date-events-title">
                      {selectedDate ? formatEventDate(selectedDate) : "Aucun évènement dans ce mois"}
                    </p>
                    {selectedDateEvents.length > 0 ? (
                      <div className="event-chronology-date-events-list">
                        {selectedDateEvents.map((event) => (
                          <button
                            key={event.id}
                            type="button"
                            className={`event-chronology-date-event${event.id === currentEvent.id ? " is-current" : ""}`}
                            onClick={() => handleNavigate(event.id)}
                          >
                            <strong>{event.display_name}</strong>
                            <span>
                              {[event.start_time || "Heure a definir", formatEventLocation(event)].filter(Boolean).join(" · ")}
                            </span>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="muted">Sélectionnez une date marquée pour ouvrir rapidement un autre évènement.</p>
                    )}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
          <ChronologyArrowButton direction="next" targetEvent={nextEvent} onNavigate={handleNavigate} />
        </div>

        <span className="event-chronology-count">
          {currentIndex >= 0 ? `${currentIndex + 1} / ${events.length}` : `1 / ${events.length}`}
        </span>
      </div>
    </section>
  );
}

function ModalShell({
  isOpen,
  title,
  subtitle,
  dialogClassName,
  onClose,
  children,
}: {
  isOpen: boolean;
  title: string;
  subtitle?: string;
  dialogClassName?: string;
  onClose: () => void;
  children: ReactNode;
}) {
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="event-modal-backdrop" onClick={onClose}>
      <div
        className={`event-modal${dialogClassName ? ` ${dialogClassName}` : ""}`}
        role="dialog" aria-label={title}
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="event-modal-head">
          <div>
            <p className="section-kicker">Action</p>
            <h2>{title}</h2>
            {subtitle ? <p className="muted">{subtitle}</p> : null}
          </div>
          <button type="button" className="button" onClick={onClose}>
            Fermer
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function RecurrenceEditorModal({
  isOpen,
  summary,
  anchorDate,
  editorState,
  previewOccurrences,
  onClose,
  onApply,
  onChange,
}: {
  isOpen: boolean;
  summary: string;
  anchorDate: string;
  editorState: RecurrenceEditorState;
  previewOccurrences: RecurrenceOccurrence[];
  onClose: () => void;
  onApply: () => void;
  onChange: (updater: (current: RecurrenceEditorState) => RecurrenceEditorState) => void;
}) {
  const frequencyOptions: Array<{ key: RecurrenceEditorState["frequency"]; label: string }> = [
    { key: "daily", label: "tous les jours" },
    { key: "weekly", label: "toutes les semaines" },
    { key: "monthly", label: "tous les mois" },
    { key: "yearly", label: "tous les ans" },
  ];
  const weekdayShortLabels: Record<(typeof RECURRENCE_WEEKDAY_KEYS)[number], string> = {
    monday: "L",
    tuesday: "M",
    wednesday: "M",
    thursday: "J",
    friday: "V",
    saturday: "S",
    sunday: "D",
  };
  const intervalUnitLabel =
    editorState.frequency === "daily"
      ? editorState.interval > 1
        ? "jours"
        : "jour"
      : editorState.frequency === "weekly"
        ? editorState.interval > 1
          ? "semaines"
          : "semaine"
        : editorState.frequency === "monthly"
          ? "mois"
          : editorState.interval > 1
            ? "ans"
            : "an";
  const intervalPrefix = editorState.frequency === "weekly" ? "toutes les" : "tous les";
  const monthlyDays = Array.from({ length: 31 }, (_value, index) => index + 1);

  return (
    <ModalShell
      isOpen={isOpen}
      title="Recurrence"
      dialogClassName="recurrence-modal-shell"
      onClose={onClose}
    >
      <div className="event-modal-body recurrence-modal-body">
        <div className="recurrence-apple-header">
          <label className="recurrence-apple-main-select">
            <span>Recurrence :</span>
            <select
              value={editorState.frequency}
              onChange={(event) =>
                onChange((current) => ({
                  ...current,
                  frequency: event.target.value as RecurrenceEditorState["frequency"],
                }))
              }
            >
              {frequencyOptions.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="recurrence-apple-panel">
          <div className="recurrence-apple-rule-line">
            <span>{intervalPrefix}</span>
            <input
              type="number"
              min={1}
              max={366}
              value={editorState.interval}
              onChange={(event) =>
                onChange((current) => ({
                  ...current,
                  interval: Number.parseInt(event.target.value || "1", 10) || 1,
                }))
              }
            />
            <span>{intervalUnitLabel}</span>
            {editorState.frequency === "weekly" ? <span>le :</span> : null}
          </div>

          {editorState.frequency === "weekly" ? (
            <div className="recurrence-apple-weekday-grid">
              {RECURRENCE_WEEKDAY_KEYS.map((weekdayKey) => {
                const isActive = editorState.weekly_days.includes(weekdayKey);
                return (
                  <button
                    key={weekdayKey}
                    type="button"
                    className={`recurrence-apple-daycell${isActive ? " is-active" : ""}`}
                    title={weekdayLabel(weekdayKey)}
                    onClick={() =>
                      onChange((current) => {
                        const isSelected = current.weekly_days.includes(weekdayKey);
                        const nextDays = isSelected
                          ? current.weekly_days.filter((day) => day !== weekdayKey)
                          : [...current.weekly_days, weekdayKey];
                        return {
                          ...current,
                          weekly_days: nextDays.length > 0 ? nextDays : [weekdayKey],
                        };
                      })
                    }
                  >
                    {weekdayShortLabels[weekdayKey]}
                  </button>
                );
              })}
            </div>
          ) : null}

          {editorState.frequency === "monthly" ? (
            <div className="recurrence-apple-monthly-stack">
              <label className="recurrence-apple-choice-row">
                <input
                  type="radio"
                  checked={editorState.monthly_mode === "day_of_month"}
                  onChange={() =>
                    onChange((current) => ({
                      ...current,
                      monthly_mode: "day_of_month",
                    }))
                  }
                />
                <span>Tous les</span>
              </label>
              <div className="recurrence-apple-monthday-grid" aria-hidden={editorState.monthly_mode !== "day_of_month"}>
                {monthlyDays.map((day) => (
                  <button
                    key={day}
                    type="button"
                    className={`recurrence-apple-daynumber${
                      editorState.monthly_mode === "day_of_month" && editorState.day_of_month === day ? " is-active" : ""
                    }`}
                    onClick={() =>
                      onChange((current) => ({
                        ...current,
                        monthly_mode: "day_of_month",
                        day_of_month: day,
                      }))
                    }
                  >
                    {day}
                  </button>
                ))}
              </div>

              <label className="recurrence-apple-choice-row">
                <input
                  type="radio"
                  checked={editorState.monthly_mode === "ordinal_weekday"}
                  onChange={() =>
                    onChange((current) => ({
                      ...current,
                      monthly_mode: "ordinal_weekday",
                    }))
                  }
                />
                <span>Le</span>
              </label>
              <div className="recurrence-apple-ordinal-row">
                <select
                  value={editorState.ordinal}
                  disabled={editorState.monthly_mode !== "ordinal_weekday"}
                  onChange={(event) =>
                    onChange((current) => ({
                      ...current,
                      monthly_mode: "ordinal_weekday",
                      ordinal: event.target.value as RecurrenceEditorState["ordinal"],
                    }))
                  }
                >
                  {RECURRENCE_ORDINALS.map((ordinalKey) => (
                    <option key={ordinalKey} value={ordinalKey}>
                      {ordinalLabel(ordinalKey)}
                    </option>
                  ))}
                </select>
                <select
                  value={editorState.monthly_weekday}
                  disabled={editorState.monthly_mode !== "ordinal_weekday"}
                  onChange={(event) =>
                    onChange((current) => ({
                      ...current,
                      monthly_mode: "ordinal_weekday",
                      monthly_weekday: event.target.value as RecurrenceEditorState["monthly_weekday"],
                    }))
                  }
                >
                  {RECURRENCE_MONTHLY_WEEKDAY_KEYS.map((weekdayKey) => (
                    <option key={weekdayKey} value={weekdayKey}>
                      {monthlyWeekdayLabel(weekdayKey)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : null}

          {editorState.frequency === "yearly" ? (
            <div className="recurrence-apple-anchor-note">
              <strong>{anchorDate ? formatEventDate(anchorDate) : "Ajoutez une date à l'évènement"}</strong>
              <span>Le jour et le mois de répétition suivent la date de l'évènement.</span>
            </div>
          ) : null}
        </div>

        <div className="recurrence-preview-panel recurrence-preview-panel-inline">
          <div className="recurrence-preview-head">
            <h3 className="event-form-section-title">Exemples</h3>
            <span className="recurrence-preview-summary">{summary}</span>
          </div>
          <div className="recurrence-preview-list">
            {previewOccurrences.length > 0 ? (
              previewOccurrences.map((occurrence, index) => (
                <div key={occurrence.key} className="recurrence-preview-item">
                  <div className="recurrence-preview-item-index">{index + 1}</div>
                  <div className="recurrence-preview-item-copy">
                    <strong>{formatEventDate(occurrence.date)}</strong>
                    <span>
                      {[occurrence.start_time || "Heure a definir", occurrence.end_time || ""].filter(Boolean).join(" - ")}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <p className="muted">Ajoutez une date sur l'évènement pour prévisualiser les prochaines occurrences.</p>
            )}
          </div>
        </div>

        <div className="event-modal-actions">
          <button type="button" className="button" onClick={onClose}>
            Annuler
          </button>
          <button type="button" className="button button-primary" onClick={onApply}>
            Appliquer
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function OccurrenceScopeModal({
  isOpen,
  occurrence,
  onClose,
  onConfirmOccurrence,
  onConfirmFuture,
}: {
  isOpen: boolean;
  occurrence: RecurrenceOccurrence | null;
  onClose: () => void;
  onConfirmOccurrence: () => void;
  onConfirmFuture: () => void;
}) {
  return (
    <ModalShell
      isOpen={isOpen}
      title="L'évènement que vous modifiez est récurrent"
      subtitle={
        occurrence
          ? `Souhaitez-vous déplacer uniquement l'occurrence du ${formatEventDate(occurrence.date)} ou appliquer le changement à tous les évènements futurs ?`
          : ""
      }
      onClose={onClose}
    >
      <div className="event-modal-body occurrence-scope-body">
        <div className="occurrence-scope-actions">
          <button type="button" className="button button-primary" onClick={onConfirmOccurrence}>
            Uniquement cette occurrence
          </button>
          <button type="button" className="button" onClick={onConfirmFuture}>
            Tous les évènements futurs
          </button>
          <button type="button" className="button" onClick={onClose}>
            Annuler
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function BulkFieldRow({
  label,
  field,
  onToggle,
  children,
}: {
  label: string;
  field: BulkEditFieldState;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <div className="bulk-field-row">
      <label className="inline-toggle">
        <input type="checkbox" checked={field.enabled} onChange={onToggle} />
        <span>{label}</span>
      </label>
      {children}
    </div>
  );
}

function usePlannerData() {
  const metaQuery = useQuery({
    queryKey: ["event-planner-meta"],
    queryFn: () => fetchJson<EventMeta>(apiUrl("meta")),
  });
  const eventsQuery = useQuery({
    queryKey: ["event-planner-events"],
    queryFn: () => fetchJson<EventRecord[]>(apiUrl("events")),
  });

  const events = useMemo(() => sortEvents(eventsQuery.data || []), [eventsQuery.data]);

  return {
    metaQuery,
    eventsQuery,
    events,
  };
}

type ConnectorWireframeDescriptor = {
  category: string;
  summary: string;
  parameterHint: string;
  visibilityLabel: string;
  endpointLabel?: string;
  endpointPlaceholder?: string;
  referenceLabel?: string;
  referencePlaceholder?: string;
  usernameLabel?: string;
  usernamePlaceholder?: string;
  secretLabel?: string;
  secretPlaceholder?: string;
  notesPlaceholder?: string;
  utility?: {
    title: string;
    description: string;
    actionLabel?: string;
    hint?: string;
    disabled?: boolean;
  };
  tutorial?: {
    title: string;
    steps: string[];
  };
};

const CONNECTOR_WIREFRAME_DESCRIPTORS: Record<string, ConnectorWireframeDescriptor> = {
  odoo: {
    category: "ERP",
    summary:
      "Synchronise les évènements publiés vers Odoo pour centraliser le catalogue et préparer la publication côté back-office.",
    parameterHint:
      "Le panneau de droite regroupe la configuration technique, le téléchargement du module Odoo compagnon et le tutoriel d'installation.",
    visibilityLabel: "Afficher cette connexion dans les fiches évènement et les états de diffusion",
    endpointLabel: "URL de base Odoo",
    endpointPlaceholder: "http://127.0.0.1:8069",
    referenceLabel: "Base Odoo",
    referencePlaceholder: "easy_event_odoo_test",
    notesPlaceholder: "Ex. publish_on_website=true, remarques de mapping, modules reliés...",
    utility: {
      title: "Module Odoo à télécharger",
      description:
        "Télécharge l'archive ZIP du module `easy_event_odoo`, puis décompresse-la dans un dossier présent dans le `addons_path` de ton instance Odoo.",
      actionLabel: "Télécharger l'addon Odoo",
      hint: "Le ZIP contient directement le dossier `easy_event_odoo`, prêt à être copié dans un répertoire d'addons custom Odoo 18.",
    },
    tutorial: {
      title: "Tutoriel d'installation",
      steps: [
        "Clique sur Télécharger l'addon Odoo puis dézippe l'archive sur la machine qui héberge Odoo.",
        "Copie le dossier `easy_event_odoo` dans un répertoire déclaré dans `addons_path` comme `/mnt/extra-addons` ou un dossier custom addons.",
        "Redémarre Odoo, puis ouvre Apps et lance Update Apps List si le module n'apparaît pas encore.",
        "Recherche `Easy Event Odoo`, vérifie que le module dépend bien de `event`, `website` et `website_event`, puis installe-le.",
        "Reviens dans Relyo et renseigne l'URL de base Odoo, le nom de la base, l'identifiant et le mot de passe ou token XML-RPC.",
        "Publie un évènement test, lance Synchroniser Odoo depuis la fiche évènement et contrôle le rendu dans Odoo.",
      ],
    },
  },
  wordpress: {
    category: "CMS",
    summary:
      "Publie les évènements vers un site ou un espace éditorial WordPress avec un paramétrage simple et lisible.",
    parameterHint:
      "Le détail doit rester souple pour différents usages: site unique, multisite, plugin éditorial ou endpoint REST.",
    visibilityLabel: "Afficher cette connexion dans les fiches évènement et la vue détail",
    endpointLabel: "Endpoint WordPress",
    endpointPlaceholder: "https://site.example.com/wp-json/...",
    referenceLabel: "Site ou espace cible",
    referencePlaceholder: "Nom du site, blog ou environnement",
    notesPlaceholder: "Notes de publication, taxonomies, statuts cibles, remarques de mapping...",
    tutorial: {
      title: "Tutoriel d'usage",
      steps: [
        "Renseigner l'endpoint ou l'URL du site cible.",
        "Associer le compte, l'espace ou la référence de publication.",
        "Documenter les règles éditoriales et les limites connues dans les notes.",
      ],
    },
  },
  browser_plugin: {
    category: "Plugin navigateur",
    summary:
      "Diffuse les évènements vers Facebook via l'extension Chrome du hosted, avec validation finale dans Facebook.",
    parameterHint:
      "Le détail se limite au téléchargement du plugin, au token d'accès et aux étapes d'installation.",
    visibilityLabel: "Afficher le plugin dans les fiches évènement et les actions de diffusion",
    endpointLabel: "URL du hosted détectée automatiquement",
    endpointPlaceholder: "https://app.relyoapp.fr",
    referenceLabel: "Compte ou espace",
    referencePlaceholder: "Page, organisation ou environnement cible",
    usernameLabel: "Identifiant operateur",
    usernamePlaceholder: "Optionnel: email, nom du compte ou page cible",
    secretLabel: "Token de l'extension",
    secretPlaceholder: "Copie ce token dans les options du plugin",
    notesPlaceholder: "Etapes manuelles, controles a faire, limites connues, remarques d'usage...",
    utility: {
      title: "Utilitaire à télécharger",
      description:
        "Télécharge l'archive du plugin Chrome prête à dézipper, puis charge-la dans Chrome en mode développeur.",
      actionLabel: "Télécharger l'extension",
      hint: "Ensuite: dézip, chrome://extensions, mode développeur, Charger l'extension non empaquetée.",
    },
    tutorial: {
      title: "Tutoriel d'installation",
      steps: [
        "Clique sur Télécharger l'extension puis dézippe l'archive obtenue.",
        "Ouvre chrome://extensions puis active le mode développeur en haut à droite.",
        "Clique sur Charger l'extension non empaquetée et sélectionne le dossier easy_event_facebook extrait de l'archive.",
        "Dans les options du plugin, vérifie l'URL du hosted proposée puis colle le token affiché ici.",
        "Ouvre ensuite Facebook, charge un évènement depuis le panneau Easy Event, puis finalise manuellement la création avant Marquer synchronisé.",
      ],
    },
  },
  caldav: {
    category: "Calendrier",
    summary:
      "Synchronise les évènements vers un agenda CalDAV depuis la même zone de paramétrage que les autres connexions.",
    parameterHint:
      "Le détail se concentre sur l'accès au calendrier, le fuseau et la rétention du secret déjà enregistré.",
    visibilityLabel: "Afficher la synchronisation calendrier dans les fiches évènement",
    utility: {
      title: "Utilitaire",
      description:
        "Aucun utilitaire à installer pour cette connexion: l'accès se fait directement via l'URL CalDAV et un mot de passe d'application.",
    },
    tutorial: {
      title: "Tutoriel de connexion",
      steps: [
        "Copier l'URL de collection CalDAV depuis le fournisseur de calendrier.",
        "Renseigner l'identifiant, le fuseau et le mot de passe d'application.",
        "Vérifier ensuite la publication d'un évènement test depuis la fiche détail.",
      ],
    },
  },
  ics_feed: {
    category: "Flux public",
    summary:
      "Expose un flux ICS public en lecture seule pour les sites, agendas collaboratifs et outils qui savent s'abonner à un lien iCal.",
    parameterHint:
      "Le détail fournit le titre du calendrier, l'URL publique à partager et la possibilité de régénérer le lien si besoin.",
    visibilityLabel: "Activer le flux ICS public pour les évènements publiés",
    utility: {
      title: "Lien public",
      description:
        "Partage cette URL avec les sites ou services qui importent un calendrier ICS en lecture seule.",
      actionLabel: "Ouvrir le flux ICS",
      hint: "Le flux expose uniquement les évènements publiés. Les séries récurrentes détaillées viendront ensuite.",
    },
    tutorial: {
      title: "Tutoriel de diffusion",
      steps: [
        "Choisis un titre de calendrier clair pour la destination.",
        "Active le flux puis copie l'URL publique générée.",
        "Transmets cette URL au site cible ou colle-la dans l'outil d'abonnement ICS.",
      ],
    },
  },
};

function connectorUtilityHref(connectorKey: string) {
  if (connectorKey === "odoo") {
    return apiUrl("connectors/odoo/download");
  }
  if (connectorKey === "browser_plugin") {
    return apiUrl("connectors/browser-plugin/download");
  }
  return null;
}

function resolveBrowserPluginEndpointUrl(config?: { endpoint_url?: string | null }) {
  const savedEndpoint = String(config?.endpoint_url || "").trim();
  if (savedEndpoint) {
    return savedEndpoint;
  }
  if (typeof window === "undefined") {
    return "";
  }
  const currentOrigin = String(window.location.origin || "").trim();
  return currentOrigin && currentOrigin !== "null" ? currentOrigin : "";
}

function normalizeConnectorSettingsForSave(
  connector: ConnectorSettingsRecord,
  browserPluginEndpointUrl: string
): ConnectorSettingsRecord {
  if (connector.connector_key !== "browser_plugin") {
    return connector;
  }
  return {
    ...connector,
    endpoint_url: browserPluginEndpointUrl || String(connector.endpoint_url || "").trim(),
    external_reference: "",
    username: "",
  };
}

function EventListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const routes = useEventPlannerRoutes();
  const queryClient = useQueryClient();
  const storedInitialViewState = readSessionJson<EventListViewState>(EVENT_LIST_VIEW_STATE_STORAGE_KEY, {
    catalogView: "table",
    filters: createDefaultEventFilters(),
    isFiltersPanelOpen: false,
    isImportModalOpen: false,
    isBulkEditModalOpen: false,
    isNewsletterModalOpen: false,
    newsletterPeriod: { startDate: "", endDate: "" },
    selectedEventIds: [],
  });
  const initialViewState = {
    ...storedInitialViewState,
    filters: normalizeEventFilters(storedInitialViewState.filters),
  };
  const { metaQuery, eventsQuery, events } = usePlannerData();
  const [sorting, setSorting] = useState<SortingState>([{ id: "schedule", desc: false }]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(() =>
    rowSelectionFromIds(initialViewState.selectedEventIds)
  );
  const lastSelectedEventIdRef = useRef<string | null>(null);
  const visibleRowIdsRef = useRef<string[]>([]);
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [activeDuplicateEventId, setActiveDuplicateEventId] = useState<number | null>(null);
  const [inlineForm, setInlineForm] = useState<EventFormState>(createEmptyEventForm());
  const [inlineFieldErrors, setInlineFieldErrors] = useState<string[]>([]);
  const [collectionStatus, setCollectionStatus] = useState<StatusState>({ message: "", tone: "neutral" });
  const [filters, setFilters] = useState<EventFilters>(initialViewState.filters);
  const [catalogView, setCatalogView] = useState<"table" | "calendar">(initialViewState.catalogView);
  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(initialViewState.isFiltersPanelOpen);
  const [isImportModalOpen, setIsImportModalOpen] = useState(initialViewState.isImportModalOpen);
  const [isImportMenuOpen, setIsImportMenuOpen] = useState(false);
  const [isBulkEditModalOpen, setIsBulkEditModalOpen] = useState(initialViewState.isBulkEditModalOpen);
  const [isNewsletterModalOpen, setIsNewsletterModalOpen] = useState(initialViewState.isNewsletterModalOpen);
  const [bulkEditForm, setBulkEditForm] = useState<BulkEditFormState>(createEmptyBulkEditForm());
  const [isBulkLocationNameFocused, setIsBulkLocationNameFocused] = useState(false);
  const [importPreview, setImportPreview] = useState<ImportPreview | null>(null);
  const [importStatus, setImportStatus] = useState<StatusState>({ message: "", tone: "neutral" });
  const [newsletterPeriod, setNewsletterPeriod] = useState(initialViewState.newsletterPeriod);
  const [isEmbeddedInWorkspace, setIsEmbeddedInWorkspace] = useState(false);
  const [isParsingImport, setIsParsingImport] = useState(false);
  const [importFileName, setImportFileName] = useState("");
  const [importFile, setImportFile] = useState<File | null>(null);
  const [isMappingConfirmed, setIsMappingConfirmed] = useState(false);
  const [importMappingError, setImportMappingError] = useState("");
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const importMenuRef = useRef<HTMLDivElement | null>(null);
  const bridgeRef = useRef<ReturnType<typeof installEasyAppBridge> | null>(null);
  const deferredSearch = useDeferredValue(filters.search);
  const deferredBulkLocationName = useDeferredValue(bulkEditForm.location_name.value.trim());
  const canOpenNewsletterComposer = isEmbeddedInWorkspace || Boolean(routes.newsletterPath);
  const newsletterDefaultDesignQuery = useQuery({
    queryKey: ["newsletter-default-design"],
    queryFn: () =>
      fetchJson<{ default_design: NewsletterDefaultDesignSnapshot | Record<string, never> | null }>(
        "/api/newsletter/default-design"
      ),
    enabled: canOpenNewsletterComposer,
    retry: false,
  });
  const effectiveFilters = useMemo(() => ({ ...filters, search: deferredSearch }), [filters, deferredSearch]);
  const configuredKeys = useMemo(() => configuredConnectorKeys(metaQuery.data), [metaQuery.data]);
  const hasConfiguredConnectors = configuredKeys.size > 0;
  const caldavConfigQuery = useQuery({
    queryKey: ["event-planner-caldav-config"],
    queryFn: () => fetchJson<CalDAVSettingsRecord>(apiUrl("connectors/caldav/config")),
    retry: false,
  });
  const icsFeedConfigQuery = useQuery({
    queryKey: ["event-planner-ics-feed-config"],
    queryFn: () => fetchJson<IcsFeedSettingsRecord>(apiUrl("connectors/ics-feed/config")),
    retry: false,
  });
  const publicPageConfigQuery = useQuery({
    queryKey: ["event-public-page-settings"],
    queryFn: () => fetchJson<EventPublicPageSettingsRecord>(apiUrl("events/public-page/config")),
    retry: false,
  });
  const hasConfiguredDistributionMode =
    hasConfiguredConnectors ||
    Boolean(caldavConfigQuery.data?.configured) ||
    Boolean(icsFeedConfigQuery.data?.configured) ||
    Boolean(publicPageConfigQuery.data?.enabled);
  const isDistributionConfigLoading =
    caldavConfigQuery.isLoading || icsFeedConfigQuery.isLoading || publicPageConfigQuery.isLoading;
  const showConnectorSetupAlert =
    !eventsQuery.isLoading && !isDistributionConfigLoading && !hasConfiguredDistributionMode;
  const showCatalogBlankState = !eventsQuery.isLoading && events.length === 0;
  const visibleConnectorsByEventId = useMemo(
    () => new Map(events.map((event) => [event.id, visibleEventConnectors(event, configuredKeys)])),
    [events, configuredKeys]
  );

  const catalogFilters = useMemo<EventFilters>(
    () => (catalogView === "calendar" ? { ...effectiveFilters, schedule: "all" } : effectiveFilters),
    [catalogView, effectiveFilters]
  );
  const filteredEvents = useMemo(
    () =>
      events.filter((event) =>
        eventMatchesFilters(event, catalogFilters, todayIso(), visibleConnectorsByEventId.get(event.id) || [])
      ),
    [events, catalogFilters, visibleConnectorsByEventId]
  );
  const selectedEvents = useMemo(() => {
    const selectedIds = new Set(Object.keys(rowSelection).filter((key) => rowSelection[key]));
    return events.filter((event) => selectedIds.has(String(event.id)));
  }, [events, rowSelection]);
  const newsletterSuggestedPeriod = useMemo(() => suggestNewsletterPeriod(events, todayIso()), [events]);
  const newsletterDefaultDesign =
    newsletterDefaultDesignQuery.data?.default_design &&
    "schema_version" in newsletterDefaultDesignQuery.data.default_design
      ? (newsletterDefaultDesignQuery.data.default_design as NewsletterDefaultDesignSnapshot)
      : null;
  const bulkLocationAddressPreview = useMemo(
    () =>
      composeEventAddress({
        location_street_number: bulkEditForm.location_street_number.value,
        location_street: bulkEditForm.location_street.value,
        location_postal_code: bulkEditForm.location_postal_code.value,
        location_city: bulkEditForm.location_city.value,
      }),
    [
      bulkEditForm.location_city.value,
      bulkEditForm.location_postal_code.value,
      bulkEditForm.location_street.value,
      bulkEditForm.location_street_number.value,
    ]
  );
  const bulkLocationSuggestionsQuery = useQuery({
    queryKey: ["event-bulk-location-suggestions", deferredBulkLocationName],
    queryFn: () =>
      fetchJson<{ suggestions: SavedLocationSuggestion[] }>(
        apiUrl(`locations/suggest?q=${encodeURIComponent(deferredBulkLocationName)}`)
      ),
    enabled: isBulkEditModalOpen && deferredBulkLocationName.length > 0,
    retry: false,
  });
  const bulkLocationSuggestions = bulkLocationSuggestionsQuery.data?.suggestions || [];
  const showBulkLocationSuggestions =
    isBulkLocationNameFocused &&
    deferredBulkLocationName.length > 0 &&
    bulkLocationSuggestions.length > 0 &&
    bulkLocationSuggestions.some(
      (suggestion) =>
        suggestion.location_name.trim().toLowerCase() !== bulkEditForm.location_name.value.trim().toLowerCase() ||
        composeEventAddress(suggestion).trim().toLowerCase() !== bulkLocationAddressPreview.trim().toLowerCase()
    );

  useEffect(() => {
    document.title = "Relyo · Évènements";
  }, []);

  useEffect(() => {
    const bridge = installEasyAppBridge("event-planner");
    bridgeRef.current = bridge;
    setIsEmbeddedInWorkspace(bridge.isEmbedded());
  }, []);

  useEffect(() => {
    if (selectedEvents.length <= 1) {
      setIsBulkEditModalOpen(false);
    }
    if (selectedEvents.length === 0) {
      lastSelectedEventIdRef.current = null;
    }
  }, [selectedEvents.length]);

  useEffect(() => {
    if (!isBulkEditModalOpen) {
      setIsBulkLocationNameFocused(false);
    }
  }, [isBulkEditModalOpen]);

  useEffect(() => {
    if (catalogView !== "calendar") {
      return;
    }

    setEditingRowId(null);
    setInlineForm(createEmptyEventForm());
    setInlineFieldErrors([]);
    setRowSelection({});
  }, [catalogView]);

  useEffect(() => {
    if (!hasConfiguredConnectors && filters.connector !== "all") {
      setFilters((current) => ({ ...current, connector: "all" }));
    }
  }, [filters.connector, hasConfiguredConnectors]);

  useEffect(() => {
    writeSessionJson(EVENT_LIST_VIEW_STATE_STORAGE_KEY, {
      catalogView,
      filters,
      isFiltersPanelOpen,
      isImportModalOpen,
      isBulkEditModalOpen,
      isNewsletterModalOpen,
      newsletterPeriod,
      selectedEventIds: selectedIdsFromRowSelection(rowSelection),
    } satisfies EventListViewState);
  }, [catalogView, filters, isBulkEditModalOpen, isFiltersPanelOpen, isImportModalOpen, isNewsletterModalOpen, newsletterPeriod, rowSelection]);

  useEffect(() => {
    const flash = (location.state as FlashState | null)?.flash;
    if (!flash) {
      return;
    }

    setCollectionStatus(flash);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    if (!isImportMenuOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!importMenuRef.current?.contains(event.target as Node)) {
        setIsImportMenuOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsImportMenuOpen(false);
      }
    };

    window.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isImportMenuOpen]);

  const searchPlaceholder = hasConfiguredConnectors
    ? "Nom, date, statut, connecteur, Facebook..."
    : "Nom, date, statut, Facebook...";
  const activeFiltersCount = useMemo(() => countActiveFilters(filters), [filters]);
  const activeSortValue = useMemo(() => resolveSortingValue(sorting), [sorting]);
  const activeSortLabel = useMemo(
    () => resolveOptionLabel(EVENT_SORT_OPTIONS, activeSortValue),
    [activeSortValue]
  );
  const activeFilterChips = useMemo(() => {
    const chips: { key: string; label: string }[] = [];

    if (filters.search.trim()) {
      chips.push({ key: "search", label: `Recherche: ${filters.search.trim()}` });
    }

    if (filters.publication_status !== "all") {
      chips.push({
        key: "publication_status",
        label: `Statut: ${resolveOptionLabel(PUBLICATION_STATUS_OPTIONS, filters.publication_status)}`,
      });
    }

    if (filters.schedule === "all") {
      chips.push({
        key: "schedule",
        label: "Évènements passés affichés",
      });
    }

    if (hasConfiguredConnectors && filters.connector !== "all") {
      chips.push({
        key: "connector",
        label: `Connecteurs: ${resolveOptionLabel(CONNECTOR_OPTIONS, filters.connector)}`,
      });
    }

    if (filters.facebook !== "all") {
      chips.push({
        key: "facebook",
        label: `Facebook: ${resolveOptionLabel(FACEBOOK_OPTIONS, filters.facebook)}`,
      });
    }

    return chips;
  }, [filters, hasConfiguredConnectors]);

  function clearFilterChip(key: string) {
    setFilters((current) => {
      if (key === "search") {
        return { ...current, search: "" };
      }
      if (key === "publication_status") {
        return { ...current, publication_status: "all" };
      }
      if (key === "schedule") {
        return { ...current, schedule: "upcoming" };
      }
      if (key === "connector") {
        return { ...current, connector: "all" };
      }
      if (key === "facebook") {
        return { ...current, facebook: "all" };
      }
      return current;
    });
  }

  function resetCatalogFilters() {
    setFilters(createDefaultEventFilters());
  }

  async function handleExport(format: "csv" | "xlsx") {
    setIsImportMenuOpen(false);
    await downloadEventExport(filteredEvents, format);
    setCollectionStatus({
      message: `Export ${format.toUpperCase()} de ${filteredEvents.length} ${pluralize(filteredEvents.length, "évènement")} préparé.`,
      tone: "success",
    });
  }

  const saveInlineMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      fetchJson<EventRecord>(apiUrl("events"), {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: (savedEvent) => {
      queryClient.setQueryData<EventRecord[]>(["event-planner-events"], (current = []) => upsertEvent(current, savedEvent));
      setEditingRowId(null);
      setInlineForm(createEmptyEventForm());
      setInlineFieldErrors([]);
      setCollectionStatus({ message: "Mise à jour rapide enregistrée.", tone: "success" });
    },
    onError: (error) => {
      const normalizedError = normalizeApiError(error);
      setInlineFieldErrors(resolveFieldErrors(normalizedError, inferEventFieldErrors(normalizedError.message)));
      setCollectionStatus({ message: normalizedError.message, tone: "error" });
    },
  });

  async function saveSelectedEvents(buildNextForm: (event: EventRecord) => EventFormState) {
    let successCount = 0;
    let failureCount = 0;
    let lastError = "";

    for (const event of selectedEvents) {
      const nextForm = buildNextForm(event);
      const validationError = validateEventForm(nextForm);
      if (validationError) {
        failureCount += 1;
        lastError = `${event.display_name}: ${validationError.message}`;
        continue;
      }

      try {
        await fetchJson<EventRecord>(apiUrl("events"), {
          method: "POST",
          body: JSON.stringify(buildEventPayload(nextForm)),
        });
        successCount += 1;
      } catch (error) {
        failureCount += 1;
        lastError = `${event.display_name}: ${normalizeApiError(error).message}`;
      }
    }

    await queryClient.invalidateQueries({ queryKey: ["event-planner-events"] });
    return { successCount, failureCount, lastError };
  }

  const bulkUpdateMutation = useMutation({
    mutationFn: () => saveSelectedEvents((event) => applyBulkEdit(event, bulkEditForm)),
    onMutate: () => {
      setCollectionStatus({ message: "Mise a jour en lot en cours...", tone: "neutral" });
    },
    onSuccess: ({ successCount, failureCount, lastError }) => {
      if (failureCount > 0) {
        setCollectionStatus({
          message:
            successCount > 0
              ? `${successCount} ${pluralize(successCount, "évènement")} mis à jour, ${failureCount} en échec. ${lastError}`
              : `Aucune mise à jour appliquée. ${lastError}`,
          tone: "error",
        });
      } else {
        setCollectionStatus({
          message: `${successCount} ${pluralize(successCount, "évènement")} mis à jour en lot.`,
          tone: "success",
        });
      }

      if (successCount > 0) {
        setRowSelection({});
        setBulkEditForm(createEmptyBulkEditForm());
        setIsBulkEditModalOpen(false);
      }
    },
    onError: (error) => {
      const normalizedError = normalizeApiError(error);
      setCollectionStatus({ message: normalizedError.message, tone: "error" });
    },
  });

  const bulkPublishMutation = useMutation({
    mutationFn: () =>
      saveSelectedEvents((event) => ({
        ...toEventForm(event),
        publication_status: "published",
      })),
    onMutate: () => {
      setCollectionStatus({ message: "Publication en cours...", tone: "neutral" });
    },
    onSuccess: ({ successCount, failureCount, lastError }) => {
      if (failureCount > 0) {
        setCollectionStatus({
          message:
            successCount > 0
              ? `${successCount} ${pluralize(successCount, "évènement")} publié${successCount > 1 ? "s" : ""}, ${failureCount} en échec. ${lastError}`
              : `Aucune publication appliquée. ${lastError}`,
          tone: "error",
        });
      } else {
        setCollectionStatus({
          message: `${successCount} ${pluralize(successCount, "évènement")} publié${successCount > 1 ? "s" : ""}.`,
          tone: "success",
        });
      }

      if (successCount > 0) {
        setRowSelection({});
      }
    },
    onError: (error) => {
      const normalizedError = normalizeApiError(error);
      setCollectionStatus({ message: normalizedError.message, tone: "error" });
    },
  });

  async function duplicateSelectedEvents() {
    const duplicatedEvents: EventRecord[] = [];
    let failureCount = 0;
    let lastError = "";

    for (const event of selectedEvents) {
      try {
        const duplicatedEvent = await fetchJson<EventRecord>(apiUrl(`events/${event.id}/duplicate`), {
          method: "POST",
        });
        duplicatedEvents.push(duplicatedEvent);
      } catch (error) {
        failureCount += 1;
        lastError = `${event.display_name}: ${normalizeApiError(error).message}`;
      }
    }

    if (duplicatedEvents.length > 0) {
      queryClient.setQueryData<EventRecord[]>(["event-planner-events"], (current = []) => {
        let nextEvents = current;
        for (const duplicatedEvent of duplicatedEvents) {
          nextEvents = upsertEvent(nextEvents, duplicatedEvent);
        }
        return nextEvents;
      });
    }

    return { duplicatedEvents, successCount: duplicatedEvents.length, failureCount, lastError };
  }

  const bulkDuplicateMutation = useMutation({
    mutationFn: duplicateSelectedEvents,
    onMutate: () => {
      setCollectionStatus({ message: "Duplication des brouillons en cours...", tone: "neutral" });
    },
    onSuccess: ({ duplicatedEvents, successCount, failureCount, lastError }) => {
      if (successCount > 0) {
        setRowSelection({});
      }

      if (successCount === 1 && failureCount === 0) {
        startTransition(() =>
          navigate(routes.eventDetailPath(duplicatedEvents[0].id), {
            state: {
              flash: {
                message: "Brouillon duplique avec succes. Completez la date avant publication.",
                tone: "success",
              } satisfies StatusState,
            },
          })
        );
        return;
      }

      if (failureCount > 0) {
        setCollectionStatus({
          message:
            successCount > 0
              ? `${successCount} ${pluralize(successCount, "évènement")} dupliqué${successCount > 1 ? "s" : ""}, ${failureCount} en échec. ${lastError}`
              : `Aucune duplication appliquée. ${lastError}`,
          tone: "error",
        });
        return;
      }

      setCollectionStatus({
        message: `${successCount} ${pluralize(successCount, "évènement")} dupliqué${successCount > 1 ? "s" : ""} en brouillon.`,
        tone: "success",
      });
    },
    onError: (error) => {
      const normalizedError = normalizeApiError(error);
      setCollectionStatus({ message: normalizedError.message, tone: "error" });
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async () =>
      fetchJson<{ deleted_count: number }>(apiUrl("events/delete-bulk"), {
        method: "POST",
        body: JSON.stringify({ event_ids: selectedEvents.map((event) => event.id) }),
      }),
    onSuccess: async ({ deleted_count }) => {
      await queryClient.invalidateQueries({ queryKey: ["event-planner-events"] });
      setRowSelection({});
      setEditingRowId(null);
      setInlineForm(createEmptyEventForm());
      setInlineFieldErrors([]);
      setCollectionStatus({
        message: `${deleted_count} ${pluralize(deleted_count, "évènement")} supprimé${deleted_count > 1 ? "s" : ""}.`,
        tone: "success",
      });
    },
    onError: (error) => {
      const normalizedError = normalizeApiError(error);
      setCollectionStatus({ message: normalizedError.message, tone: "error" });
    },
  });

  const hasConfiguredActionableSyncConnector = configuredKeys.has("odoo") || Boolean(caldavConfigQuery.data?.configured);
  const isBulkConnectorSyncDisabled =
    selectedEvents.length === 0 ||
    bulkPublishMutation.isPending ||
    bulkDuplicateMutation.isPending ||
    bulkDeleteMutation.isPending ||
    bulkUpdateMutation.isPending ||
    isDistributionConfigLoading ||
    !hasConfiguredActionableSyncConnector;

  const bulkConnectorSyncMutation = useMutation({
    mutationFn: () =>
      fetchJson<BulkConnectorSyncResponse>(apiUrl("events/connectors/sync"), {
        method: "POST",
        body: JSON.stringify({ event_ids: selectedEvents.map((event) => event.id) }),
      }),
    onMutate: () => {
      setCollectionStatus({ message: "Synchronisation en cours...", tone: "neutral" });
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["event-planner-events"] });
      if (result.synced_count > 0) {
        setRowSelection({});
      }
      setCollectionStatus({
        message: formatBulkConnectorSyncSummary(result),
        tone: result.failed_count > 0 ? "error" : "success",
      });
    },
    onError: (error) => {
      const normalizedError = normalizeApiError(error);
      setCollectionStatus({ message: normalizedError.message, tone: "error" });
    },
  });

  const duplicateEventMutation = useMutation({
    mutationFn: (sourceEventId: number) =>
      fetchJson<EventRecord>(apiUrl(`events/${sourceEventId}/duplicate`), {
        method: "POST",
      }),
    onMutate: (sourceEventId) => {
      setActiveDuplicateEventId(sourceEventId);
      setCollectionStatus({ message: "Duplication du brouillon en cours...", tone: "neutral" });
    },
    onSuccess: (duplicatedEvent) => {
      queryClient.setQueryData<EventRecord[]>(["event-planner-events"], (current = []) => upsertEvent(current, duplicatedEvent));
      startTransition(() =>
        navigate(routes.eventDetailPath(duplicatedEvent.id), {
          state: {
            flash: {
              message: "Brouillon duplique avec succes. Completez la date avant publication.",
              tone: "success",
            } satisfies StatusState,
          },
        })
      );
    },
    onError: (error) => {
      const normalizedError = normalizeApiError(error);
      setCollectionStatus({ message: normalizedError.message, tone: "error" });
    },
    onSettled: () => {
      setActiveDuplicateEventId(null);
    },
  });

  const importMutation = useMutation({
    mutationFn: async () => {
      if (!importPreview) {
        return { createdCount: 0, updatedCount: 0, failedCount: 0, lastError: "" };
      }

      let createdCount = 0;
      let updatedCount = 0;
      let failedCount = 0;
      let lastError = "";

      for (const entry of importPreview.entries) {
        if (entry.issues.length > 0) {
          continue;
        }

        try {
          const savedEvent = await fetchJson<EventRecord>(apiUrl("events"), {
            method: "POST",
            body: JSON.stringify(
              buildEventPayload({
                ...entry.form,
                id: entry.matchedEventId || entry.form.id,
              })
            ),
          });
          if (entry.mode === "update") {
            updatedCount += 1;
          } else {
            createdCount += 1;
          }
          queryClient.setQueryData<EventRecord[]>(["event-planner-events"], (current = []) => upsertEvent(current, savedEvent));
        } catch (error) {
          failedCount += 1;
          lastError = normalizeApiError(error).message;
        }
      }

      return { createdCount, updatedCount, failedCount, lastError };
    },
    onSuccess: ({ createdCount, updatedCount, failedCount, lastError }) => {
      const successCount = createdCount + updatedCount;
      if (failedCount > 0) {
        setImportStatus({
          message:
            successCount > 0
              ? `${successCount} lignes importées, ${failedCount} en échec. ${lastError}`
              : `Import echoue. ${lastError}`,
          tone: "error",
        });
      } else {
        setImportStatus({
          message: `${createdCount} créations et ${updatedCount} mises à jour importées.`,
          tone: "success",
        });
      }

      if (successCount > 0) {
        setImportPreview(null);
        setImportFileName("");
        setImportFile(null);
        setImportMappingError("");
        setIsImportModalOpen(false);
      }
    },
    onError: (error) => {
      const normalizedError = normalizeApiError(error);
      setImportStatus({ message: normalizedError.message, tone: "error" });
    },
  });

  function updateInlineFormField<Key extends keyof EventFormState>(field: Key, value: EventFormState[Key]) {
    setInlineForm((current) => {
      return { ...current, [field]: value };
    });
    if (field === "display_name" || field === "internal_name") {
      setInlineFieldErrors((current) => current.filter((item) => item !== "display_name" && item !== "internal_name"));
    } else {
      setInlineFieldErrors((current) => current.filter((item) => item !== field));
    }
  }

  function updateBulkField(
    field: keyof BulkEditFormState,
    updater: BulkEditFieldState | ((current: BulkEditFieldState) => BulkEditFieldState)
  ) {
    setBulkEditForm((current) => ({
      ...current,
      [field]: typeof updater === "function" ? updater(current[field]) : updater,
    }));
  }

  function updateBulkLocationField(
    field: "location_name" | "location_street_number" | "location_street" | "location_postal_code" | "location_city",
    value: string,
    options?: { enable?: boolean }
  ) {
    setBulkEditForm((current) => ({
      ...current,
      [field]: {
        enabled: options?.enable ? true : current[field].enabled,
        value,
      },
    }));
  }

  function applySavedBulkLocationSuggestion(suggestion: SavedLocationSuggestion) {
    setBulkEditForm((current) => ({
      ...current,
      location_name: { enabled: true, value: suggestion.location_name },
      location_street_number: { enabled: true, value: suggestion.location_street_number },
      location_street: { enabled: true, value: suggestion.location_street },
      location_postal_code: { enabled: true, value: suggestion.location_postal_code },
      location_city: { enabled: true, value: suggestion.location_city },
    }));
    setIsBulkLocationNameFocused(false);
  }

  function openInlineEdit(event: EventRecord) {
    setEditingRowId(event.id);
    setInlineForm(toEventForm(event));
    setInlineFieldErrors([]);
    setCollectionStatus({ message: "", tone: "neutral" });
  }

  function openNewsletterModal() {
    setNewsletterPeriod(newsletterSuggestedPeriod);
    setIsNewsletterModalOpen(true);
  }

  function handleOpenNewsletterDraft(newsletterDraft: Parameters<typeof persistNewsletterComposeDraft>[0]) {
    if (!canOpenNewsletterComposer) {
      return;
    }

    persistNewsletterComposeDraft(newsletterDraft);
    if (bridgeRef.current?.isEmbedded()) {
      const didNavigate = bridgeRef.current.navigate("newsletter");
      if (!didNavigate) {
        throw new Error("Impossible d'ouvrir la newsletter depuis cette session.");
      }
    } else if (routes.newsletterPath) {
      startTransition(() => navigate(routes.newsletterPath!));
    } else {
      throw new Error("Impossible d'ouvrir la newsletter depuis cette session.");
    }
    setIsNewsletterModalOpen(false);
  }

  async function applyImportMapping(mapping: string[]) {
    if (!importFile) return;
    setIsParsingImport(true);
    setImportMappingError("");
    try {
      const preview = await parseImportFile(importFile, events, mapping);
      setImportPreview(preview);
      setIsMappingConfirmed(true);
      setImportStatus({ message: `${preview.readyCount} ligne(s) prête(s), ${preview.issueCount} avec alerte.`, tone: preview.issueCount > 0 ? "error" : "success" });
    } catch (error) {
      setImportMappingError(error instanceof Error ? error.message : "Impossible de prévisualiser le fichier.");
    } finally {
      setIsParsingImport(false);
    }
  }

  async function handleImportFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) {
      return;
    }

    setImportFile(file);
    setIsMappingConfirmed(false);
    setImportMappingError("");
    setImportPreview(null);
    setImportFileName(file.name);
    setIsParsingImport(true);
    setImportStatus({ message: "Analyse du fichier en cours...", tone: "neutral" });
    try {
      const preview = await parseImportFile(file, events);
      setIsMappingConfirmed(!preview.columns);
      setImportPreview(preview);
      setImportStatus({
        message: `${preview.readyCount} ligne(s) prete(s), ${preview.issueCount} avec alerte.`,
        tone: preview.issueCount > 0 ? "error" : "success",
      });
    } catch (error) {
      setImportPreview(null);
      setImportMappingError(error instanceof Error ? error.message : "Impossible de lire le fichier.");
      setImportStatus({
        message: error instanceof Error ? error.message : "Impossible de lire le fichier d'import.",
        tone: "error",
      });
    } finally {
      setIsParsingImport(false);
    }
  }

  function handleVisibleRowSelection(
    mouseEvent: ReactMouseEvent<HTMLButtonElement>,
    eventId: string,
    isCurrentlySelected: boolean
  ) {
    mouseEvent.preventDefault();
    mouseEvent.stopPropagation();

    const nextSelected = !isCurrentlySelected;
    const visibleRowIds = visibleRowIdsRef.current;
    const targetIndex = visibleRowIds.indexOf(eventId);
    if (targetIndex === -1) {
      return;
    }

    const anchorId = lastSelectedEventIdRef.current;
    const anchorIndex = anchorId ? visibleRowIds.indexOf(anchorId) : -1;
    if (mouseEvent.shiftKey && anchorIndex >= 0) {
      const [start, end] = anchorIndex <= targetIndex ? [anchorIndex, targetIndex] : [targetIndex, anchorIndex];
      const rangeIds = visibleRowIds.slice(start, end + 1);
      setRowSelection((current) => {
        const nextSelection = { ...current };
        for (const rowId of rangeIds) {
          if (nextSelected) {
            nextSelection[rowId] = true;
          } else {
            delete nextSelection[rowId];
          }
        }
        return nextSelection;
      });
      lastSelectedEventIdRef.current = eventId;
      return;
    }

    setRowSelection((current) => {
      if (nextSelected) {
        return { ...current, [eventId]: true };
      }
      const nextSelection = { ...current };
      delete nextSelection[eventId];
      return nextSelection;
    });
    lastSelectedEventIdRef.current = eventId;
  }

  function handleToggleVisibleRows(
    mouseEvent: ReactMouseEvent<HTMLButtonElement>,
    nextSelected: boolean
  ) {
    mouseEvent.preventDefault();
    mouseEvent.stopPropagation();
    const visibleRowIds = visibleRowIdsRef.current;
    setRowSelection((current) => {
      const nextSelection = { ...current };
      for (const rowId of visibleRowIds) {
        if (nextSelected) {
          nextSelection[rowId] = true;
        } else {
          delete nextSelection[rowId];
        }
      }
      return nextSelection;
    });
    lastSelectedEventIdRef.current = null;
  }

  const columns = useMemo<ColumnDef<EventRecord>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <button
            type="button"
            className="table-checkbox-hitbox"
            role="checkbox"
            aria-label="Sélectionner les lignes visibles"
            aria-checked={table.getIsSomePageRowsSelected() && !table.getIsAllPageRowsSelected() ? "mixed" : table.getIsAllPageRowsSelected()}
            onClick={(mouseEvent) => handleToggleVisibleRows(mouseEvent, !table.getIsAllPageRowsSelected())}
          >
            <input
              type="checkbox"
              className="table-checkbox"
              aria-hidden={true}
              tabIndex={-1}
              checked={table.getIsAllPageRowsSelected()}
              readOnly
              ref={(input) => {
                if (input) {
                  input.indeterminate = table.getIsSomePageRowsSelected() && !table.getIsAllPageRowsSelected();
                }
              }}
            />
          </button>
        ),
        cell: ({ row }) => (
          <button
            type="button"
            className="table-checkbox-hitbox"
            role="checkbox"
            aria-label={`Sélectionner ${row.original.display_name}`}
            aria-checked={row.getIsSelected()}
            onClick={(mouseEvent) => handleVisibleRowSelection(mouseEvent, row.id, row.getIsSelected())}
          >
            <input
              type="checkbox"
              className="table-checkbox"
              aria-hidden={true}
              tabIndex={-1}
              checked={row.getIsSelected()}
              readOnly
            />
          </button>
        ),
        enableSorting: false,
        size: 44,
      },
      {
        id: "illustration",
        accessorFn: (event) => event.illustration.reference,
        header: "Illu",
        enableSorting: false,
        cell: ({ row }) => {
          const illustration = row.original.illustration;
          const illustrationUrl =
            illustration.kind === "external_url" && illustration.reference.trim() ? illustration.reference.trim() : "";

          return (
            <span
              className={`table-event-illustration${illustrationUrl ? " has-image" : ""}`}
              title={illustrationUrl ? "Illustration présente" : "Aucune illustration"}
              aria-label={illustrationUrl ? "Illustration présente" : "Aucune illustration"}
            >
              {illustrationUrl ? (
                <img
                  src={illustrationUrl}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  onClick={(event) => event.stopPropagation()}
                />
              ) : (
                <Icon name="image" size="1rem" stroke={1.8} aria-hidden={true} />
              )}
            </span>
          );
        },
        size: 48,
      },
      {
        id: "identity",
        accessorFn: (event) => event.display_name,
        header: ({ column }) => <SortableHeader column={column} label="Évènement" />,
        cell: ({ row }) =>
          editingRowId === row.original.id ? (
            <div className="table-cell-stack" onClick={(event) => event.stopPropagation()}>
              <input
                value={inlineForm.display_name}
                className={hasFieldError(inlineFieldErrors, "display_name") ? "is-field-invalid" : ""}
                onChange={(event) => updateInlineFormField("display_name", event.target.value)}
                placeholder="Nom visible"
              />
              <input
                value={inlineForm.internal_name}
                className={hasFieldError(inlineFieldErrors, "internal_name") ? "is-field-invalid" : ""}
                onChange={(event) => updateInlineFormField("internal_name", event.target.value)}
                placeholder="Nom interne"
              />
              <select
                value={inlineForm.publication_status}
                className={hasFieldError(inlineFieldErrors, "publication_status") ? "is-field-invalid" : ""}
                onChange={(event) =>
                  updateInlineFormField(
                    "publication_status",
                    event.target.value as EventFormState["publication_status"]
                  )
                }
              >
                <option value="published">Publie</option>
                <option value="draft">Brouillon</option>
              </select>
            </div>
          ) : (
            <div className="table-event-name">
              <strong className="table-event-title">{row.original.display_name}</strong>
              <button
                type="button"
                className="table-row-icon-action"
                aria-label={`Dupliquer ${row.original.display_name}`}
                title="Dupliquer"
                disabled={duplicateEventMutation.isPending}
                onClick={(event) => {
                  event.stopPropagation();
                  duplicateEventMutation.mutate(row.original.id);
                }}
              >
                <Icon name="copy" size="1rem" stroke={1.9} />
              </button>
              <div className="table-event-meta">
                <span className={publicationStatusClassName(row.original.publication_status)}>
                  {publicationStatusLabel(row.original.publication_status)}
                </span>
                <span className="table-subtext">{row.original.internal_name}</span>
              </div>
            </div>
          ),
      },
      {
        id: "schedule",
        accessorFn: (event) => `${event.date} ${event.start_time}`,
        header: ({ column }) => <SortableHeader column={column} label="Planning" />,
        cell: ({ row }) =>
          editingRowId === row.original.id ? (
            <div className="table-cell-stack" onClick={(event) => event.stopPropagation()}>
              <input
                type="date"
                value={inlineForm.date}
                className={hasFieldError(inlineFieldErrors, "date") ? "is-field-invalid" : ""}
                onChange={(event) => updateInlineFormField("date", event.target.value)}
              />
              <div className="event-inline-actions">
                <input
                  type="time"
                  value={inlineForm.start_time}
                  className={`table-compact-field${hasFieldError(inlineFieldErrors, "start_time") ? " is-field-invalid" : ""}`}
                  onChange={(event) => updateInlineFormField("start_time", event.target.value)}
                />
                <input
                  type="time"
                  value={inlineForm.end_time}
                  className={`table-compact-field${hasFieldError(inlineFieldErrors, "end_time") ? " is-field-invalid" : ""}`}
                  onChange={(event) => updateInlineFormField("end_time", event.target.value)}
                />
              </div>
            </div>
          ) : (
            <div className="table-cell-stack">
              <strong>{formatEventDate(row.original.date)}</strong>
              <span className="table-subtext">
                {row.original.start_time} - {row.original.end_time}
              </span>
            </div>
          ),
      },
      {
        id: "price",
        accessorFn: (event) => event.price.amount_cents ?? event.price.label,
        header: ({ column }) => <SortableHeader column={column} label="Prix" />,
        cell: ({ row }) =>
          editingRowId === row.original.id ? (
            <div className="table-cell-stack" onClick={(event) => event.stopPropagation()}>
              <div className="event-inline-actions">
                <input
                  value={inlineForm.price_amount}
                  className={`table-compact-field${hasFieldError(inlineFieldErrors, "price") ? " is-field-invalid" : ""}`}
                  onChange={(event) => updateInlineFormField("price_amount", event.target.value)}
                  placeholder="12.50"
                />
                <input
                  value={inlineForm.price_currency}
                  className={`table-compact-field${hasFieldError(inlineFieldErrors, "price") ? " is-field-invalid" : ""}`}
                  onChange={(event) => updateInlineFormField("price_currency", event.target.value.toUpperCase())}
                  placeholder="EUR"
                />
              </div>
              <input
                value={inlineForm.price_label}
                className={hasFieldError(inlineFieldErrors, "price") ? "is-field-invalid" : ""}
                onChange={(event) => updateInlineFormField("price_label", event.target.value)}
                placeholder="Tarif public"
              />
            </div>
          ) : (
            <div className="table-cell-stack">
              <strong>{formatEventPrice(row.original)}</strong>
              {row.original.price.label && row.original.price.label.trim().toLocaleLowerCase() !== formatEventPrice(row.original).trim().toLocaleLowerCase() ? <span className="table-subtext">{row.original.price.label}</span> : null}
            </div>
          ),
      },
      ...(hasConfiguredConnectors
        ? [
            {
              id: "connectors",
              accessorFn: (event: EventRecord) =>
                (visibleConnectorsByEventId.get(event.id) || []).map((connector) => connector.status).join(" "),
              header: "Connecteurs",
              enableSorting: false,
              cell: ({ row }: { row: { original: EventRecord } }) => (
                <ConnectorCluster connectors={visibleConnectorsByEventId.get(row.original.id) || []} />
              ),
            } satisfies ColumnDef<EventRecord>,
          ]
        : []),
      {
        id: "facebook",
        accessorFn: (event) => event.facebook_url,
        header: "Facebook",
        enableSorting: false,
        cell: ({ row }) =>
          editingRowId === row.original.id ? (
            <div className="table-cell-stack" onClick={(event) => event.stopPropagation()}>
              <input
                value={inlineForm.facebook_url}
                className={`table-compact-field is-wide${hasFieldError(inlineFieldErrors, "facebook_url") ? " is-field-invalid" : ""}`}
                onChange={(event) => updateInlineFormField("facebook_url", event.target.value)}
                placeholder="https://facebook.com/events/..."
              />
            </div>
          ) : row.original.facebook_url ? (
            <a
              href={row.original.facebook_url}
              target="_blank"
              rel="noreferrer"
              className="external-link table-facebook-link"
              onClick={(event) => event.stopPropagation()}
            >
              Ouvrir
            </a>
          ) : (
            <span className="muted table-facebook-empty">Aucun lien</span>
          ),
      },
    ],
    [
      editingRowId,
      hasConfiguredConnectors,
      inlineFieldErrors,
      inlineForm,
      navigate,
      routes,
      duplicateEventMutation,
      visibleConnectorsByEventId,
    ]
  );

  const table = useReactTable({
    data: filteredEvents,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
      rowSelection,
    },
    getRowId: (event) => String(event.id),
    enableRowSelection: true,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
  });
  visibleRowIdsRef.current = table.getRowModel().rows.map((row) => row.id);

  return (
    <AppChrome
      className="event-page-shell"
      title="Événements"
      headerActions={<div className="event-catalog-actions">
              <div className="event-import-split" ref={importMenuRef}>
                <button type="button" className="button event-toolbar-secondary-button" onClick={() => setIsImportModalOpen(true)}>
                  <IconLabel icon="upload" label="Importer" />
                </button>
                <button
                  type="button"
                  className="button event-import-split-toggle event-toolbar-secondary-button"
                  aria-label="Options d'import et d'export"
                  aria-haspopup="menu"
                  aria-expanded={isImportMenuOpen}
                  onClick={() => setIsImportMenuOpen((current) => !current)}
                >
                  <Icon name="chevron-down" />
                </button>
                {isImportMenuOpen ? (
                  <div className="event-import-menu" role="menu" aria-label="Options d'import et d'export">
                    <button
                      type="button"
                      className="event-import-menu-item"
                      role="menuitem"
                      disabled={filteredEvents.length === 0}
                      onClick={() => void handleExport("csv")}
                    >
                      <IconLabel icon="download" label="Exporter CSV" />
                    </button>
                    <button
                      type="button"
                      className="event-import-menu-item"
                      role="menuitem"
                      disabled={filteredEvents.length === 0}
                      onClick={() => void handleExport("xlsx")}
                    >
                      <IconLabel icon="download" label="Exporter XLSX" />
                    </button>
                  </div>
                ) : null}
              </div>
              <button type="button" className="button event-toolbar-secondary-button" onClick={openNewsletterModal}>
                <IconLabel icon="mail" label="Generer une newsletter" />
              </button>
              <button
                type="button"
                className="button button-primary"
                onClick={() => startTransition(() => navigate(routes.createPath))}
              >
                <IconLabel icon="plus" label="Créer un évènement" />
              </button>
            </div>}
      topBarStart={
        <div className="event-view-toggle" role="group" aria-label="Choix de la vue catalogue">
          <button
            type="button"
            className={`event-view-toggle-button${catalogView === "table" ? " is-active" : ""}`}
            aria-pressed={catalogView === "table"}
            onClick={() => setCatalogView("table")}
          >
            <IconLabel icon="table" label="Tableau" />
          </button>
          <button
            type="button"
            className={`event-view-toggle-button${catalogView === "calendar" ? " is-active" : ""}`}
            aria-pressed={catalogView === "calendar"}
            onClick={() => setCatalogView("calendar")}
          >
            <IconLabel icon="calendar" label="Calendrier" />
          </button>
        </div>
      }
      topBarEnd={routes.newsletterPath ? null : (
        <button
          type="button"
          className="icon-button"
          aria-label="Paramètres"
          title="Paramètres"
          onClick={() => startTransition(() => navigate(routes.settingsPath))}
        >
          <Icon name="settings" />
        </button>
      )}
    >
      <AppSurface className={`event-list-panel${catalogView === "calendar" ? " is-calendar-view" : ""}`}>
        <div className="event-catalog-toolbar">
          <div className="event-catalog-toolbar-main">
            <div className="event-catalog-search-shell">
              <div className="event-catalog-searchbar">
                <Icon name="search" className="ui-icon event-catalog-search-icon" />
                <input
                  type="search"
                  value={filters.search}
                  placeholder={searchPlaceholder}
                  aria-label="Rechercher dans le catalogue"
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    setFilters((current) => ({ ...current, search: value }));
                  }}
                />
                {filters.search.trim() ? (
                  <button
                    type="button"
                    className="event-catalog-clear-search"
                    aria-label="Effacer la recherche"
                    title="Effacer la recherche"
                    onClick={() => setFilters((current) => ({ ...current, search: "" }))}
                  >
                    <Icon name="x" />
                  </button>
                ) : null}
              </div>
              <button
                type="button"
                className={`event-catalog-filter-toggle${activeFiltersCount > 0 ? " has-active-filters" : ""}${isFiltersPanelOpen ? " is-open" : ""}`}
                aria-expanded={isFiltersPanelOpen}
                aria-controls="event-filters-panel"
                onClick={() => setIsFiltersPanelOpen((current) => !current)}
              >
                <Icon name="filter" />
                <span>Filtres</span>
                {activeFiltersCount > 0 ? <span className="event-catalog-filter-count">{activeFiltersCount}</span> : null}
              </button>
            </div>


          </div>

          {isFiltersPanelOpen ? (
            <div id="event-filters-panel" className="event-advanced-filters">
              <div className={`event-advanced-filter-grid${hasConfiguredConnectors ? "" : " is-without-connectors"}`}>
                <label className="event-toolbar-field" onClick={openSelectFromFieldClick}>
                  <span className="event-toolbar-field-label">
                    <Icon name="edit" className="ui-icon" />
                    Statut
                  </span>
                  <div className="toolbar-dropdown">
                    <select
                      className="toolbar-select"
                      value={filters.publication_status}
                      onChange={(event) => {
                        const value = event.currentTarget.value as EventFilters["publication_status"];
                        setFilters((current) => ({ ...current, publication_status: value }));
                      }}
                    >
                      {PUBLICATION_STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </label>

                <label className="event-toolbar-field event-past-events-filter">
                  <span className="event-toolbar-field-label">
                    <Icon name="calendar" className="ui-icon" />
                    Dates
                  </span>
                  <span className="event-past-events-filter-control">
                    <input
                      type="checkbox"
                      checked={filters.schedule === "all"}
                      onChange={(event) => {
                        const schedule = event.currentTarget.checked ? "all" : "upcoming";
                        setFilters((current) => ({ ...current, schedule }));
                      }}
                    />
                    <span>Montrer les évènements passés</span>
                  </span>
                </label>

                <label className="event-toolbar-field" onClick={openSelectFromFieldClick}>
                  <span className="event-toolbar-field-label">
                    <Icon name="eye" className="ui-icon" />
                    Facebook
                  </span>
                  <div className="toolbar-dropdown">
                    <select
                      className="toolbar-select"
                      value={filters.facebook}
                      onChange={(event) => {
                        const value = event.currentTarget.value as EventFilters["facebook"];
                        setFilters((current) => ({ ...current, facebook: value }));
                      }}
                    >
                      {FACEBOOK_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </label>

                {hasConfiguredConnectors ? (
                  <label className="event-toolbar-field" onClick={openSelectFromFieldClick}>
                    <span className="event-toolbar-field-label">
                      <Icon name="filter" className="ui-icon" />
                      Connecteurs
                    </span>
                    <div className="toolbar-dropdown">
                      <select
                        className="toolbar-select"
                        value={filters.connector}
                        onChange={(event) => {
                          const value = event.currentTarget.value as EventFilters["connector"];
                          setFilters((current) => ({ ...current, connector: value }));
                        }}
                      >
                        {CONNECTOR_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </label>
                ) : null}

                <label className="event-toolbar-field" onClick={openSelectFromFieldClick}>
                  <span className="event-toolbar-field-label">
                    <Icon name="table" className="ui-icon" />
                    Tri
                  </span>
                  <div className="toolbar-dropdown">
                    <select
                      className="toolbar-select"
                      value={activeSortValue}
                      onChange={(event) => {
                        const value = event.currentTarget.value;
                        setSorting(resolveSortingState(value));
                      }}
                    >
                      {EVENT_SORT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </label>
              </div>

              <div className="event-advanced-filters-footer is-actions-only">
                <button
                  type="button"
                  className="button event-toolbar-secondary-button"
                  onClick={resetCatalogFilters}
                  disabled={activeFiltersCount === 0}
                >
                  <IconLabel icon="x" label="Réinitialiser" />
                </button>
              </div>
            </div>
          ) : null}

          <div className="event-catalog-meta">
            <div className="event-catalog-meta-main">
              <strong>
                {filteredEvents.length} {pluralize(filteredEvents.length, "évènement")}
              </strong>
              {catalogView === "table" ? <span className="event-catalog-meta-detail">Tri: {activeSortLabel}</span> : null}
              {activeFiltersCount > 0 ? (
                <span className="event-catalog-meta-detail">
                  {activeFiltersCount} filtre{activeFiltersCount > 1 ? "s" : ""} actif{activeFiltersCount > 1 ? "s" : ""}
                </span>
              ) : null}
            </div>
            <button
              type="button"
              className="event-catalog-refresh"
              aria-label="Rafraîchir le catalogue"
              title="Rafraîchir le catalogue"
              onClick={() => eventsQuery.refetch()}
            >
              <Icon name="refresh" />
            </button>
          </div>

          {activeFilterChips.length > 0 ? (
            <div className="event-active-filter-chips" aria-label="Filtres actifs">
              {activeFilterChips.map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  className="event-active-filter-chip"
                  onClick={() => clearFilterChip(chip.key)}
                >
                  <span>{chip.label}</span>
                  <Icon name="x" />
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {catalogView === "table" && selectedEvents.length > 0 ? (
          <AppSecondaryBar className="event-selection-bar">
            <p className="event-selection-summary">
              {selectedEvents.length} {pluralize(selectedEvents.length, "évènement")} sélectionné
              {selectedEvents.length > 1 ? "s" : ""}
            </p>
            <button
              type="button"
              className="button"
              disabled={selectedEvents.length === 0 || bulkPublishMutation.isPending || bulkConnectorSyncMutation.isPending}
              onClick={() => {
                if (selectedEvents.length === 0) {
                  return;
                }
                bulkPublishMutation.mutate();
              }}
            >
              <IconLabel
                icon="spark"
                label={
                  bulkPublishMutation.isPending
                    ? "Publication..."
                    : "Publier"
                }
              />
            </button>
            <button
              type="button"
              className="button"
              disabled={isBulkConnectorSyncDisabled || bulkConnectorSyncMutation.isPending}
              onClick={() => {
                if (selectedEvents.length === 0 || isBulkConnectorSyncDisabled) {
                  return;
                }
                bulkConnectorSyncMutation.mutate();
              }}
            >
              <IconLabel
                icon="refresh"
                label={bulkConnectorSyncMutation.isPending ? "Synchro..." : "Synchroniser"}
              />
            </button>
            <button
              type="button"
              className="button"
              disabled={selectedEvents.length === 0 || bulkDuplicateMutation.isPending || bulkConnectorSyncMutation.isPending}
              onClick={() => {
                if (selectedEvents.length === 0) {
                  return;
                }
                bulkDuplicateMutation.mutate();
              }}
            >
              <IconLabel
                icon="copy"
                label={
                  bulkDuplicateMutation.isPending
                    ? "Duplication..."
                    : "Dupliquer"
                }
              />
            </button>
            <button
              type="button"
              className="button button-primary"
              disabled={selectedEvents.length <= 1 || bulkConnectorSyncMutation.isPending}
              onClick={() => {
                if (selectedEvents.length > 1) {
                  setIsBulkEditModalOpen(true);
                }
              }}
            >
              <IconLabel
                icon="spark"
                label={
                  "Modifier"
                }
              />
            </button>
            <button
              type="button"
              className="button button-danger"
              disabled={
                selectedEvents.length === 0 ||
                bulkDeleteMutation.isPending ||
                bulkPublishMutation.isPending ||
                bulkConnectorSyncMutation.isPending
              }
              onClick={() => {
                if (selectedEvents.length === 0) {
                  return;
                }
                const confirmed = window.confirm(
                  selectedEvents.length === 1
                    ? "Supprimer cet évènement ?"
                    : `Supprimer ${selectedEvents.length} évènements ?`
                );
                if (!confirmed) {
                  return;
                }
                bulkDeleteMutation.mutate();
              }}
            >
              <IconLabel
                icon="trash"
                label={
                  bulkDeleteMutation.isPending
                    ? "Suppression..."
                    : "Supprimer"
                }
              />
            </button>
          </AppSecondaryBar>
        ) : null}

        {eventsQuery.isLoading ? <p className="status-line">Chargement du catalogue en cours...</p> : null}
        {showConnectorSetupAlert ? (
          <div className="app-context-alert is-warning">
            <div className="app-context-alert-copy">
              <strong>Choisissez où diffuser vos évènements</strong>
              <p>Choisissez où vos évènements seront publiés ou synchronisés.</p>
            </div>
            <div className="app-context-alert-actions">
              <button
                type="button"
                className="button button-primary"
                onClick={() => startTransition(() => navigate(routes.settingsPath))}
              >
                Choisir où diffuser
              </button>
            </div>
          </div>
        ) : null}

        <p className={statusClassName(collectionStatus)}>{collectionStatus.message}</p>

        {showCatalogBlankState ? (
          <section className="app-empty-state event-module-empty-state">
            <div className="app-empty-state-visual">
              <Icon name="calendar" size={36} />
            </div>
            <div className="app-empty-state-copy">
              <h3>Aucun évènement pour l'instant</h3>
              <p>Créez votre premier évènement à la main, ou importez un fichier existant pour démarrer plus vite.</p>
            </div>
            <div className="app-empty-state-actions">
              <button
                type="button"
                className="button button-primary"
                onClick={() => startTransition(() => navigate(routes.createPath))}
              >
                <IconLabel icon="plus" label="Créer un évènement" />
              </button>
              <button type="button" className="button" onClick={() => importInputRef.current?.click()}>
                <IconLabel icon="upload" label="Importer" />
              </button>
            </div>
          </section>
        ) : catalogView === "table" ? (
          <div className="table-wrap event-table-wrap">
            <table>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className={`${header.id === "select" ? "table-select-col " : ""}table-col-${header.id}`}
                      >
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="empty-cell">
                      Aucun évènement ne correspond aux filtres actifs.
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <tr
                      key={row.id}
                      className={row.getIsSelected() ? "row-bulk-selected" : ""}
                      onClick={() => {
                        if (editingRowId === row.original.id) {
                          return;
                        }
                        startTransition(() => navigate(routes.eventDetailPath(row.original.id)));
                      }}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className={`table-col-${cell.column.id}`}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <EventCalendarView
            events={filteredEvents}
            todayIso={todayIso()}
            onOpenEvent={(eventId, occurrenceDate, sourceDate) => {
              const targetEvent = filteredEvents.find((event) => event.id === eventId) || null;
              const allowOccurrenceScope = RECURRING_EVENTS_V1_ENABLED && hasEventRecurrence(targetEvent?.recurrence);
              const searchParams = new URLSearchParams();
              if (allowOccurrenceScope && occurrenceDate) {
                searchParams.set("occurrence", occurrenceDate);
              }
              if (allowOccurrenceScope && sourceDate) {
                searchParams.set("source", sourceDate);
              }
              const search = searchParams.toString();
              startTransition(() => navigate(routes.eventDetailPath(eventId, search)));
            }}
          />
        )}
      </AppSurface>

      <ModalShell
        isOpen={isImportModalOpen}
        title="Importer des évènements"
        subtitle="Ajoutez un fichier CSV ou XLSX, vérifiez les colonnes puis importez les lignes valides."
        onClose={() => { if (!isParsingImport && !importMutation.isPending) setIsImportModalOpen(false); }}
      >
        <div className="import-template-bar">
          <div className="import-template-card">
            <strong>Modele d’import</strong>
            <p className="muted">
              Telecharge un fichier pret a remplir avec les bonnes colonnes et une ligne d’exemple.
            </p>
          </div>
          <div className="import-actions">
            <button type="button" className="button" onClick={() => void downloadImportTemplate("csv")}>
              <IconLabel icon="table" label="Modele CSV" />
            </button>
            <button type="button" className="button" onClick={() => void downloadImportTemplate("xlsx")}>
              <IconLabel icon="upload" label="Modele XLSX" />
            </button>
          </div>
        </div>

        <div className="import-dropzone">
          <div className="import-file-field">
            <span className="import-file-label">Fichier a analyser</span>
            <div className="import-file-picker">
              <button
                type="button"
                className="button button-primary"
                onClick={() => importInputRef.current?.click()}
                disabled={isParsingImport || importMutation.isPending}
              >
                <IconLabel icon="upload" label={isParsingImport ? "Analyse en cours..." : "Choisir un fichier"} />
              </button>
              <span className={`import-file-name${importFileName || importPreview ? " has-file" : ""}`}>
                {importPreview?.fileName || importFileName || "Aucun fichier sélectionné"}
              </span>
              <input
                ref={importInputRef}
                className="import-file-input"
                type="file"
                accept=".csv,.xlsx"
                onChange={handleImportFileChange}
                disabled={isParsingImport || importMutation.isPending}
              />
            </div>
          </div>
          <p className="event-form-help">
            L’import crée ou met à jour selon l’ID ou le nom interne de chaque ligne.
          </p>
          <p className="event-form-help">
            Colonnes recommandees: <code>internal_name</code>, <code>display_name</code>, <code>date</code>,{" "}
            <code>start_time</code>, <code>end_time</code>, <code>publication_status</code>, <code>price_*</code>,{" "}
            <code>illustration_*</code>.
          </p>
          <p className="event-form-help">
            Si la colonne <code>publication_status</code> est absente ou vide, une création importée sera ajoutée en{" "}
            <code>draft</code>. Utilisez <code>published</code> uniquement pour rendre l’évènement publiable sur les
            connecteurs.
          </p>
        </div>

        <p className={statusClassName(importStatus)}>{importStatus.message}</p>

        {importMappingError ? <p role="alert" className="status-line error">{importMappingError}</p> : null}
        {importPreview?.columns && !isMappingConfirmed ? (
          <ImportColumnMapping key={importFileName + String(importFile?.lastModified)} columns={importPreview.columns} fields={importFields} busy={isParsingImport} onApply={applyImportMapping} />
        ) : null}
        {importPreview?.columns && isMappingConfirmed ? (
          <button type="button" className="button" disabled={isParsingImport} onClick={() => setIsMappingConfirmed(false)}>Modifier les correspondances</button>
        ) : null}

        {importPreview && isMappingConfirmed ? (
          <div className="import-summary">
            <div className="detail-meta-list">
              <div className="detail-meta-item">
                <strong>Fichier</strong>
                <span>{importPreview.fileName}</span>
              </div>
              <div className="detail-meta-item">
                <strong>Feuille</strong>
                <span>{importPreview.sheetName}</span>
              </div>
              <div className="detail-meta-item">
                <strong>Importables</strong>
                <span>
                  {importPreview.readyCount} / {importPreview.entries.length}
                </span>
              </div>
              <div className="detail-meta-item">
                <strong>Creations</strong>
                <span>{importPreview.createCount}</span>
              </div>
              <div className="detail-meta-item">
                <strong>Mises a jour</strong>
                <span>{importPreview.updateCount}</span>
              </div>
            </div>

            <div className="import-actions">
              <button
                type="button"
                className="button button-primary"
                disabled={importMutation.isPending || isParsingImport || importPreview.readyCount === 0}
                onClick={() => importMutation.mutate()}
              >
                {importMutation.isPending ? "Import en cours..." : "Importer les lignes valides"}
              </button>
              <button
                type="button"
                className="button"
                disabled={importMutation.isPending}
                onClick={() => {
                  setImportPreview(null);
                  setImportFileName("");
                  setImportFile(null);
                  setImportMappingError("");
                }}
              >
                Effacer l’aperçu
              </button>
            </div>

            <div className="import-preview-list">
              {importPreview.entries.slice(0, 6).map((entry) => (
                <div
                  key={`${entry.rowNumber}-${entry.form.internal_name}-${entry.form.display_name}`}
                  className={`import-preview-item${entry.issues.length > 0 ? " has-issues" : ""}`}
                >
                  <strong>
                    Ligne {entry.rowNumber} · {entry.mode === "update" ? "mise a jour" : "creation"}
                  </strong>
                  <div className="muted-inline">
                    {entry.form.display_name || "Sans nom"} · {publicationStatusLabel(entry.form.publication_status)} ·{" "}
                    {entry.form.date || "-"} ·{" "}
                    {entry.form.start_time || "-"} - {entry.form.end_time || "-"}
                  </div>
                  {entry.issues.length > 0 ? (
                    <ul className="issue-list">
                      {entry.issues.map((issue) => (
                        <li key={issue}>{issue}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ))}
            </div>

            {importPreview.entries.length > 6 ? (
              <p className="event-form-help">
                Aperçu limité aux 6 premières lignes. L’import traite toutes les lignes valides.
              </p>
            ) : null}
          </div>
        ) : null}
      </ModalShell>

      <ModalShell
        isOpen={isNewsletterModalOpen}
        title="Generer une newsletter"
        subtitle="Choisissez une période, prévisualisez la newsletter puis copiez-la ou ouvrez la page Newsletter."
        onClose={() => setIsNewsletterModalOpen(false)}
      >
        <NewsletterFromEventsBuilder
          events={events}
          period={newsletterPeriod}
          defaultDesign={newsletterDefaultDesign}
          canOpenNewsletterComposer={canOpenNewsletterComposer}
          onPeriodChange={setNewsletterPeriod}
          onOpenNewsletterDraft={handleOpenNewsletterDraft}
        />
      </ModalShell>

      <ModalShell
        isOpen={isBulkEditModalOpen}
        title="Modifier en lot"
        subtitle={`Appliquer des changements communs aux ${selectedEvents.length} évènements sélectionnés.`}
        onClose={() => setIsBulkEditModalOpen(false)}
      >
        <div className="bulk-panel-grid">
          <BulkFieldRow
            label="Date"
            field={bulkEditForm.date}
            onToggle={() => updateBulkField("date", (current) => ({ ...current, enabled: !current.enabled }))}
          >
            <input
              type="date"
              value={bulkEditForm.date.value}
              onChange={(event) => updateBulkField("date", { ...bulkEditForm.date, value: event.target.value })}
            />
          </BulkFieldRow>

          <BulkFieldRow
            label="Debut"
            field={bulkEditForm.start_time}
            onToggle={() => updateBulkField("start_time", (current) => ({ ...current, enabled: !current.enabled }))}
          >
            <input
              type="time"
              value={bulkEditForm.start_time.value}
              onChange={(event) =>
                updateBulkField("start_time", { ...bulkEditForm.start_time, value: event.target.value })
              }
            />
          </BulkFieldRow>

          <BulkFieldRow
            label="Fin"
            field={bulkEditForm.end_time}
            onToggle={() => updateBulkField("end_time", (current) => ({ ...current, enabled: !current.enabled }))}
          >
            <input
              type="time"
              value={bulkEditForm.end_time.value}
              onChange={(event) => updateBulkField("end_time", { ...bulkEditForm.end_time, value: event.target.value })}
            />
          </BulkFieldRow>

          <BulkFieldRow
            label="Fuseau horaire"
            field={bulkEditForm.timezone}
            onToggle={() => updateBulkField("timezone", (current) => ({ ...current, enabled: !current.enabled }))}
          >
            <input
              value={bulkEditForm.timezone.value}
              onChange={(event) => updateBulkField("timezone", { ...bulkEditForm.timezone, value: event.target.value })}
              placeholder="Europe/Paris"
            />
          </BulkFieldRow>

          <BulkFieldRow
            label="Nom du lieu"
            field={bulkEditForm.location_name}
            onToggle={() =>
              updateBulkField("location_name", (current) => ({ ...current, enabled: !current.enabled }))
            }
          >
            <div className="event-autocomplete-field">
              <input
                value={bulkEditForm.location_name.value}
                onChange={(event) => updateBulkLocationField("location_name", event.target.value, { enable: true })}
                onFocus={() => setIsBulkLocationNameFocused(true)}
                onBlur={() => window.setTimeout(() => setIsBulkLocationNameFocused(false), 120)}
                placeholder="Halle des expositions"
              />
              {showBulkLocationSuggestions ? (
                <div className="event-autocomplete-list" role="listbox" aria-label="Lieux existants pour l'edition en lot">
                  {bulkLocationSuggestions.map((suggestion) => (
                    <button
                      key={`${suggestion.location_name}::${suggestion.location_address}`}
                      type="button"
                      className="event-autocomplete-item"
                      onMouseDown={(mouseEvent) => {
                        mouseEvent.preventDefault();
                        applySavedBulkLocationSuggestion(suggestion);
                      }}
                    >
                      <strong>{suggestion.location_name}</strong>
                      <span>{composeEventAddress(suggestion) || "Adresse non renseignee"}</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </BulkFieldRow>

          <BulkFieldRow
            label="N°"
            field={bulkEditForm.location_street_number}
            onToggle={() =>
              updateBulkField("location_street_number", (current) => ({ ...current, enabled: !current.enabled }))
            }
          >
            <input
              value={bulkEditForm.location_street_number.value}
              onChange={(event) => updateBulkLocationField("location_street_number", event.target.value, { enable: true })}
              placeholder="12"
            />
          </BulkFieldRow>

          <BulkFieldRow
            label="Adresse"
            field={bulkEditForm.location_street}
            onToggle={() =>
              updateBulkField("location_street", (current) => ({ ...current, enabled: !current.enabled }))
            }
          >
            <input
              value={bulkEditForm.location_street.value}
              onChange={(event) => updateBulkLocationField("location_street", event.target.value, { enable: true })}
              placeholder="Route de Saint-Joseph"
            />
          </BulkFieldRow>

          <BulkFieldRow
            label="Code postal"
            field={bulkEditForm.location_postal_code}
            onToggle={() =>
              updateBulkField("location_postal_code", (current) => ({ ...current, enabled: !current.enabled }))
            }
          >
            <input
              value={bulkEditForm.location_postal_code.value}
              onChange={(event) => updateBulkLocationField("location_postal_code", event.target.value, { enable: true })}
              placeholder="44000"
            />
          </BulkFieldRow>

          <BulkFieldRow
            label="Ville"
            field={bulkEditForm.location_city}
            onToggle={() =>
              updateBulkField("location_city", (current) => ({ ...current, enabled: !current.enabled }))
            }
          >
            <input
              value={bulkEditForm.location_city.value}
              onChange={(event) => updateBulkLocationField("location_city", event.target.value, { enable: true })}
              placeholder="Nantes"
            />
          </BulkFieldRow>

          <BulkFieldRow
            label="Description"
            field={bulkEditForm.description}
            onToggle={() =>
              updateBulkField("description", (current) => ({ ...current, enabled: !current.enabled }))
            }
          >
            <textarea
              rows={3}
              value={bulkEditForm.description.value}
              onChange={(event) =>
                updateBulkField("description", { ...bulkEditForm.description, value: event.target.value })
              }
            />
          </BulkFieldRow>

          <BulkFieldRow
            label="Infos pratiques"
            field={bulkEditForm.practical_info}
            onToggle={() =>
              updateBulkField("practical_info", (current) => ({ ...current, enabled: !current.enabled }))
            }
          >
            <textarea
              rows={3}
              value={bulkEditForm.practical_info.value}
              onChange={(event) =>
                updateBulkField("practical_info", { ...bulkEditForm.practical_info, value: event.target.value })
              }
            />
          </BulkFieldRow>

          <BulkFieldRow
            label="Lien Facebook"
            field={bulkEditForm.facebook_url}
            onToggle={() =>
              updateBulkField("facebook_url", (current) => ({ ...current, enabled: !current.enabled }))
            }
          >
            <input
              value={bulkEditForm.facebook_url.value}
              onChange={(event) =>
                updateBulkField("facebook_url", { ...bulkEditForm.facebook_url, value: event.target.value })
              }
              placeholder="Laisser vide + cocher pour effacer"
            />
          </BulkFieldRow>

          <BulkFieldRow
            label="Libellé prix"
            field={bulkEditForm.price_label}
            onToggle={() => updateBulkField("price_label", (current) => ({ ...current, enabled: !current.enabled }))}
          >
            <input
              value={bulkEditForm.price_label.value}
              onChange={(event) =>
                updateBulkField("price_label", { ...bulkEditForm.price_label, value: event.target.value })
              }
            />
          </BulkFieldRow>

          <BulkFieldRow
            label="Montant"
            field={bulkEditForm.price_amount}
            onToggle={() =>
              updateBulkField("price_amount", (current) => ({ ...current, enabled: !current.enabled }))
            }
          >
            <input
              value={bulkEditForm.price_amount.value}
              onChange={(event) =>
                updateBulkField("price_amount", { ...bulkEditForm.price_amount, value: event.target.value })
              }
              placeholder="12.50"
            />
          </BulkFieldRow>

          <BulkFieldRow
            label="Devise"
            field={bulkEditForm.price_currency}
            onToggle={() =>
              updateBulkField("price_currency", (current) => ({ ...current, enabled: !current.enabled }))
            }
          >
            <input
              value={bulkEditForm.price_currency.value}
              onChange={(event) =>
                updateBulkField("price_currency", {
                  ...bulkEditForm.price_currency,
                  value: event.target.value.toUpperCase(),
                })
              }
            />
          </BulkFieldRow>

          <div className="event-summary-actions">
            <button
              type="button"
              className="button button-primary"
              disabled={bulkUpdateMutation.isPending || bulkConnectorSyncMutation.isPending || !hasEnabledBulkEdit(bulkEditForm)}
              onClick={() => bulkUpdateMutation.mutate()}
            >
              {bulkUpdateMutation.isPending ? "Application..." : "Appliquer à la sélection"}
            </button>
            <button type="button" className="button" onClick={() => setBulkEditForm(createEmptyBulkEditForm())}>
              Réinitialiser
            </button>
          </div>
        </div>
      </ModalShell>
    </AppChrome>
  );
}

function EventSettingsPage({ embedded = false }: { embedded?: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { connectorKey } = useParams();
  const skipLeaveConfirmationRef = useRef(false);
  const queryClient = useQueryClient();
  const { metaQuery } = usePlannerData();
  const browserPluginEndpointUrl = useMemo(() => resolveBrowserPluginEndpointUrl(), []);
  const caldavQuery = useQuery({
    queryKey: ["event-planner-caldav-config"],
    queryFn: () => fetchJson<CalDAVSettingsRecord>(apiUrl("connectors/caldav/config")),
    retry: false,
  });
  const publicPageQuery = useQuery({
    queryKey: ["event-planner-public-page-config"],
    queryFn: () => fetchJson<EventPublicPageSettingsRecord>(apiUrl("events/public-page/config")),
    retry: false,
  });
  const icsFeedQuery = useQuery({
    queryKey: ["event-planner-ics-feed-config"],
    queryFn: () => fetchJson<IcsFeedSettingsRecord>(apiUrl("connectors/ics-feed/config")),
    retry: false,
  });
  const [settingsForm, setSettingsForm] = useState<ConnectorSettingsRecord[]>([]);
  const [settingsStatus, setSettingsStatus] = useState<StatusState>({ message: "", tone: "neutral" });
  const [publicPageForm, setPublicPageForm] = useState<EventPublicPageSettingsRecord>(createDefaultEventPublicPageSettings());
  const [publicPageStatus, setPublicPageStatus] = useState<StatusState>({ message: "", tone: "neutral" });
  const [isPublicLogoModalOpen, setIsPublicLogoModalOpen] = useState(false);
  const [caldavForm, setCaldavForm] = useState<CalDAVSettingsRecord>(createDefaultCalDAVSettings());
  const [caldavStatus, setCaldavStatus] = useState<StatusState>({ message: "", tone: "neutral" });
  const [icsFeedForm, setIcsFeedForm] = useState<IcsFeedSettingsRecord>(createDefaultIcsFeedSettings());
  const [icsFeedStatus, setIcsFeedStatus] = useState<StatusState>({ message: "", tone: "neutral" });
  const [standaloneConnectorKey, setStandaloneConnectorKey] = useState<string>("");
  const activeConnectorKey = embedded ? connectorKey || "" : standaloneConnectorKey;

  function finishSettingsSave(message: string) {
    if (!embedded) return;
    skipLeaveConfirmationRef.current = true;
    navigate("/settings/events", { replace: true, state: { settingsSaved: message } });
  }

  useEffect(() => {
    skipLeaveConfirmationRef.current = false;
    setSettingsStatus({ message: "", tone: "neutral" });
    setPublicPageStatus({ message: "", tone: "neutral" });
    setCaldavStatus({ message: "", tone: "neutral" });
    setCaldavForm((current) => ({ ...current, password: "" }));
    setIcsFeedStatus({ message: "", tone: "neutral" });
  }, [location.pathname]);

  useEffect(() => {
    document.title = "Relyo · Évènements · Paramètres";
  }, []);

  useEffect(() => {
    setSettingsForm(connectorSettingsFromMeta(metaQuery.data));
  }, [metaQuery.data, location.pathname]);

  useEffect(() => {
    setCaldavForm((current) => createDefaultCalDAVSettings({ ...caldavQuery.data, password: current.password || "" }));
  }, [caldavQuery.data, location.pathname]);

  useEffect(() => {
    setPublicPageForm(createDefaultEventPublicPageSettings(publicPageQuery.data));
  }, [publicPageQuery.data, location.pathname]);

  useEffect(() => {
    setIcsFeedForm(createDefaultIcsFeedSettings(icsFeedQuery.data));
  }, [icsFeedQuery.data, location.pathname]);

  const hasPublicPageConfig = Boolean(publicPageQuery.data) || publicPageQuery.isLoading || !publicPageQuery.isError;
  const hasCalDAV = Boolean(caldavQuery.data) || caldavQuery.isLoading || !caldavQuery.isError;
  const hasIcsFeed = Boolean(icsFeedQuery.data) || icsFeedQuery.isLoading || !icsFeedQuery.isError;
  const publicAccentSwatches = ["#D95F43", "#C35E3E", "#9B6B37", "#2E7A67", "#2B5F8A", "#6A4C93"];
  const publicSecondarySwatches = ["#6F7D8C", "#8A7464", "#857246", "#2E7A67", "#2B5F8A", "#6A4C93"];
  const connectorMenuItems = useMemo(
    () => [
      ...(hasPublicPageConfig
        ? [
            {
              key: "public_page",
              label: "Page publique",
              configured: publicPageForm.enabled,
              updatedAt: publicPageForm.updated_at,
              category: "Diffusion publique",
              summary: "Publier une page évènement clé en main sur le domaine Relyo.",
            },
          ]
        : []),
      ...settingsForm.map((connector) => ({
        key: connector.connector_key,
        label: connector.label,
        configured: connector.is_configured,
        updatedAt: connector.updated_at,
        category: undefined,
        summary: undefined,
      })),
      ...(hasCalDAV
        ? [
            {
              key: "caldav",
              label: "Calendrier CalDAV",
              configured: caldavForm.configured,
              updatedAt: caldavForm.updated_at,
              category: undefined,
              summary: undefined,
            },
          ]
        : []),
      ...(hasIcsFeed
        ? [
            {
              key: "ics_feed",
              label: "Flux ICS public",
              configured: icsFeedForm.configured,
              updatedAt: icsFeedForm.updated_at,
              category: undefined,
              summary: undefined,
            },
          ]
        : []),
    ],
    [
      caldavForm.configured,
      caldavForm.updated_at,
      hasCalDAV,
      hasIcsFeed,
      hasPublicPageConfig,
      icsFeedForm.configured,
      icsFeedForm.updated_at,
      publicPageForm.enabled,
      publicPageForm.updated_at,
      settingsForm,
    ]
  );

  useEffect(() => {
    if (embedded) return;
    if (!connectorMenuItems.length) {
      setStandaloneConnectorKey("");
      return;
    }
    if (!connectorMenuItems.some((item) => item.key === activeConnectorKey)) {
      setStandaloneConnectorKey(connectorMenuItems[0]?.key || "");
    }
  }, [activeConnectorKey, connectorMenuItems, embedded]);

  const saveSettingsMutation = useMutation({
    mutationFn: () =>
      fetchJson<Record<string, ConnectorSettingsRecord>>(apiUrl("settings/connectors"), {
        method: "POST",
        body: JSON.stringify({
          connectors: settingsForm.map((connector) => normalizeConnectorSettingsForSave(connector, browserPluginEndpointUrl)),
        }),
      }),
    onSuccess: (savedSettings) => {
      queryClient.setQueryData<EventMeta | undefined>(["event-planner-meta"], (current) =>
        current ? { ...current, connector_settings: savedSettings } : current
      );
      setSettingsForm(connectorSettingsFromMeta({ ...(metaQuery.data || ({} as EventMeta)), connector_settings: savedSettings }));
      setSettingsStatus({ message: "Paramètres connecteurs enregistrés.", tone: "success" });
      finishSettingsSave("Paramètres connecteurs enregistrés.");
    },
    onError: (error) => {
      const normalizedError = normalizeApiError(error);
      setSettingsStatus({ message: normalizedError.message, tone: "error" });
    },
  });

  const savePublicPageMutation = useMutation({
    mutationFn: () =>
      fetchJson<EventPublicPageSettingsRecord>(apiUrl("events/public-page/config"), {
        method: "POST",
        body: JSON.stringify({
          enabled: publicPageForm.enabled,
          website_url: publicPageForm.website_url,
          website_label: publicPageForm.website_label,
          display: publicPageForm.display,
        }),
      }),
    onSuccess: (savedConfig) => {
      queryClient.setQueryData<EventPublicPageSettingsRecord>(["event-planner-public-page-config"], savedConfig);
      setPublicPageForm(createDefaultEventPublicPageSettings(savedConfig));
      setPublicPageStatus({ message: "Paramètres de la page publique enregistrés.", tone: "success" });
      finishSettingsSave("Paramètres de la page publique enregistrés.");
    },
    onError: (error) => {
      const normalizedError = normalizeApiError(error);
      setPublicPageStatus({ message: normalizedError.message, tone: "error" });
    },
  });

  const saveCalDAVMutation = useMutation({
    mutationFn: () =>
      fetchJson<CalDAVSettingsRecord>(apiUrl("connectors/caldav/config"), {
        method: "POST",
        body: JSON.stringify({
          calendar_url: caldavForm.calendar_url,
          username: caldavForm.username,
          password: caldavForm.password,
          timezone: caldavForm.timezone,
        }),
      }),
    onSuccess: (savedConfig) => {
      queryClient.setQueryData<CalDAVSettingsRecord>(["event-planner-caldav-config"], savedConfig);
      setCaldavForm(createDefaultCalDAVSettings({ ...savedConfig, password: "" }));
      setCaldavStatus({ message: "Configuration CalDAV enregistrée.", tone: "success" });
      finishSettingsSave("Configuration CalDAV enregistrée.");
    },
    onError: (error) => {
      const normalizedError = normalizeApiError(error);
      setCaldavStatus({ message: normalizedError.message, tone: "error" });
    },
  });

  const saveIcsFeedMutation = useMutation({
    mutationFn: () =>
      fetchJson<IcsFeedSettingsRecord>(apiUrl("connectors/ics-feed/config"), {
        method: "POST",
        body: JSON.stringify({
          configured: icsFeedForm.configured,
          calendar_title: icsFeedForm.calendar_title,
        }),
      }),
    onSuccess: (savedConfig) => {
      queryClient.setQueryData<IcsFeedSettingsRecord>(["event-planner-ics-feed-config"], savedConfig);
      setIcsFeedForm(createDefaultIcsFeedSettings(savedConfig));
      setIcsFeedStatus({ message: "Flux ICS public enregistre.", tone: "success" });
      finishSettingsSave("Flux ICS public enregistre.");
    },
    onError: (error) => {
      const normalizedError = normalizeApiError(error);
      setIcsFeedStatus({ message: normalizedError.message, tone: "error" });
    },
  });

  const regenerateIcsFeedMutation = useMutation({
    mutationFn: () =>
      fetchJson<IcsFeedSettingsRecord>(apiUrl("connectors/ics-feed/config"), {
        method: "POST",
        body: JSON.stringify({
          configured: icsFeedForm.configured,
          calendar_title: icsFeedForm.calendar_title,
          regenerate_token: true,
        }),
      }),
    onSuccess: (savedConfig) => {
      queryClient.setQueryData<IcsFeedSettingsRecord>(["event-planner-ics-feed-config"], savedConfig);
      setIcsFeedForm(createDefaultIcsFeedSettings(savedConfig));
      setIcsFeedStatus({ message: "Lien du flux ICS regenere.", tone: "success" });
    },
    onError: (error) => {
      const normalizedError = normalizeApiError(error);
      setIcsFeedStatus({ message: normalizedError.message, tone: "error" });
    },
  });

  function updateConnectorSetting(
    connectorKey: string,
    updater: Partial<ConnectorSettingsRecord> | ((current: ConnectorSettingsRecord) => ConnectorSettingsRecord)
  ) {
    setSettingsForm((current) =>
      current.map((connector) => {
        if (connector.connector_key !== connectorKey) {
          return connector;
        }
        return typeof updater === "function" ? updater(connector) : { ...connector, ...updater };
      })
    );
  }

  function updateCalDAVField<Key extends keyof CalDAVSettingsRecord>(field: Key, value: CalDAVSettingsRecord[Key]) {
    setCaldavForm((current) => ({ ...current, [field]: value }));
  }

  function updatePublicPageField<Key extends keyof EventPublicPageSettingsRecord>(
    field: Key,
    value: EventPublicPageSettingsRecord[Key]
  ) {
    setPublicPageForm((current) => ({ ...current, [field]: value }));
  }

  function updatePublicPageDisplayField<Key extends keyof EventPublicPageSettingsRecord["display"]>(
    field: Key,
    value: EventPublicPageSettingsRecord["display"][Key]
  ) {
    setPublicPageForm((current) => ({
      ...current,
      display: {
        ...current.display,
        [field]: value,
      },
    }));
  }

  function resolveFrontendPublicPageUrl(rawUrl: string) {
    const trimmedUrl = String(rawUrl || "").trim();
    if (!trimmedUrl || typeof window === "undefined") {
      return trimmedUrl;
    }
    try {
      const resolvedUrl = new URL(trimmedUrl, window.location.origin);
      if (resolvedUrl.hostname === window.location.hostname && resolvedUrl.pathname.startsWith("/public/events/")) {
        return `${window.location.origin}${resolvedUrl.pathname}${resolvedUrl.search}${resolvedUrl.hash}`;
      }
      return resolvedUrl.toString();
    } catch (_error) {
      return trimmedUrl;
    }
  }

  function updateIcsFeedField<Key extends keyof IcsFeedSettingsRecord>(field: Key, value: IcsFeedSettingsRecord[Key]) {
    setIcsFeedForm((current) => ({ ...current, [field]: value }));
  }

  const activeGenericConnector =
    activeConnectorKey && activeConnectorKey !== "caldav" && activeConnectorKey !== "ics_feed" && activeConnectorKey !== "public_page"
      ? settingsForm.find((connector) => connector.connector_key === activeConnectorKey) || null
      : null;
  const activeDescriptor =
    CONNECTOR_WIREFRAME_DESCRIPTORS[activeConnectorKey] ||
    CONNECTOR_WIREFRAME_DESCRIPTORS[activeGenericConnector?.connector_key || ""] ||
    null;
  const isPublicPageActive = activeConnectorKey === "public_page";
  const isCalDAVActive = activeConnectorKey === "caldav";
  const isIcsFeedActive = activeConnectorKey === "ics_feed";
  const isBrowserPluginActive = activeGenericConnector?.connector_key === "browser_plugin";
  const activeUtilityHref = activeGenericConnector ? connectorUtilityHref(activeGenericConnector.connector_key) : null;
  const activeStatus = isPublicPageActive ? publicPageStatus : isCalDAVActive ? caldavStatus : isIcsFeedActive ? icsFeedStatus : settingsStatus;
  const isActiveConfigured = isPublicPageActive
    ? publicPageForm.enabled
    : isCalDAVActive
    ? caldavForm.configured
    : isIcsFeedActive
      ? icsFeedForm.configured
      : Boolean(activeGenericConnector?.is_configured);
  const activeSaveLabel = isPublicPageActive
    ? savePublicPageMutation.isPending
      ? "Enregistrement..."
      : "Enregistrer"
    : isCalDAVActive
    ? saveCalDAVMutation.isPending
      ? "Enregistrement..."
      : "Enregistrer"
    : isIcsFeedActive
      ? saveIcsFeedMutation.isPending
        ? "Enregistrement..."
        : "Enregistrer"
      : saveSettingsMutation.isPending
      ? "Enregistrement..."
      : "Enregistrer";
  const publicPageUrl = resolveFrontendPublicPageUrl(publicPageForm.public_url);
  const publicPageEmbedUrl = resolveFrontendPublicPageUrl(publicPageForm.embed_url);
  const publicPagePreviewUrl = resolveFrontendPublicPageUrl(publicPageForm.preview_url);
  const isSavingActive = isPublicPageActive
    ? savePublicPageMutation.isPending
    : isCalDAVActive
    ? saveCalDAVMutation.isPending
    : isIcsFeedActive
      ? saveIcsFeedMutation.isPending || regenerateIcsFeedMutation.isPending
      : saveSettingsMutation.isPending;
  const isLoadingActive = isPublicPageActive
    ? publicPageQuery.isLoading
    : isCalDAVActive
      ? caldavQuery.isLoading
      : isIcsFeedActive
        ? icsFeedQuery.isLoading
        : metaQuery.isLoading;
  const isConnectionList = embedded && !connectorKey;
  const activeMenuItem = connectorMenuItems.find((item) => item.key === activeConnectorKey);
  const hasUnknownConnection = embedded && Boolean(connectorKey) && !activeMenuItem && !metaQuery.isLoading;
  const currentEditableSettings = isPublicPageActive
    ? { enabled: publicPageForm.enabled, website_url: publicPageForm.website_url, website_label: publicPageForm.website_label, display: publicPageForm.display }
    : isCalDAVActive
      ? caldavForm
      : isIcsFeedActive
        ? { configured: icsFeedForm.configured, calendar_title: icsFeedForm.calendar_title }
        : activeGenericConnector;
  const savedPublicPage = createDefaultEventPublicPageSettings(publicPageQuery.data);
  const savedIcsFeed = createDefaultIcsFeedSettings(icsFeedQuery.data);
  const savedEditableSettings = isPublicPageActive
    ? { enabled: savedPublicPage.enabled, website_url: savedPublicPage.website_url, website_label: savedPublicPage.website_label, display: savedPublicPage.display }
    : isCalDAVActive
      ? createDefaultCalDAVSettings({ ...caldavQuery.data, password: "" })
      : isIcsFeedActive
        ? { configured: savedIcsFeed.configured, calendar_title: savedIcsFeed.calendar_title }
        : connectorSettingsFromMeta(metaQuery.data).find((item) => item.connector_key === activeConnectorKey) || null;
  const hasUnsavedSettings = embedded && Boolean(activeMenuItem) && !isLoadingActive &&
    JSON.stringify(currentEditableSettings) !== JSON.stringify(savedEditableSettings);
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    embedded && (hasUnsavedSettings || isSavingActive) && !skipLeaveConfirmationRef.current && currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    if (blocker.state !== "blocked") return;
    if (isSavingActive) {
      blocker.reset();
      return;
    }
    if (window.confirm("Quitter cette connexion sans enregistrer les modifications ?")) blocker.proceed();
    else blocker.reset();
  }, [blocker, isSavingActive]);

  useEffect(() => {
    if (!hasUnsavedSettings) return;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [hasUnsavedSettings]);

  return (
    <div className="event-editor-shell">
      {!embedded ? <AppToolbar leading={<ReturnToCatalogLink />} /> : null}
      {embedded && !isConnectionList ? (
        <div className="event-toolbar-actions connector-settings-back">
          <button type="button" className="button" disabled={isSavingActive} onClick={() => navigate("/settings/events")}>
            Retour aux connexions
          </button>
        </div>
      ) : null}

      <section className="panel panel-primary">
        <div className="panel-head">
          <div>
            <p className="section-kicker">Configuration</p>
            <h2>{embedded && !isConnectionList ? activeMenuItem?.label || (metaQuery.isLoading ? "Connexion" : "Connexion introuvable") : "Connexions"}</h2>
            {isConnectionList ? <p className="muted">Choisissez une connexion pour ouvrir ses réglages.</p> : null}
          </div>
          {!isConnectionList && !hasUnknownConnection ? <div className="event-toolbar-actions">
            <button
              type="button"
              className="button"
              onClick={() => {
                if (isPublicPageActive) {
                  setPublicPageForm(createDefaultEventPublicPageSettings(publicPageQuery.data));
                  return;
                }
                if (isCalDAVActive) {
                  setCaldavForm(createDefaultCalDAVSettings({ ...caldavQuery.data, password: "" }));
                  return;
                }
                if (isIcsFeedActive) {
                  setIcsFeedForm(createDefaultIcsFeedSettings(icsFeedQuery.data));
                  return;
                }
                setSettingsForm(connectorSettingsFromMeta(metaQuery.data));
              }}
              disabled={isSavingActive}
            >
              <IconLabel icon="refresh" label="Recharger" />
            </button>
            <button
              type="button"
              className="button button-primary"
              onClick={() => {
                if (isPublicPageActive) {
                  savePublicPageMutation.mutate();
                  return;
                }
                if (isCalDAVActive) {
                  saveCalDAVMutation.mutate();
                  return;
                }
                if (isIcsFeedActive) {
                  saveIcsFeedMutation.mutate();
                  return;
                }
                saveSettingsMutation.mutate();
              }}
              disabled={!activeConnectorKey || isSavingActive}
            >
              <IconLabel icon="settings" label={embedded && !isSavingActive ? "Enregistrer et revenir" : activeSaveLabel} />
            </button>
          </div> : null}
        </div>

        {isLoadingActive ? (
          <p className="status-line">
            {isPublicPageActive
              ? "Chargement de la page publique..."
              : isCalDAVActive
              ? "Chargement de la configuration CalDAV..."
              : isIcsFeedActive
                ? "Chargement du flux ICS public..."
                : "Chargement des parametres connecteurs..."}
          </p>
        ) : null}
        {isConnectionList && location.state?.settingsSaved ? <p className="status-line success" role="status">{location.state.settingsSaved}</p> : null}
        {!isConnectionList && activeStatus.message ? <p className={statusClassName(activeStatus)}>{activeStatus.message}</p> : null}

        {hasUnknownConnection ? <p className="status-line">Cette connexion n’est pas disponible.</p> : connectorMenuItems.length ? (
          <div className={`connector-settings-layout${embedded ? " is-page-navigation" : ""}`}>
            {!embedded || isConnectionList ? <aside className="connector-settings-sidebar">
              <div className="connector-settings-nav">
                {connectorMenuItems.map((connector) => {
                  const descriptor = CONNECTOR_WIREFRAME_DESCRIPTORS[connector.key];
                  const isActive = connector.key === activeConnectorKey;
                  return (
                    <button
                      key={connector.key}
                      type="button"
                      className={`connector-settings-nav-item${isActive ? " is-active" : ""}`}
                      onClick={() => embedded ? navigate(`/settings/events/${encodeURIComponent(connector.key)}`) : setStandaloneConnectorKey(connector.key)}
                    >
                      <div className="connector-settings-nav-head">
                        <div className="connector-settings-nav-title">
                          <strong>{connector.label}</strong>
                          <span>{connector.category || descriptor?.category || "Connexion"}</span>
                        </div>
                        <span
                          className={
                            connector.configured
                              ? "pill connector-pill connector-pill-synced"
                              : "pill connector-pill connector-pill-disabled"
                          }
                        >
                          {connector.configured ? "Configuré" : "À régler"}
                        </span>
                      </div>
                      <p className="connector-settings-nav-copy">{connector.summary || descriptor?.summary || "Configurer cette connexion."}</p>
                      <div className="connector-settings-nav-meta">
                        {!embedded ? <span>{connector.key}</span> : null}
                        <span>{connector.updatedAt ? `Mis à jour: ${connector.updatedAt}` : "Jamais configuré"}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </aside> : null}

            {!isConnectionList ? <div className="connector-settings-detail">
              <section className={`connector-settings-card connector-settings-hero${isActiveConfigured ? " is-configured" : ""}`}>
                <div className="connector-settings-head">
                  <div>
                    <p className="section-kicker">Connexion sélectionnée</p>
                    <h3>
                      {isPublicPageActive
                        ? "Page publique évènements"
                        : isCalDAVActive
                        ? "Calendrier CalDAV"
                        : isIcsFeedActive
                          ? "Flux ICS public"
                          : activeGenericConnector?.label || "Connexion"}
                    </h3>
                  </div>
                  <span
                    className={
                      isActiveConfigured
                        ? "pill connector-pill connector-pill-synced"
                        : "pill connector-pill connector-pill-disabled"
                    }
                  >
                    {isActiveConfigured ? "Configuré" : "Non configuré"}
                  </span>
                </div>
              </section>

              {isPublicPageActive ? (
                <>
                  <section className={`connector-settings-card${publicPageForm.enabled ? " is-configured" : ""}`}>
                    <div className="connector-settings-head">
                      <div>
                        <p className="section-kicker">Publication</p>
                        <h3>Paramètres essentiels</h3>
                      </div>
                    </div>

                    <label className="connector-settings-toggle-card">
                      <input
                        type="checkbox"
                        checked={publicPageForm.enabled}
                        onChange={(event) => updatePublicPageField("enabled", event.target.checked)}
                      />
                      <span className="connector-settings-toggle-indicator" aria-hidden="true" />
                      <span className="connector-settings-toggle-copy">
                        <strong>Activer la page publique</strong>
                        <span>Expose une page évènements publique par tenant et autorise son intégration en iframe.</span>
                      </span>
                    </label>

                    <div className="grid grid-2">
                      <label className="field-span-2">
                        URL du site client
                        <input
                          value={publicPageForm.website_url}
                          onChange={(event) => updatePublicPageField("website_url", event.target.value)}
                          placeholder="https://www.client.fr"
                        />
                      </label>
                      <label className="field-span-2">
                        Libellé du bouton retour
                        <input
                          value={publicPageForm.website_label}
                          onChange={(event) => updatePublicPageField("website_label", event.target.value)}
                          placeholder="Retour au site"
                        />
                      </label>
                      <label className="field-span-2">
                        Nom client affiché
                        <input
                          value={publicPageForm.display.brand_name}
                          onChange={(event) => updatePublicPageDisplayField("brand_name", event.target.value)}
                          placeholder="Le Pont 34"
                        />
                      </label>
                    </div>

                    <div className="public-page-brand-card">
                      <div className="public-page-brand-preview">
                        {publicPageForm.display.logo_url ? (
                          <img src={publicPageForm.display.logo_url} alt={publicPageForm.display.brand_name || "Logo client"} />
                        ) : (
                          <div className="public-page-brand-placeholder">Logo</div>
                        )}
                      </div>
                      <div className="event-toolbar-actions">
                        <button type="button" className="button" onClick={() => setIsPublicLogoModalOpen(true)}>
                          <IconLabel icon="image" label={publicPageForm.display.logo_url ? "Changer le logo" : "Choisir un logo"} />
                        </button>
                        {publicPageForm.display.logo_url ? (
                          <button
                            type="button"
                            className="button"
                            onClick={() => updatePublicPageDisplayField("logo_url", "")}
                          >
                            <IconLabel icon="trash" label="Retirer le logo" />
                          </button>
                        ) : null}
                      </div>
                    </div>

                    <div className="grid grid-2">
                      <label className="field-span-2">
                        URL publique
                        <input value={publicPageUrl} readOnly aria-readonly="true" />
                      </label>
                      <label className="field-span-2">
                        URL iframe
                        <input value={publicPageEmbedUrl} readOnly aria-readonly="true" />
                      </label>
                    </div>

                    <div className="event-toolbar-actions">
                      <button
                        type="button"
                        className="button"
                        disabled={!publicPageUrl}
                        onClick={() => {
                          copyTextToClipboard(publicPageUrl)
                            .then(() => setPublicPageStatus({ message: "URL publique copiée.", tone: "success" }))
                            .catch((error) => {
                              const normalizedError = normalizeApiError(error);
                              setPublicPageStatus({ message: normalizedError.message, tone: "error" });
                            });
                        }}
                      >
                        <IconLabel icon="copy" label="Copier l'URL" />
                      </button>
                      <button
                        type="button"
                        className="button"
                        disabled={!publicPagePreviewUrl}
                        onClick={() => {
                          window.open(publicPagePreviewUrl, "_blank", "noopener,noreferrer");
                          setPublicPageStatus({ message: "Aperçu ouvert dans un nouvel onglet.", tone: "success" });
                        }}
                      >
                        <IconLabel icon="eye" label="Aperçu" />
                      </button>
                    </div>

                    <div className="connector-settings-meta">
                      <span>{publicPageForm.enabled ? "Page publique active" : "Page publique inactive"}</span>
                      <span>Mis à jour: {publicPageForm.updated_at || "-"}</span>
                    </div>
                  </section>

                  <section className="connector-settings-card">
                    <div className="connector-settings-head">
                      <div>
                        <p className="section-kicker">Affichage</p>
                        <h3>Signature visuelle et modules</h3>
                      </div>
                    </div>

                    <div className="public-page-settings-grid">
                      <label className="public-page-settings-check">
                        <input
                          type="checkbox"
                          checked={publicPageForm.display.show_filters}
                          onChange={(event) => updatePublicPageDisplayField("show_filters", event.target.checked)}
                        />
                        <span>Afficher les filtres</span>
                      </label>
                      <label className="public-page-settings-check">
                        <input
                          type="checkbox"
                          checked={publicPageForm.display.show_calendar}
                          onChange={(event) => updatePublicPageDisplayField("show_calendar", event.target.checked)}
                        />
                        <span>Afficher le mini calendrier</span>
                      </label>
                      <label className="public-page-settings-check">
                        <input
                          type="checkbox"
                          checked={publicPageForm.display.show_featured_event}
                          onChange={(event) => updatePublicPageDisplayField("show_featured_event", event.target.checked)}
                        />
                        <span>Mettre en avant un évènement</span>
                      </label>
                      <label className="public-page-settings-check">
                        <input
                          type="checkbox"
                          checked={publicPageForm.display.show_past_events_toggle}
                          onChange={(event) => updatePublicPageDisplayField("show_past_events_toggle", event.target.checked)}
                        />
                        <span>Afficher le toggle à venir / passés</span>
                      </label>
                      <label className="public-page-settings-check">
                        <input
                          type="checkbox"
                          checked={publicPageForm.display.show_site_link}
                          onChange={(event) => updatePublicPageDisplayField("show_site_link", event.target.checked)}
                        />
                        <span>Afficher le bouton retour site</span>
                      </label>
                    </div>

                    <div className="grid grid-2">
                      <div className="field-span-2">
                        <ColorInput
                          label="Couleur principale"
                          format="hex"
                          placeholder="#D95F43"
                          withEyeDropper={false}
                          swatches={publicAccentSwatches}
                          value={publicPageForm.display.accent_color}
                          rightSection={
                            publicPageForm.display.accent_color &&
                            publicPageForm.display.accent_color.toUpperCase() !== "#D95F43" ? (
                              <CloseButton
                                aria-label="Réinitialiser la couleur principale"
                                size="sm"
                                variant="transparent"
                                onClick={() => updatePublicPageDisplayField("accent_color", "#D95F43")}
                              />
                            ) : null
                          }
                          onChange={(value) => updatePublicPageDisplayField("accent_color", value || "#D95F43")}
                        />
                      </div>
                      <div className="field-span-2">
                        <ColorInput
                          label="Couleur secondaire"
                          format="hex"
                          placeholder="#6F7D8C"
                          withEyeDropper={false}
                          swatches={publicSecondarySwatches}
                          value={publicPageForm.display.secondary_color}
                          rightSection={
                            publicPageForm.display.secondary_color &&
                            publicPageForm.display.secondary_color.toUpperCase() !== "#6F7D8C" ? (
                              <CloseButton
                                aria-label="Réinitialiser la couleur secondaire"
                                size="sm"
                                variant="transparent"
                                onClick={() => updatePublicPageDisplayField("secondary_color", "#6F7D8C")}
                              />
                            ) : null
                          }
                          onChange={(value) => updatePublicPageDisplayField("secondary_color", value || "#6F7D8C")}
                        />
                      </div>
                      <label className="field-span-2">
                        Titre du bloc d’introduction
                        <input
                          value={publicPageForm.display.intro_title}
                          onChange={(event) => updatePublicPageDisplayField("intro_title", event.target.value)}
                          placeholder="Un lieu vivant, associatif et ouvert a tous."
                        />
                      </label>
                      <label className="field-span-2">
                        Texte du bloc d’introduction
                        <textarea
                          rows={4}
                          value={publicPageForm.display.intro_body}
                          onChange={(event) => updatePublicPageDisplayField("intro_body", event.target.value)}
                          placeholder="Ajoutez ici un court texte de presentation. S’il est vide, le bloc ne s’affiche pas."
                        />
                      </label>
                    </div>
                  </section>
                </>
              ) : isCalDAVActive ? (
                <section className={`connector-settings-card${caldavForm.configured ? " is-configured" : ""}`}>
                  <div className="connector-settings-head">
                    <div>
                      <p className="section-kicker">Paramètres</p>
                      <h3>Acces calendrier</h3>
                    </div>
                  </div>

                  <label className="connector-settings-toggle-card">
                    <input
                      type="checkbox"
                      checked={caldavForm.configured}
                      onChange={(event) => updateCalDAVField("configured", event.target.checked)}
                    />
                    <span className="connector-settings-toggle-indicator" aria-hidden="true" />
                    <span className="connector-settings-toggle-copy">
                      <strong>{activeDescriptor?.visibilityLabel}</strong>
                      <span>Active cette connexion dans les fiches évènement et les états de diffusion.</span>
                    </span>
                  </label>

                  <div className="grid grid-2">
                    <label className="field-span-2">
                      URL du calendrier CalDAV
                      <input
                        value={caldavForm.calendar_url}
                        onChange={(event) => updateCalDAVField("calendar_url", event.target.value)}
                        placeholder="https://sync.infomaniak.com/ical/..."
                      />
                    </label>
                    <label>
                      Identifiant
                      <input
                        value={caldavForm.username}
                        onChange={(event) => updateCalDAVField("username", event.target.value)}
                        placeholder="Utilisateur, email ou login"
                      />
                    </label>
                    <label>
                      Fuseau horaire
                      <input
                        value={caldavForm.timezone}
                        onChange={(event) => updateCalDAVField("timezone", event.target.value)}
                        placeholder="Europe/Paris"
                      />
                    </label>
                    <label className="field-span-2">
                      Mot de passe / mot de passe d'application
                      <input
                        type="password"
                        value={caldavForm.password}
                        onChange={(event) => updateCalDAVField("password", event.target.value)}
                        placeholder={caldavForm.has_password ? "Laisser vide pour conserver le secret existant" : "Secret CalDAV"}
                      />
                    </label>
                  </div>

                  <div className="connector-settings-meta">
                    <span>{caldavForm.has_password ? "Secret déjà enregistré" : "Aucun secret enregistré"}</span>
                    <span>Mis à jour: {caldavForm.updated_at || "-"}</span>
                  </div>
                </section>
              ) : isIcsFeedActive ? (
                <section className={`connector-settings-card${icsFeedForm.configured ? " is-configured" : ""}`}>
                  <div className="connector-settings-head">
                    <div>
                      <p className="section-kicker">Paramètres</p>
                      <h3>Flux ICS public</h3>
                    </div>
                  </div>

                  <label className="connector-settings-toggle-card">
                    <input
                      type="checkbox"
                      checked={icsFeedForm.configured}
                      onChange={(event) => updateIcsFeedField("configured", event.target.checked)}
                    />
                    <span className="connector-settings-toggle-indicator" aria-hidden="true" />
                    <span className="connector-settings-toggle-copy">
                      <strong>{activeDescriptor?.visibilityLabel}</strong>
                      <span>Le lien reste stable tant que vous ne regenerez pas le token public.</span>
                    </span>
                  </label>

                  <div className="grid grid-2">
                    <label className="field-span-2">
                      Titre du calendrier
                      <input
                        value={icsFeedForm.calendar_title}
                        onChange={(event) => updateIcsFeedField("calendar_title", event.target.value)}
                        placeholder="Relyo · Évènements"
                      />
                    </label>
                    <label className="field-span-2">
                      URL publique du flux
                      <input value={icsFeedForm.feed_url} readOnly placeholder="Enregistrez pour generer le lien public" />
                    </label>
                  </div>

                  <div className="event-toolbar-actions">
                    <button
                      type="button"
                      className="button"
                      disabled={!icsFeedForm.feed_url}
                      onClick={() => {
                        copyTextToClipboard(icsFeedForm.feed_url)
                          .then(() => setIcsFeedStatus({ message: "URL du flux ICS copiee.", tone: "success" }))
                          .catch((error) => {
                            const normalizedError = normalizeApiError(error);
                            setIcsFeedStatus({ message: normalizedError.message, tone: "error" });
                          });
                      }}
                    >
                      <IconLabel icon="copy" label="Copier l'URL" />
                    </button>
                    <button
                      type="button"
                      className="button"
                      disabled={regenerateIcsFeedMutation.isPending}
                      onClick={() => regenerateIcsFeedMutation.mutate()}
                    >
                      <IconLabel
                        icon="refresh"
                        label={regenerateIcsFeedMutation.isPending ? "Regeneration..." : "Regenerer le lien"}
                      />
                    </button>
                  </div>

                  <div className="connector-settings-meta">
                    <span>{icsFeedForm.has_token ? "Lien public genere" : "Aucun lien public genere"}</span>
                    <span>Mis à jour: {icsFeedForm.updated_at || "-"}</span>
                  </div>
                </section>
              ) : activeGenericConnector ? (
                <section className={`connector-settings-card${activeGenericConnector.is_configured ? " is-configured" : ""}`}>
                  <div className="connector-settings-head">
                    <div>
                      <p className="section-kicker">Paramètres</p>
                      <h3>{activeGenericConnector.label}</h3>
                    </div>
                  </div>

                  <label className="connector-settings-toggle-card">
                    <input
                      type="checkbox"
                      checked={activeGenericConnector.is_configured}
                      onChange={(event) =>
                        updateConnectorSetting(activeGenericConnector.connector_key, { is_configured: event.target.checked })
                      }
                    />
                    <span className="connector-settings-toggle-indicator" aria-hidden="true" />
                    <span className="connector-settings-toggle-copy">
                      <strong>{activeDescriptor?.visibilityLabel || "Afficher cette connexion dans les fiches évènement"}</strong>
                      <span>Permet de faire apparaitre cette connexion dans la vue detail et les suivis de diffusion.</span>
                    </span>
                  </label>

                  {isBrowserPluginActive ? (
                    <>
                      <p className="muted">
                        Le plugin récupère automatiquement l'URL du hosted courant. Il suffit de copier le token ci-dessous
                        dans les options de l'extension.
                      </p>
                      <div className="grid grid-2">
                        <label className="field-span-2">
                          {activeDescriptor?.endpointLabel || "URL / endpoint"}
                          <input value={browserPluginEndpointUrl} readOnly aria-readonly="true" />
                        </label>
                        <label className="field-span-2">
                          {activeDescriptor?.secretLabel || "Secret / token"}
                          <div className="event-toolbar-actions">
                            <input
                              type="text"
                              value={activeGenericConnector.secret}
                              readOnly
                              aria-readonly="true"
                              placeholder={activeDescriptor?.secretPlaceholder || "Cle API, mot de passe ou token"}
                            />
                            <button
                              type="button"
                              className="button"
                              disabled={!activeGenericConnector.secret}
                              onClick={() => {
                                copyTextToClipboard(activeGenericConnector.secret)
                                  .then(() => setSettingsStatus({ message: "Token du plugin copie.", tone: "success" }))
                                  .catch((error) => {
                                    const normalizedError = normalizeApiError(error);
                                    setSettingsStatus({ message: normalizedError.message, tone: "error" });
                                  });
                              }}
                            >
                              <IconLabel icon="copy" label="Copier" />
                            </button>
                          </div>
                        </label>
                        <label className="field-span-2">
                          Notes
                          <textarea
                            rows={5}
                            value={activeGenericConnector.notes}
                            onChange={(event) =>
                              updateConnectorSetting(activeGenericConnector.connector_key, { notes: event.target.value })
                            }
                            placeholder={activeDescriptor?.notesPlaceholder || "Contexte, consignes, limitations, remarques..."}
                          />
                        </label>
                      </div>
                    </>
                  ) : (
                    <div className="grid grid-2">
                      <label>
                        {activeDescriptor?.endpointLabel || "URL / endpoint"}
                        <input
                          value={activeGenericConnector.endpoint_url}
                          onChange={(event) =>
                            updateConnectorSetting(activeGenericConnector.connector_key, { endpoint_url: event.target.value })
                          }
                          placeholder={activeDescriptor?.endpointPlaceholder || "https://service.example.com"}
                        />
                      </label>
                      <label>
                        {activeDescriptor?.referenceLabel || "Référence externe"}
                        <input
                          value={activeGenericConnector.external_reference}
                          onChange={(event) =>
                            updateConnectorSetting(activeGenericConnector.connector_key, { external_reference: event.target.value })
                          }
                          placeholder={activeDescriptor?.referencePlaceholder || "Base, site, espace ou compte"}
                        />
                      </label>
                      <label>
                        {activeDescriptor?.usernameLabel || "Identifiant"}
                        <input
                          value={activeGenericConnector.username}
                          onChange={(event) =>
                            updateConnectorSetting(activeGenericConnector.connector_key, { username: event.target.value })
                          }
                          placeholder={activeDescriptor?.usernamePlaceholder || "Utilisateur, email ou login"}
                        />
                      </label>
                      <label>
                        {activeDescriptor?.secretLabel || "Secret / token"}
                        <input
                          type="password"
                          value={activeGenericConnector.secret}
                          onChange={(event) =>
                            updateConnectorSetting(activeGenericConnector.connector_key, { secret: event.target.value })
                          }
                          placeholder={activeDescriptor?.secretPlaceholder || "Cle API, mot de passe ou token"}
                        />
                      </label>
                      <label className="field-span-2">
                        Notes
                        <textarea
                          rows={5}
                          value={activeGenericConnector.notes}
                          onChange={(event) =>
                            updateConnectorSetting(activeGenericConnector.connector_key, { notes: event.target.value })
                          }
                          placeholder={activeDescriptor?.notesPlaceholder || "Contexte, consignes, limitations, remarques..."}
                        />
                      </label>
                    </div>
                  )}

                  <div className="connector-settings-meta">
                    <span>URL: {(isBrowserPluginActive ? browserPluginEndpointUrl : activeGenericConnector.endpoint_url) || "-"}</span>
                    <span>Mis à jour: {activeGenericConnector.updated_at || "-"}</span>
                  </div>
                </section>
              ) : null}

              {activeDescriptor?.utility ? (
                <section className="connector-settings-card connector-settings-utility-card">
                  <div className="connector-settings-head">
                    <div>
                      <p className="section-kicker">Utilitaire</p>
                      <h3>{activeDescriptor.utility.title}</h3>
                    </div>
                    <Icon name="download" />
                  </div>

                  <p className="muted">{activeDescriptor.utility.description}</p>

                  {activeDescriptor.utility.actionLabel ? (
                    <div className="event-toolbar-actions">
                      {(isIcsFeedActive ? icsFeedForm.feed_url : activeUtilityHref) && !activeDescriptor.utility.disabled ? (
                        <a
                          href={isIcsFeedActive ? icsFeedForm.feed_url : activeUtilityHref || "#"}
                          className="button"
                          target="_blank"
                          rel="noreferrer"
                        >
                          <IconLabel icon={isIcsFeedActive ? "eye" : "download"} label={activeDescriptor.utility.actionLabel} />
                        </a>
                      ) : (
                        <button
                          type="button"
                          className="button"
                          disabled={activeDescriptor.utility.disabled || (isIcsFeedActive ? !icsFeedForm.feed_url : !activeUtilityHref)}
                        >
                          <IconLabel icon={isIcsFeedActive ? "eye" : "download"} label={activeDescriptor.utility.actionLabel} />
                        </button>
                      )}
                      {activeDescriptor.utility.hint ? <span className="muted-inline">{activeDescriptor.utility.hint}</span> : null}
                    </div>
                  ) : null}
                </section>
              ) : null}

              {activeDescriptor?.tutorial ? (
                <section className="connector-settings-card">
                  <div className="connector-settings-head">
                    <div>
                      <p className="section-kicker">Tutoriel</p>
                      <h3>{activeDescriptor.tutorial.title}</h3>
                    </div>
                    <Icon name="eye" />
                  </div>

                  <ol className="connector-settings-tutorial-list">
                    {activeDescriptor.tutorial.steps.map((step, index) => (
                      <li key={step} className="connector-settings-tutorial-step">
                        <span className="connector-settings-step-index">{String(index + 1).padStart(2, "0")}</span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </section>
              ) : null}
            </div> : null}
          </div>
        ) : (
          <p className="status-line">Aucune connexion disponible dans le catalogue.</p>
        )}
      </section>

      <MediaLibraryModal
        opened={isPublicLogoModalOpen}
        title="Choisir un logo client"
        subtitle="Sélectionnez un visuel existant ou importez-le depuis votre ordinateur."
        confirmLabel="Utiliser comme logo"
        usage={{
          source_app: "event-planner",
          usage_context: "public-page-logo",
          usage_label: publicPageForm.display.brand_name.trim() || "Logo page publique",
        }}
        onClose={() => setIsPublicLogoModalOpen(false)}
        onConfirm={(item: MediaLibraryItem) => {
          updatePublicPageDisplayField("logo_url", item.public_url);
        }}
      />
    </div>
  );
}

function EventEditorPage({ mode }: { mode: "create" | "detail" }) {
  const navigate = useNavigate();
  const routes = useEventPlannerRoutes();
  const location = useLocation();
  const params = useParams();
  const queryClient = useQueryClient();
  const { metaQuery, events, eventsQuery } = usePlannerData();
  const [eventForm, setEventForm] = useState<EventFormState>(createEmptyEventForm());
  const [formFieldErrors, setFormFieldErrors] = useState<string[]>([]);
  const [formStatus, setFormStatus] = useState<StatusState>({ message: "", tone: "neutral" });
  const [connectorStatus, setConnectorStatus] = useState<StatusState>({ message: "", tone: "neutral" });
  const [activeSyncConnectorKey, setActiveSyncConnectorKey] = useState<string | null>(null);
  const [isDuplicatingEvent, setIsDuplicatingEvent] = useState(false);
  const [isRecurrenceModalOpen, setIsRecurrenceModalOpen] = useState(false);
  const [recurrenceEditorState, setRecurrenceEditorState] = useState<RecurrenceEditorState>(() =>
    buildRecurrenceEditorState({ segments: [], overrides: [] }, createEmptyEventForm())
  );
  const [pendingOccurrenceSave, setPendingOccurrenceSave] = useState<PendingOccurrenceSaveState | null>(null);
  const formFeedbackRef = useRef<HTMLDivElement | null>(null);
  const pendingSaveIntentRef = useRef<"save" | "publish" | "facebook" | "occurrence" | "future">("save");
  const pendingOccurrenceRedirectRef = useRef<string | null>(null);
  const eventId = Number.parseInt(params.eventId || "", 10);
  const editorViewStateStorageKey =
    mode === "create" ? "easy-event:editor:create" : Number.isFinite(eventId) ? `easy-event:editor:${eventId}` : "easy-event:editor:detail";
  const readEditorViewState = () =>
    readSessionJson<EventEditorViewState>(editorViewStateStorageKey, {
      isEditingDetail: mode === "create",
      isMediaModalOpen: false,
    });
  const [isEditingDetail, setIsEditingDetail] = useState(() => readEditorViewState().isEditingDetail);
  const [isMediaModalOpen, setIsMediaModalOpen] = useState(() => readEditorViewState().isMediaModalOpen);
  const [facebookLinkDraft, setFacebookLinkDraft] = useState("");
  const [isTitleFieldFocused, setIsTitleFieldFocused] = useState(false);
  const [isLocationNameFocused, setIsLocationNameFocused] = useState(false);
  const titleAutocompleteRef = useRef<HTMLDivElement | null>(null);
  const hydratedRef = useRef("");
  const event = mode === "detail" ? events.find((item) => item.id === eventId) || null : null;
  const occurrenceQuery = useMemo(() => {
    const searchParams = new URLSearchParams(location.search || "");
    return {
      occurrenceDate: String(searchParams.get("occurrence") || "").trim(),
      sourceDate: String(searchParams.get("source") || "").trim(),
    };
  }, [location.search]);
  const occurrenceContext = useMemo(
    () =>
      RECURRING_EVENTS_V1_ENABLED && event && hasEventRecurrence(event.recurrence) && occurrenceQuery.occurrenceDate
        ? resolveOccurrenceContext(event, occurrenceQuery.occurrenceDate, occurrenceQuery.sourceDate)
        : null,
    [event, occurrenceQuery.occurrenceDate, occurrenceQuery.sourceDate]
  );
  const isDetailView = mode === "detail" && Boolean(event);
  const isEditingForm = mode === "create" || isEditingDetail;
  const isOccurrenceDetail = Boolean(occurrenceContext);
  const isOccurrenceEditMode = isDetailView && isEditingForm && Boolean(occurrenceContext);
  const illustrationPreviewUrl = eventForm.illustration_kind === "external_url" ? eventForm.illustration_reference.trim() : "";
  const hasIllustrationDetails = Boolean(
    event &&
      (event.illustration.kind !== "none" ||
        event.illustration.reference.trim() ||
        event.illustration.alt_text.trim())
  );
  const caldavConfigQuery = useQuery({
    queryKey: ["event-planner-caldav-config"],
    queryFn: () => fetchJson<CalDAVSettingsRecord>(apiUrl("connectors/caldav/config")),
    retry: false,
  });
  const isCalDAVConfigured = Boolean(caldavConfigQuery.data?.configured);
  const configuredKeys = useMemo(() => {
    const keys = configuredConnectorKeys(metaQuery.data);
    if (isCalDAVConfigured) {
      keys.add("caldav");
    }
    return keys;
  }, [isCalDAVConfigured, metaQuery.data]);
  const visibleConnectors = useMemo(
    () => {
      if (!event) {
        return [];
      }
      const connectors = [...visibleEventConnectors(event, configuredKeys)];
      if (isCalDAVConfigured && !connectors.some((connector) => connector.connector_key === "caldav")) {
        connectors.push({
          connector_key: "caldav",
          label: "Calendrier CalDAV",
          status: event.publication_status === "draft" ? "disabled" : "pending",
          status_revision: null,
          is_current: null,
          external_id: "",
          external_url: "",
          last_error: "",
          last_synced_at: null,
          created_at: null,
          updated_at: null,
        });
      }
      return connectors;
    },
    [configuredKeys, event, isCalDAVConfigured]
  );
  const currentDayMarker = useMemo(() => todayIso(), []);
  const normalizedRecurrence = useMemo(() => normalizeEventRecurrence(eventForm.recurrence), [eventForm.recurrence]);
  const recurrenceQuickPreset = useMemo(
    () => detectQuickRecurrencePreset(eventForm.recurrence, eventForm.date),
    [eventForm.date, eventForm.recurrence]
  );
  const canEditInlineRecurrenceEnding = useMemo(
    () => normalizedRecurrence.segments.length === 1 && normalizedRecurrence.overrides.length === 0,
    [normalizedRecurrence]
  );
  const inlineRecurrenceEditorState = useMemo(
    () => buildRecurrenceEditorState(eventForm.recurrence, eventForm),
    [eventForm]
  );
  const recurrenceSummary = useMemo(() => formatRecurrenceSummary(eventForm.recurrence), [eventForm.recurrence]);
  const recurrenceDetail = useMemo(() => formatRecurrenceDetail(eventForm.recurrence), [eventForm.recurrence]);
  const persistedListViewState = useMemo(() => {
    const storedViewState = readSessionJson<EventListViewState>(EVENT_LIST_VIEW_STATE_STORAGE_KEY, {
        catalogView: "table",
        filters: createDefaultEventFilters(),
        isFiltersPanelOpen: false,
        isImportModalOpen: false,
        isBulkEditModalOpen: false,
        isNewsletterModalOpen: false,
        newsletterPeriod: { startDate: "", endDate: "" },
        selectedEventIds: [],
      });
    return {
      ...storedViewState,
      filters: normalizeEventFilters(storedViewState.filters),
    };
  }, []);
  const visibleConnectorsByEventId = useMemo(
    () =>
      new Map(
        events.map((item) => {
          const connectors = [...visibleEventConnectors(item, configuredKeys)];
          if (isCalDAVConfigured && !connectors.some((connector) => connector.connector_key === "caldav")) {
            connectors.push({
              connector_key: "caldav",
              label: "Calendrier CalDAV",
              status: item.publication_status === "draft" ? "disabled" : "pending",
              status_revision: null,
              is_current: null,
              external_id: "",
              external_url: "",
              last_error: "",
              last_synced_at: null,
              created_at: null,
              updated_at: null,
            });
          }
          return [item.id, connectors];
        })
      ),
    [configuredKeys, events, isCalDAVConfigured]
  );
  const filteredChronologyEvents = useMemo(
    () =>
      events.filter((item) =>
        eventMatchesFilters(item, persistedListViewState.filters, currentDayMarker, visibleConnectorsByEventId.get(item.id) || [])
      ),
    [currentDayMarker, events, persistedListViewState.filters, visibleConnectorsByEventId]
  );
  const chronologyEvents = useMemo(() => {
    if (!event) {
      return events;
    }
    const filteredContainsCurrentEvent = filteredChronologyEvents.some((item) => item.id === event.id);
    return filteredContainsCurrentEvent && filteredChronologyEvents.length > 0 ? filteredChronologyEvents : events;
  }, [event, events, filteredChronologyEvents]);
  const hasIllustrationPreview =
    event?.illustration.kind === "external_url" && Boolean(event.illustration.reference.trim());
  const publicationMessage =
    eventForm.publication_status === "draft"
      ? "Le brouillon reste visible uniquement dans Évènements et garde les connecteurs inactifs."
      : "L’évènement est publié et peut être diffusé sur les connecteurs actifs.";
  const recurringPublishedMessage =
    RECURRING_EVENTS_V1_ENABLED && event?.publication_status === "published" && hasEventRecurrence(event?.recurrence)
      ? "La diffusion connecteur des series recurrentes sera branchee dans un second temps."
      : "";
  const deferredDisplayName = useDeferredValue(eventForm.display_name.trim());
  const titleTemplateSuggestions = useMemo(() => {
    const normalizedQuery = normalizeInternalName(deferredDisplayName);
    if (mode !== "create" || normalizedQuery.length < 3) {
      return [];
    }

    return events.filter((item) => eventTitleTemplateMatches(item, normalizedQuery)).slice(0, 3);
  }, [deferredDisplayName, events, mode]);
  const showTitleTemplateSuggestions = isTitleFieldFocused && titleTemplateSuggestions.length > 0;
  const deferredLocationName = useDeferredValue(eventForm.location_name.trim());
  const locationSuggestionsQuery = useQuery({
    queryKey: ["event-location-suggestions", deferredLocationName],
    queryFn: () =>
      fetchJson<{ suggestions: SavedLocationSuggestion[] }>(
        apiUrl(`locations/suggest?q=${encodeURIComponent(deferredLocationName)}`)
      ),
    enabled: isEditingForm && deferredLocationName.length > 0,
    retry: false,
  });
  const locationSuggestions = locationSuggestionsQuery.data?.suggestions || [];
  const showLocationSuggestions =
    isLocationNameFocused &&
    deferredLocationName.length > 0 &&
    locationSuggestions.length > 0 &&
    locationSuggestions.some(
      (suggestion) =>
        suggestion.location_name.trim().toLowerCase() !== eventForm.location_name.trim().toLowerCase() ||
        composeEventAddress(suggestion).trim().toLowerCase() !== composeEventAddress(eventForm).trim().toLowerCase()
    );
  const recurrencePreviewEvent = useMemo<EventRecord>(
    () => ({
      id: event?.id || 0,
      internal_name: eventForm.internal_name || "recurrence-preview",
      display_name: eventForm.display_name || "Aperçu récurrence",
      date: eventForm.date,
      start_time: eventForm.start_time,
      end_time: eventForm.end_time,
      publication_status: eventForm.publication_status,
      timezone: eventForm.timezone,
      location_name: eventForm.location_name,
      location_address: eventForm.location_address,
      location_street_number: eventForm.location_street_number,
      location_street: eventForm.location_street,
      location_postal_code: eventForm.location_postal_code,
      location_city: eventForm.location_city,
      recurrence: buildRecurrenceFromEditorState(recurrenceEditorState, eventForm),
      description: eventForm.description,
      practical_info: eventForm.practical_info,
      facebook_url: eventForm.facebook_url,
      revision: event?.revision || 1,
      price: {
        label: eventForm.price_label,
        amount_cents: null,
        currency: eventForm.price_currency || "EUR",
      },
      illustration: {
        kind: eventForm.illustration_kind,
        reference: eventForm.illustration_reference,
        alt_text: eventForm.illustration_alt_text,
      },
      connectors: event?.connectors || [],
      created_at: event?.created_at || null,
      updated_at: event?.updated_at || null,
    }),
    [event, eventForm, recurrenceEditorState]
  );
  const recurrencePreviewOccurrences = useMemo(
    () =>
      RECURRING_EVENTS_V1_ENABLED && eventForm.date
        ? listUpcomingOccurrences(recurrencePreviewEvent, eventForm.date, 5)
        : [],
    [eventForm.date, recurrencePreviewEvent]
  );
  const displayedUpcomingOccurrences = useMemo(
    () =>
      RECURRING_EVENTS_V1_ENABLED && event
        ? listUpcomingOccurrences(event, occurrenceContext?.date || (event.date >= currentDayMarker ? event.date : currentDayMarker), 6)
        : [],
    [currentDayMarker, event, occurrenceContext?.date]
  );

  useEffect(() => {
    if (mode === "create") {
      document.title = "Relyo · Nouvel évènement";
      return;
    }
    document.title = event?.display_name ? `${event.display_name} · Relyo` : "Relyo · Détail évènement";
  }, [event?.display_name, mode]);

  useEffect(() => {
    if (mode === "create") {
      if (hydratedRef.current === editorViewStateStorageKey) {
        return;
      }

      hydratedRef.current = editorViewStateStorageKey;
      const persistedViewState = readEditorViewState();
      setIsEditingDetail(persistedViewState.isEditingDetail);
      setIsMediaModalOpen(persistedViewState.isMediaModalOpen);
      setEventForm(createEmptyEventForm());
      setFacebookLinkDraft("");
      setFormFieldErrors([]);
      setFormStatus({ message: "", tone: "neutral" });
      setConnectorStatus({ message: "", tone: "neutral" });
      return;
    }

    if (event && hydratedRef.current !== editorViewStateStorageKey) {
      hydratedRef.current = editorViewStateStorageKey;
      const persistedViewState = readEditorViewState();
      setIsEditingDetail(persistedViewState.isEditingDetail);
      setIsMediaModalOpen(persistedViewState.isMediaModalOpen);
      setEventForm(toEventForm(event));
      setFacebookLinkDraft(String(event.facebook_url || ""));
      setFormFieldErrors([]);
      setFormStatus({ message: "", tone: "neutral" });
      setConnectorStatus({ message: "", tone: "neutral" });
    }
  }, [editorViewStateStorageKey, event, mode]);

  useEffect(() => {
    writeSessionJson(editorViewStateStorageKey, {
      isEditingDetail,
      isMediaModalOpen,
    } satisfies EventEditorViewState);
  }, [editorViewStateStorageKey, isEditingDetail, isMediaModalOpen]);

  useEffect(() => {
    const flash = (location.state as FlashState | null)?.flash;
    if (!flash) {
      return;
    }

    setFormStatus(flash);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.pathname, location.search, location.state, navigate]);

  const saveFormMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      fetchJson<EventRecord>(apiUrl("events"), {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: (savedEvent) => {
      queryClient.setQueryData<EventRecord[]>(["event-planner-events"], (current = []) => upsertEvent(current, savedEvent));
      setFormFieldErrors([]);

      if (mode === "create") {
        pendingSaveIntentRef.current = "save";
        startTransition(() =>
          navigate(routes.eventDetailPath(savedEvent.id), {
            replace: true,
            state: { flash: { message: "Brouillon créé avec succès. Vous pouvez maintenant le publier.", tone: "success" } satisfies StatusState },
          })
        );
        return;
      }

      if (pendingOccurrenceRedirectRef.current) {
        const nextPath = pendingOccurrenceRedirectRef.current;
        const nextMessage =
          pendingSaveIntentRef.current === "future"
            ? "Serie recurrente mise a jour."
            : "Occurrence mise a jour.";
        setEventForm(toEventForm(savedEvent));
        setIsEditingDetail(false);
        setPendingOccurrenceSave(null);
        pendingOccurrenceRedirectRef.current = null;
        pendingSaveIntentRef.current = "save";
        startTransition(() =>
          navigate(nextPath, {
            replace: true,
            state: { flash: { message: nextMessage, tone: "success" } satisfies StatusState },
          })
        );
        return;
      }

      setEventForm(toEventForm(savedEvent));
      setIsEditingDetail(false);
      setFormStatus({
        message:
          pendingSaveIntentRef.current === "publish"
            ? "Évènement publié avec succès."
            : pendingSaveIntentRef.current === "facebook"
              ? "Lien Facebook mis à jour."
            : "Évènement mis à jour avec succès.",
        tone: "success",
      });
      pendingSaveIntentRef.current = "save";
    },
    onError: (error) => {
      const normalizedError = normalizeApiError(error);
      setFormFieldErrors(resolveFieldErrors(normalizedError, inferEventFieldErrors(normalizedError.message)));
      setFormStatus({ message: normalizedError.message, tone: "error" });
      pendingOccurrenceRedirectRef.current = null;
      pendingSaveIntentRef.current = "save";
      scrollFormFeedbackIntoView();
    },
  });

  const syncConnectorMutation = useMutation({
    mutationFn: (connectorKey: string) =>
      fetchJson<{ connector_key: string; resource_url?: string; warnings?: string[] }>(apiUrl(`events/${eventId}/connectors/${connectorKey}/sync`), {
        method: "POST",
      }),
    onMutate: (connectorKey) => {
      setActiveSyncConnectorKey(connectorKey);
      setConnectorStatus({
        message: connectorKey === "odoo" ? "Synchronisation Odoo en cours..." : "Synchronisation en cours...",
        tone: "neutral",
      });
    },
    onSuccess: async (result, connectorKey) => {
      await queryClient.invalidateQueries({ queryKey: ["event-planner-events"] });
      const connectorLabel = connectorKey === "odoo" ? "Odoo" : connectorKey === "caldav" ? "CalDAV" : result.connector_key;
      const baseMessage = result.resource_url
        ? `Synchronisation ${connectorLabel} terminee. Destination disponible.`
        : `Synchronisation ${connectorLabel} terminee.`;
      const firstWarning = Array.isArray(result.warnings) ? result.warnings.find((warning) => String(warning).trim()) : "";
      const warningSuffix = firstWarning ? ` ${String(firstWarning).trim()}` : "";
      setConnectorStatus({
        message: `${baseMessage}${warningSuffix}`,
        tone: "success",
      });
    },
    onError: (error) => {
      const normalizedError = normalizeApiError(error);
      setConnectorStatus({ message: normalizedError.message, tone: "error" });
    },
    onSettled: () => {
      setActiveSyncConnectorKey(null);
    },
  });

  const duplicateEventMutation = useMutation({
    mutationFn: (sourceEventId: number) =>
      fetchJson<EventRecord>(apiUrl(`events/${sourceEventId}/duplicate`), {
        method: "POST",
      }),
    onMutate: () => {
      setIsDuplicatingEvent(true);
      setFormStatus({ message: "Duplication du brouillon en cours...", tone: "neutral" });
    },
    onSuccess: (duplicatedEvent) => {
      queryClient.setQueryData<EventRecord[]>(["event-planner-events"], (current = []) => upsertEvent(current, duplicatedEvent));
      startTransition(() =>
        navigate(routes.eventDetailPath(duplicatedEvent.id), {
          state: {
            flash: {
              message: "Brouillon duplique avec succes. Completez la date avant publication.",
              tone: "success",
            } satisfies StatusState,
          },
        })
      );
    },
    onError: (error) => {
      const normalizedError = normalizeApiError(error);
      setFormStatus({ message: normalizedError.message, tone: "error" });
    },
    onSettled: () => {
      setIsDuplicatingEvent(false);
    },
  });

  const deleteEventMutation = useMutation({
    mutationFn: async (targetEvent: EventRecord) =>
      fetchJson<{ deleted_count: number }>(apiUrl("events/delete-bulk"), {
        method: "POST",
        body: JSON.stringify({ event_ids: [targetEvent.id] }),
      }),
    onMutate: () => {
      setFormStatus({ message: "Suppression de l’évènement en cours...", tone: "neutral" });
    },
    onSuccess: async ({ deleted_count }, deletedEvent) => {
      await queryClient.invalidateQueries({ queryKey: ["event-planner-events"] });
      queryClient.setQueryData<EventRecord[]>(["event-planner-events"], (current = []) =>
        current.filter((item) => item.id !== deletedEvent.id)
      );
      startTransition(() =>
        navigate(routes.listPath, {
          replace: true,
          state: {
            flash: {
              message: `${deleted_count} ${pluralize(deleted_count, "évènement")} supprimé${deleted_count > 1 ? "s" : ""}.`,
              tone: "success",
            } satisfies StatusState,
          },
        })
      );
    },
    onError: (error) => {
      const normalizedError = normalizeApiError(error);
      setFormStatus({ message: normalizedError.message, tone: "error" });
    },
  });

  function handleDeleteEvent() {
    if (!event) {
      return;
    }

    const eventName = event.display_name || event.internal_name || `#${event.id}`;
    const confirmed = window.confirm(`Supprimer l’évènement "${eventName}" ? Cette action est définitive.`);
    if (!confirmed) {
      return;
    }

    deleteEventMutation.mutate(event);
  }

  function updateEventFormField<Key extends keyof EventFormState>(field: Key, value: EventFormState[Key]) {
    setEventForm((current) => {
      const nextState = { ...current, [field]: value };
      if (
        field === "location_street_number" ||
        field === "location_street" ||
        field === "location_postal_code" ||
        field === "location_city"
      ) {
        nextState.location_address = composeEventAddress(nextState);
      }
      if (
        field === "date" ||
        field === "start_time" ||
        field === "end_time" ||
        field === "timezone"
      ) {
        nextState.recurrence = syncRecurrenceWithSeriesForm(nextState.recurrence, nextState);
      }
      return nextState;
    });
    if (field === "display_name" || field === "internal_name") {
      setFormFieldErrors((current) => current.filter((item) => item !== "display_name" && item !== "internal_name"));
    } else {
      setFormFieldErrors((current) => current.filter((item) => item !== field));
    }
  }

  function scrollFormFeedbackIntoView() {
    requestAnimationFrame(() => {
      formFeedbackRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }

  function normalizeEventTimeField(field: "start_time" | "end_time") {
    setEventForm((current) => ({ ...current, [field]: normalizeImportedTime(current[field]) }));
  }

  function handlePublishEvent() {
    const publishForm = { ...eventForm, publication_status: "published" as const };
    const validationError = validateEventForm(publishForm);
    if (validationError) {
      setEventForm(publishForm);
      setFormFieldErrors(validationError.fieldErrors);
      setFormStatus({
        message: "Complétez les informations obligatoires avant de publier l’évènement.",
        tone: "error",
      });
      setIsEditingDetail(true);
      scrollFormFeedbackIntoView();
      return;
    }

    pendingSaveIntentRef.current = "publish";
    setFormFieldErrors([]);
    setFormStatus({ message: "Publication en cours...", tone: "neutral" });
    saveFormMutation.mutate(buildEventPayload(publishForm));
  }

  function handleSaveFacebookLink() {
    pendingSaveIntentRef.current = "facebook";
    setFormStatus({ message: "Enregistrement du lien Facebook...", tone: "neutral" });
    saveFormMutation.mutate(
      buildEventPayload({
        ...eventForm,
        facebook_url: facebookLinkDraft.trim(),
      })
    );
  }

  function applySavedLocationSuggestion(suggestion: SavedLocationSuggestion) {
    updateEventFormField("location_name", suggestion.location_name);
    updateEventFormField("location_street_number", suggestion.location_street_number);
    updateEventFormField("location_street", suggestion.location_street);
    updateEventFormField("location_postal_code", suggestion.location_postal_code);
    updateEventFormField("location_city", suggestion.location_city);
    updateEventFormField("location_address", composeEventAddress(suggestion));
    setIsLocationNameFocused(false);
  }

  function applyEventTemplateSuggestion(template: EventRecord) {
    setEventForm((current) => {
      const preservedDate = current.date;
      const nextState = {
        ...toEventForm(template),
        id: null,
        internal_name: "",
        date: preservedDate,
        publication_status: "draft" as const,
        facebook_url: "",
        recurrence: { segments: [], overrides: [] },
      };
      return {
        ...nextState,
        location_address: composeEventAddress(nextState),
      };
    });
    setFormFieldErrors([]);
    setFormStatus({
      message: "Modèle repris. Ajustez la date puis créez le brouillon.",
      tone: "success",
    });
    setIsTitleFieldFocused(false);
  }

  function handleTitleAutocompleteBlur(event: FocusEvent<HTMLDivElement>) {
    const nextFocusTarget = event.relatedTarget;
    if (nextFocusTarget instanceof Node && titleAutocompleteRef.current?.contains(nextFocusTarget)) {
      return;
    }
    setIsTitleFieldFocused(false);
  }

  function openRecurrenceModal() {
    setRecurrenceEditorState(buildRecurrenceEditorState(eventForm.recurrence, eventForm));
    setIsRecurrenceModalOpen(true);
  }

  function updateInlineRecurrenceState(updater: (current: RecurrenceEditorState) => RecurrenceEditorState) {
    setEventForm((current) => ({
      ...current,
      recurrence: buildRecurrenceFromEditorState(updater(buildRecurrenceEditorState(current.recurrence, current)), current),
    }));
    setFormFieldErrors((current) => current.filter((item) => item !== "recurrence"));
  }

  function applyQuickRecurrencePreset(nextPreset: RecurrencePresetKey) {
    if (nextPreset === "none") {
      updateEventFormField("recurrence", { segments: [], overrides: [] });
      return;
    }
    if (nextPreset === "custom") {
      openRecurrenceModal();
      return;
    }
    const nextState = buildRecurrenceEditorState(createPresetRecurrence(nextPreset, eventForm), eventForm);
    const preservedEndState = buildRecurrenceEditorState(eventForm.recurrence, eventForm);
    updateEventFormField(
      "recurrence",
      buildRecurrenceFromEditorState(
        {
          ...nextState,
          end_mode: preservedEndState.end_mode,
          until_date: preservedEndState.until_date,
          count: preservedEndState.count,
        },
        eventForm
      )
    );
  }

  function openDetailEdition() {
    if (event && occurrenceContext) {
      setEventForm(buildOccurrenceEditForm(event, occurrenceContext));
    } else if (event) {
      setEventForm(toEventForm(event));
    }
    setFormFieldErrors([]);
    setFormStatus({ message: "", tone: "neutral" });
    setIsEditingDetail(true);
  }

  function confirmOccurrenceScope(scope: "occurrence" | "future") {
    if (!event || !pendingOccurrenceSave) {
      return;
    }

    const baseForm = toEventForm(event);
    if (scope === "occurrence") {
      const nextState = applyOccurrenceOverrideToEvent(event, pendingOccurrenceSave.occurrence, pendingOccurrenceSave.nextForm);
      pendingSaveIntentRef.current = "occurrence";
      pendingOccurrenceRedirectRef.current = routes.eventDetailPath(
        event.id,
        `occurrence=${encodeURIComponent(pendingOccurrenceSave.nextForm.date)}&source=${encodeURIComponent(
          pendingOccurrenceSave.occurrence.source_date
        )}`
      );
      setPendingOccurrenceSave(null);
      setFormStatus({ message: "Mise a jour de l'occurrence...", tone: "neutral" });
      saveFormMutation.mutate(
        buildEventPayload({
          ...baseForm,
          recurrence: nextState.recurrence,
        })
      );
      return;
    }

    const nextState = applyFutureSplitToEvent(event, pendingOccurrenceSave.occurrence, pendingOccurrenceSave.nextForm);
    pendingSaveIntentRef.current = "future";
    pendingOccurrenceRedirectRef.current = routes.eventDetailPath(
      event.id,
      `occurrence=${encodeURIComponent(nextState.date)}&source=${encodeURIComponent(nextState.date)}`
    );
    setPendingOccurrenceSave(null);
    setFormStatus({ message: "Mise a jour des occurrences futures...", tone: "neutral" });
    saveFormMutation.mutate(
      buildEventPayload({
        ...baseForm,
        date: nextState.date,
        start_time: nextState.start_time,
        end_time: nextState.end_time,
        timezone: nextState.timezone,
        recurrence: nextState.recurrence,
      })
    );
  }

  if (mode === "detail" && !eventsQuery.isLoading && !event) {
    return (
      <div className="event-editor-shell">
        <AppToolbar
          leading={<ReturnToCatalogLink />}
          actions={routes.newsletterPath ? null : (
            <Link to={routes.settingsPath} className="icon-button" aria-label="Paramètres" title="Paramètres">
              <Icon name="settings" />
            </Link>
          )}
        />
        <section className="panel panel-primary event-not-found">
          <p className="section-kicker">Detail</p>
          <h2>Évènement introuvable</h2>
          <p className="muted">L’identifiant demandé ne correspond à aucun évènement chargé par l’application.</p>
          <ReturnToCatalogLink />
        </section>
      </div>
    );
  }

  return (
    <EditorPage className="event-editor-shell"
        back={<ReturnToCatalogLink />}
        heading={isEditingForm ? (<EditorHeader className="event-edit-header"
                kicker={mode === "create" ? "Creation" : isOccurrenceEditMode ? "Occurrence" : "Edition"}
                title={
                    mode === "create"
                      ? "Créer l’évènement"
                      : isOccurrenceEditMode && occurrenceContext
                        ? `Modifier l'occurrence du ${formatEventDate(occurrenceContext.date)}`
                        : "Modifier l’évènement"
                }
                actions={<>
                  <button
                    type="button"
                    className="button"
                    onClick={() => {
                      if (mode === "detail" && event) {
                        setEventForm(toEventForm(event));
                        setFormFieldErrors([]);
                        setFormStatus({ message: "", tone: "neutral" });
                      setPendingOccurrenceSave(null);
                      setIsEditingDetail(false);
                      return;
                    }

                      startTransition(() => navigate(routes.listPath));
                    }}
                  >
                    Annuler
                  </button>
                  <button form="event-edit-form" type="submit" className="button button-primary" disabled={saveFormMutation.isPending}>
                    {saveFormMutation.isPending
                      ? "Enregistrement..."
                      : mode === "create"
                        ? "Créer le brouillon"
                        : "Mettre a jour"}
                  </button>
                </>}
              />) : (<EditorHeader
          title={<span className="event-heading-title">{event?.display_name || "Fiche évènement"}<span className={`pill event-publication-pill event-publication-pill-${event?.publication_status || "draft"}`}>{publicationStatusLabel(event?.publication_status || "draft")}</span></span>}
          actions={<div className="event-toolbar-actions">

                    <button
                      type="button"
                      className="button"
                      disabled={isDuplicatingEvent}
                      onClick={() => {
                        if (event?.id) {
                          duplicateEventMutation.mutate(event.id);
                        }
                      }}
                    >
                      <IconLabel icon="copy" label={isDuplicatingEvent ? "Duplication..." : "Dupliquer"} />
                    </button>
                    <button type="button" className="button button-primary" onClick={openDetailEdition}>
                      <IconLabel icon="edit" label="Modifier" />
                    </button>
<Menu position="bottom-end" withinPortal>
                      <Menu.Target><button type="button" className="icon-button" aria-label="Autres actions de l’évènement">⋯</button></Menu.Target>
                      <Menu.Dropdown><Menu.Item color="red" disabled={deleteEventMutation.isPending} onClick={handleDeleteEvent}>Supprimer</Menu.Item></Menu.Dropdown>
                    </Menu>
                  </div>}
        />)}
        actions={isDetailView && event && !isEditingForm ? (<EventChronologyNavigator
          events={chronologyEvents}
          currentEvent={event}
          onNavigate={(nextEventId) => startTransition(() => navigate(routes.eventDetailPath(nextEventId)))}
        />) : routes.newsletterPath ? null : (
            <Link to={routes.settingsPath} className="icon-button" aria-label="Paramètres" title="Paramètres">
            <Icon name="settings" />
          </Link>
        )}
      >


      <main
        className={`content-grid event-editor-layout${isDetailView ? " is-detail-view" : " is-form-only"}${
          isEditingForm ? " is-editing-view" : ""
        }`}
      >
        <div className="side-stack event-main-column">
          <EditorPanel variant="layout">
            {isEditingForm ? (
              <form
                id="event-edit-form"
                className="event-form event-edit-form"
                onSubmit={(submitEvent) => {
                  submitEvent.preventDefault();
                  const nextForm =
                    mode === "create"
                      ? { ...eventForm, publication_status: "draft" as const }
                      : eventForm;
                  const validationError = validateEventForm(nextForm);
                  if (validationError) {
                    setFormFieldErrors(validationError.fieldErrors);
                    setFormStatus({ message: validationError.message, tone: "error" });
                    scrollFormFeedbackIntoView();
                    return;
                  }

                  if (isOccurrenceEditMode && occurrenceContext) {
                    if (!hasOccurrenceScheduleChange(occurrenceContext, nextForm)) {
                      setIsEditingDetail(false);
                      return;
                    }

                    setFormFieldErrors([]);
                    setPendingOccurrenceSave({ nextForm, occurrence: occurrenceContext });
                    setFormStatus({ message: "", tone: "neutral" });
                    return;
                  }

                  setFormFieldErrors([]);
                  setFormStatus({ message: "", tone: "neutral" });
                  pendingSaveIntentRef.current = "save";
                  saveFormMutation.mutate(buildEventPayload(nextForm));
                }}
              >


              <div ref={formFeedbackRef} className="event-form-feedback" aria-live="polite">
                {formStatus.message ? <p className={statusClassName(formStatus)}>{formStatus.message}</p> : null}
              </div>

              {isOccurrenceEditMode && occurrenceContext ? (
                <section className="event-form-section event-edit-block occurrence-edit-summary">
                  <h3 className="event-form-section-title">Serie</h3>
                  <div className="occurrence-edit-summary-card">
                    <strong>{event?.display_name || "Évènement récurrent"}</strong>
                    <span>{recurrenceSummary}</span>
                    <span>Les autres informations de la serie restent inchangées depuis cette vue.</span>
                  </div>
                </section>
              ) : (
                <section className="event-form-section event-edit-block">
                  <h3 className="event-form-section-title">Titre</h3>
                  <div className="grid grid-1">
                    <div
                      ref={titleAutocompleteRef}
                      className="event-autocomplete-field"
                      onFocus={() => setIsTitleFieldFocused(true)}
                      onBlur={handleTitleAutocompleteBlur}
                    >
                      <label>
                        Nom d'affichage
                        <input
                          value={eventForm.display_name}
                          className={hasFieldError(formFieldErrors, "display_name") ? "is-field-invalid" : ""}
                          onChange={(event) => updateEventFormField("display_name", event.target.value)}
                          placeholder="Salon du livre"
                        />
                      </label>
                      {showTitleTemplateSuggestions ? (
                        <div className="event-autocomplete-list" role="group" aria-label="Évènements existants">
                          {titleTemplateSuggestions.map((suggestion) => (
                            <button
                              key={suggestion.id}
                              type="button"
                              className="event-autocomplete-item"
                              onMouseDown={(mouseEvent) => {
                                mouseEvent.preventDefault();
                              }}
                              onClick={() => {
                                applyEventTemplateSuggestion(suggestion);
                              }}
                            >
                              <strong>Reprendre {suggestion.display_name}</strong>
                              <span>
                                {[formatEventDate(suggestion.date), suggestion.start_time].filter(Boolean).join(" - ") ||
                                  "Brouillon sans date"}
                              </span>
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </section>
              )}

              <section className="event-form-section event-edit-block">
                <h3 className="event-form-section-title">Date et heure</h3>
                <div className="grid grid-3">
                  <label>
                    Date
                    <input
                      type="date"
                      value={eventForm.date}
                      className={hasFieldError(formFieldErrors, "date") ? "is-field-invalid" : ""}
                      onChange={(event) => updateEventFormField("date", event.target.value)}
                    />
                  </label>
                  <label>
                    Debut
                    <input
                      type="text"
                      inputMode="numeric"
                      value={eventForm.start_time}
                      className={hasFieldError(formFieldErrors, "start_time") ? "is-field-invalid" : ""}
                      onChange={(event) => updateEventFormField("start_time", event.target.value)}
                      onBlur={() => normalizeEventTimeField("start_time")}
                      placeholder="12:00"
                    />
                  </label>
                  <label>
                    Fin
                    <input
                      type="text"
                      inputMode="numeric"
                      value={eventForm.end_time}
                      className={hasFieldError(formFieldErrors, "end_time") ? "is-field-invalid" : ""}
                      onChange={(event) => updateEventFormField("end_time", event.target.value)}
                      onBlur={() => normalizeEventTimeField("end_time")}
                      placeholder="18:00"
                    />
                  </label>
                  <label>
                    Fuseau horaire
                    <input
                      value={eventForm.timezone}
                      className={hasFieldError(formFieldErrors, "timezone") ? "is-field-invalid" : ""}
                      onChange={(event) => updateEventFormField("timezone", event.target.value)}
                      placeholder="Europe/Paris"
                    />
                  </label>
                </div>
                {!isOccurrenceEditMode && RECURRING_EVENTS_V1_ENABLED ? (
                  <div className="recurrence-form-panel">
                    <div className="recurrence-form-header">
                      <div>
                        <strong>Recurrence</strong>
                        <span>{recurrenceDetail || recurrenceSummary}</span>
                      </div>
                      <button type="button" className="button button-small" onClick={openRecurrenceModal}>
                        Personnaliser...
                      </button>
                    </div>
                    <div className="grid grid-3 recurrence-inline-grid">
                      <label>
                        Regle
                        <select
                          value={recurrenceQuickPreset}
                          className={hasFieldError(formFieldErrors, "recurrence") ? "is-field-invalid" : ""}
                          onChange={(event) => applyQuickRecurrencePreset(event.target.value as RecurrencePresetKey)}
                        >
                          <option value="none">Aucune</option>
                          <option value="daily">Tous les jours</option>
                          <option value="weekly">Toutes les semaines</option>
                          <option value="monthly">Tous les mois</option>
                          <option value="yearly">Tous les ans</option>
                          <option value="custom">Personnalisee...</option>
                        </select>
                      </label>
                      <div className="recurrence-inline-summary">
                        <strong>Etat</strong>
                        <span>{recurrenceSummary}</span>
                      </div>
                    </div>
                    {hasEventRecurrence(eventForm.recurrence) && canEditInlineRecurrenceEnding ? (
                      <div className="recurrence-end-inline-panel">
                        <strong>Fin de la recurrence</strong>
                        <div className="grid grid-3 recurrence-inline-grid">
                          <label>
                            Fin
                            <select
                              value={inlineRecurrenceEditorState.end_mode}
                              onChange={(event) =>
                                updateInlineRecurrenceState((current) => ({
                                  ...current,
                                  end_mode: event.target.value as RecurrenceEditorState["end_mode"],
                                }))
                              }
                            >
                              <option value="never">Jamais</option>
                              <option value="until">Le</option>
                              <option value="count">Apres</option>
                            </select>
                          </label>
                          {inlineRecurrenceEditorState.end_mode === "until" ? (
                            <label>
                              Date de fin
                              <input
                                type="date"
                                value={inlineRecurrenceEditorState.until_date}
                                onChange={(event) =>
                                  updateInlineRecurrenceState((current) => ({
                                    ...current,
                                    until_date: event.target.value,
                                  }))
                                }
                              />
                            </label>
                          ) : null}
                          {inlineRecurrenceEditorState.end_mode === "count" ? (
                            <label>
                              Occurrences
                              <input
                                type="number"
                                min={1}
                                max={500}
                                value={inlineRecurrenceEditorState.count}
                                onChange={(event) =>
                                  updateInlineRecurrenceState((current) => ({
                                    ...current,
                                    count: Number.parseInt(event.target.value || "10", 10) || 1,
                                  }))
                                }
                              />
                            </label>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : isOccurrenceEditMode ? (
                  <div className="occurrence-edit-note">
                    <strong>Portee</strong>
                    <span>
                      Au moment d'enregistrer, vous choisirez si le decalage concerne uniquement cette occurrence ou toute la
                      suite.
                    </span>
                  </div>
                ) : null}
              </section>

              {!isOccurrenceEditMode ? (
              <section className="event-form-section event-edit-block">
                <h3 className="event-form-section-title">Lieu</h3>
                <div className="grid grid-2">
                  <label className="event-autocomplete-field">
                    Nom du lieu
                    <input
                      value={eventForm.location_name}
                      className={hasFieldError(formFieldErrors, "location_name") ? "is-field-invalid" : ""}
                      onChange={(event) => updateEventFormField("location_name", event.target.value)}
                      onFocus={() => setIsLocationNameFocused(true)}
                      onBlur={() => window.setTimeout(() => setIsLocationNameFocused(false), 120)}
                      placeholder="Halle des expositions"
                    />
                    {showLocationSuggestions ? (
                      <div className="event-autocomplete-list" role="listbox" aria-label="Lieux existants">
                        {locationSuggestions.map((suggestion) => (
                          <button
                            key={`${suggestion.location_name}::${suggestion.location_address}`}
                            type="button"
                            className="event-autocomplete-item"
                            onMouseDown={(mouseEvent) => {
                              mouseEvent.preventDefault();
                              applySavedLocationSuggestion(suggestion);
                            }}
                          >
                            <strong>{suggestion.location_name}</strong>
                            <span>{composeEventAddress(suggestion) || "Adresse non renseignee"}</span>
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </label>
                  <label>
                    N°
                    <input
                      value={eventForm.location_street_number}
                      onChange={(event) => updateEventFormField("location_street_number", event.target.value)}
                      placeholder="12"
                    />
                  </label>
                </div>
                <div className="grid grid-2">
                  <label className="field-span-2">
                    Adresse
                    <input
                      value={eventForm.location_street}
                      className={hasFieldError(formFieldErrors, "location_address") ? "is-field-invalid" : ""}
                      onChange={(event) => updateEventFormField("location_street", event.target.value)}
                      placeholder="Route de Saint-Joseph"
                    />
                  </label>
                  <label>
                    Code postal
                    <input
                      value={eventForm.location_postal_code}
                      onChange={(event) => updateEventFormField("location_postal_code", event.target.value)}
                      placeholder="44000"
                    />
                  </label>
                  <label>
                    Ville
                    <input
                      value={eventForm.location_city}
                      onChange={(event) => updateEventFormField("location_city", event.target.value)}
                      placeholder="Nantes"
                    />
                  </label>
                </div>
              </section>
              ) : null}

              {!isOccurrenceEditMode ? (
              <section className="event-form-section event-edit-block">
                <h3 className="event-form-section-title">Informations</h3>
                <label>
                  Description
                  <textarea
                    rows={5}
                    value={eventForm.description}
                    onChange={(event) => updateEventFormField("description", event.target.value)}
                  />
                </label>
                <label>
                  Infos pratiques
                  <textarea
                    rows={4}
                    value={eventForm.practical_info}
                    onChange={(event) => updateEventFormField("practical_info", event.target.value)}
                  />
                </label>
              </section>
              ) : null}

              {!isOccurrenceEditMode ? (
              <section className="event-form-section event-edit-block">
                <h3 className="event-form-section-title">Prix</h3>
                <div className="grid grid-3">
                  <label>
                    Libellé prix
                    <input
                      value={eventForm.price_label}
                      className={hasFieldError(formFieldErrors, "price") ? "is-field-invalid" : ""}
                      onChange={(event) => updateEventFormField("price_label", event.target.value)}
                      placeholder="Plein tarif"
                    />
                  </label>
                  <label>
                    Montant
                    <input
                      value={eventForm.price_amount}
                      className={hasFieldError(formFieldErrors, "price") ? "is-field-invalid" : ""}
                      onChange={(event) => updateEventFormField("price_amount", event.target.value)}
                      placeholder="12.50"
                    />
                  </label>
                  <label>
                    Devise
                    <input
                      value={eventForm.price_currency}
                      className={hasFieldError(formFieldErrors, "price") ? "is-field-invalid" : ""}
                      onChange={(event) => updateEventFormField("price_currency", event.target.value.toUpperCase())}
                      placeholder="EUR"
                    />
                  </label>
                </div>
              </section>
              ) : null}

              {!isOccurrenceEditMode ? (
              <section className="event-form-section event-edit-block">
                <h3 className="event-form-section-title">Illustration</h3>
                <div className="event-summary-actions">
                  <button type="button" className="button" onClick={() => setIsMediaModalOpen(true)}>
                    Ajouter une image
                  </button>
                  {eventForm.illustration_reference.trim() ? (
                    <button
                      type="button"
                      className="button"
                      onClick={() => {
                        updateEventFormField("illustration_kind", "none");
                        updateEventFormField("illustration_reference", "");
                        updateEventFormField("illustration_alt_text", "");
                      }}
                    >
                      Retirer l'image
                    </button>
                  ) : null}
                </div>
                {illustrationPreviewUrl ? (
                  <div className="event-illustration-preview">
                    <img src={illustrationPreviewUrl} alt={eventForm.illustration_alt_text || eventForm.display_name || "Illustration évènement"} />
                  </div>
                ) : null}
                <label>
                  Texte alternatif
                  <input
                    value={eventForm.illustration_alt_text}
                    onChange={(event) => updateEventFormField("illustration_alt_text", event.target.value)}
                    placeholder="Visuel affiché évènement"
                  />
                </label>
              </section>
              ) : null}
              </form>
            ) : (
              <div className="event-readonly-stack event-detail-page">
                                    {RECURRING_EVENTS_V1_ENABLED && occurrenceContext ? (
                      <div className="event-occurrence-banner">
                        <strong>Occurrence du {formatEventDate(occurrenceContext.date)}</strong>
                        <span>
                          {[occurrenceContext.start_time || "Heure a definir", occurrenceContext.end_time || ""]
                            .filter(Boolean)
                            .join(" - ")}
                        </span>
                        <button
                          type="button"
                          className="button button-small"
                          onClick={() => startTransition(() => navigate(routes.eventDetailPath(event?.id || "")))}
                        >
                          Voir la serie
                        </button>
                      </div>
                    ) : null}
                {formStatus.message ? <p className={statusClassName(formStatus)}>{formStatus.message}</p> : null}

                <div className="event-detail-main">
                    <section className="event-detail-section event-detail-summary-section">

                      <div className="event-detail-summary-card">
                        <div className="event-detail-summary-visual">
                          {hasIllustrationPreview ? (
                            <img
                              src={event!.illustration.reference}
                              alt={event!.illustration.alt_text || event!.display_name || "Illustration évènement"}
                            />
                          ) : (
                            <span>Aucune image</span>
                          )}
                        </div>
                        <div className="event-detail-summary-body">
                          <div className="event-detail-inline-meta">
                            <div className="event-summary-fact"><span className="event-summary-fact-label"><Icon name="calendar" />Date</span><CopyableDetailValue value={occurrenceContext ? formatEventDate(occurrenceContext.date) : event ? formatEventDate(event.date) : ""} emptyFallback="Date à définir" /></div>
                            <div className="event-summary-fact"><span className="event-summary-fact-label"><Icon name="clock" />Horaires</span><CopyableDetailValue value={(occurrenceContext ? [occurrenceContext.start_time, occurrenceContext.end_time] : [event?.start_time, event?.end_time]).filter(Boolean).join(" – ")} emptyFallback="Heure à définir" /></div>
                            <div className="event-summary-fact"><span className="event-summary-fact-label"><Icon name="map-pin" />Lieu</span><CopyableDetailValue value={event?.location_name || ""} emptyFallback="Lieu à définir" /></div>
                            <div className="event-summary-fact"><span className="event-summary-fact-label">Tarif</span><CopyableDetailValue value={event ? formatEventPrice(event) : ""} /></div>
                          </div>
                        </div>
                        <div className="event-summary-presentation">
                          <h3>Présentation</h3>
                          <p><CopyableDetailValue value={event?.description || ""} emptyFallback="Ajoutez un résumé pour présenter rapidement l’évènement." /></p>
                          <div className="event-detail-summary-block"><strong>Informations pratiques</strong><CopyableDetailValue value={event?.practical_info || ""} emptyFallback="Ajoutez les consignes pratiques à afficher en tête de fiche." /></div>
                        </div>
                      </div>
                    </section>

                    <details className="event-detail-additional"><summary>Détails de l’évènement</summary>
                    <section className="event-detail-section">
                      <h3 className="event-detail-section-title">Date et heure</h3>
                      <div className="event-detail-info-grid event-detail-info-grid-compact">
                        <div className="event-detail-info-item">
                          <strong>Date</strong>
                          <CopyableDetailValue value={occurrenceContext ? formatEventDate(occurrenceContext.date) : event ? formatEventDate(event.date) : ""} />
                        </div>
                        <div className="event-detail-info-item">
                          <strong>Debut</strong>
                          <CopyableDetailValue value={occurrenceContext?.start_time || event?.start_time || ""} />
                        </div>
                        <div className="event-detail-info-item">
                          <strong>Fin</strong>
                          <CopyableDetailValue value={occurrenceContext?.end_time || event?.end_time || ""} />
                        </div>
                        <div className="event-detail-info-item">
                          <strong>Fuseau horaire</strong>
                          <CopyableDetailValue value={occurrenceContext?.timezone || event?.timezone || ""} />
                        </div>
                      </div>
                      {RECURRING_EVENTS_V1_ENABLED && hasEventRecurrence(event?.recurrence) ? (
                        <div className="event-upcoming-occurrences">
                          <strong>Prochaines occurrences</strong>
                          <div className="event-upcoming-occurrence-list">
                            {displayedUpcomingOccurrences.map((occurrence) => (
                              <button
                                key={occurrence.key}
                                type="button"
                                className={`event-upcoming-occurrence${occurrenceContext?.key === occurrence.key ? " is-current" : ""}`}
                                onClick={() =>
                                  startTransition(() =>
                                    navigate(
                                      routes.eventDetailPath(
                                        event?.id || "",
                                        `occurrence=${encodeURIComponent(occurrence.date)}&source=${encodeURIComponent(
                                          occurrence.source_date
                                        )}`
                                      )
                                    )
                                  )
                                }
                              >
                                <strong>{formatEventDate(occurrence.date)}</strong>
                                <span>
                                  {[occurrence.start_time || "Heure a definir", occurrence.end_time || ""]
                                    .filter(Boolean)
                                    .join(" - ")}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </section>

                    <section className="event-detail-section">
                      <h3 className="event-detail-section-title">Lieu</h3>
                      <div className="event-detail-info-grid">
                        <div className="event-detail-info-item">
                          <strong>Nom du lieu</strong>
                          <CopyableDetailValue value={event?.location_name || ""} />
                        </div>
                        <div className="event-detail-info-item">
                          <strong>N°</strong>
                          <CopyableDetailValue value={event?.location_street_number || ""} />
                        </div>
                        <div className="event-detail-info-item">
                          <strong>Adresse</strong>
                          <CopyableDetailValue value={event?.location_street || event?.location_address || ""} />
                        </div>
                        <div className="event-detail-info-item">
                          <strong>Code postal</strong>
                          <CopyableDetailValue value={event?.location_postal_code || ""} />
                        </div>
                        <div className="event-detail-info-item">
                          <strong>Ville</strong>
                          <CopyableDetailValue value={event?.location_city || ""} />
                        </div>
                      </div>
                    </section>

                    <section className="event-detail-section">
                      <h3 className="event-detail-section-title">Informations</h3>
                      <div className="event-detail-copy-stack">
                        <div className="event-detail-info-item">
                          <strong>Description</strong>
                          <CopyableDetailValue value={event?.description || ""} />
                        </div>
                        <div className="event-detail-info-item">
                          <strong>Infos pratiques</strong>
                          <CopyableDetailValue value={event?.practical_info || ""} />
                        </div>
                      </div>
                    </section>

                    <section className="event-detail-section">
                      <h3 className="event-detail-section-title">Prix</h3>
                      <div className="event-detail-info-grid event-detail-info-grid-compact">
                        <div className="event-detail-info-item">
                          <strong>Prix affiché</strong>
                          <CopyableDetailValue value={event ? formatEventPrice(event) : ""} />
                        </div>
                        <div className="event-detail-info-item">
                          <strong>Libellé</strong>
                          <CopyableDetailValue value={event?.price.label || ""} />
                        </div>
                        <div className="event-detail-info-item">
                          <strong>Montant</strong>
                          <CopyableDetailValue
                            value={
                              typeof event?.price.amount_cents === "number"
                                ? (event.price.amount_cents / 100).toFixed(2).replace(".", ",")
                                : ""
                            }
                          />
                        </div>
                        <div className="event-detail-info-item">
                          <strong>Devise</strong>
                          <CopyableDetailValue value={event?.price.currency || ""} />
                        </div>
                      </div>
                    </section>

                    <section className="event-detail-section">
                      <h3 className="event-detail-section-title">Illustration</h3>
                      {hasIllustrationPreview ? (
                        <div className="event-detail-illustration-card">
                          <div className="event-illustration-preview">
                            <img
                              src={event!.illustration.reference}
                              alt={event!.illustration.alt_text || event!.display_name || "Illustration évènement"}
                            />
                          </div>
                          <div className="event-detail-info-grid">
                            <div className="event-detail-info-item">
                              <strong>Type</strong>
                              <CopyableDetailValue value={event?.illustration.kind || ""} />
                            </div>
                            <div className="event-detail-info-item">
                              <strong>Référence</strong>
                              <CopyableDetailValue value={event?.illustration.reference || ""} />
                            </div>
                            <div className="event-detail-info-item event-detail-info-item-full">
                              <strong>Texte alternatif</strong>
                              <CopyableDetailValue value={event?.illustration.alt_text || ""} />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="event-detail-empty-block">Aucune illustration pour cet évènement.</div>
                      )}
                    </section>
                    </details>
                </div>
              </div>
            )}
          </EditorPanel>

          <MediaLibraryModal
            opened={isMediaModalOpen}
            title="Choisir une image pour l'évènement"
            subtitle="Sélectionnez un visuel existant ou importez-le depuis votre ordinateur."
            confirmLabel="Utiliser pour l'évènement"
            usage={{
              source_app: "event-planner",
              usage_context: "event-illustration",
              usage_label: eventForm.display_name.trim() || eventForm.internal_name.trim() || "Illustration évènement",
            }}
            onClose={() => setIsMediaModalOpen(false)}
            onConfirm={(item: MediaLibraryItem) => {
              updateEventFormField("illustration_kind", "external_url");
              updateEventFormField("illustration_reference", item.public_url);
              updateEventFormField(
                "illustration_alt_text",
                item.alt_text.trim() || eventForm.display_name.trim() || item.original_name
              );
            }}
          />
          {RECURRING_EVENTS_V1_ENABLED ? (
            <>
              <RecurrenceEditorModal
                isOpen={isRecurrenceModalOpen}
                summary={formatRecurrenceSummary(buildRecurrenceFromEditorState(recurrenceEditorState, eventForm))}
                anchorDate={eventForm.date}
                editorState={recurrenceEditorState}
                previewOccurrences={recurrencePreviewOccurrences}
                onClose={() => setIsRecurrenceModalOpen(false)}
                onApply={() => {
                  updateEventFormField("recurrence", buildRecurrenceFromEditorState(recurrenceEditorState, eventForm));
                  setIsRecurrenceModalOpen(false);
                }}
                onChange={(updater) => setRecurrenceEditorState((current) => updater(current))}
              />
              <OccurrenceScopeModal
                isOpen={Boolean(pendingOccurrenceSave)}
                occurrence={pendingOccurrenceSave?.occurrence || null}
                onClose={() => setPendingOccurrenceSave(null)}
                onConfirmOccurrence={() => confirmOccurrenceScope("occurrence")}
                onConfirmFuture={() => confirmOccurrenceScope("future")}
              />
            </>
          ) : null}

        </div>
        {isDetailView && !isEditingForm ? (
          <aside className="event-detail-side">
            <section className="event-detail-sidebar-panel">
              <div className="event-detail-sidebar-head">
                <h2>Diffusion</h2>
                {event?.publication_status === "draft" ? (
                  <button
                    type="button"
                    className="button button-primary"
                    disabled={saveFormMutation.isPending}
                    onClick={handlePublishEvent}
                  >
                    {saveFormMutation.isPending ? "Publication..." : "Publier"}
                  </button>
                ) : null}
              </div>
              <span className={`pill event-publication-pill event-publication-pill-${event?.publication_status || "draft"}`}>
                {publicationStatusLabel(event?.publication_status || "draft")}
              </span>
              <p className="muted">{publicationMessage}</p>
              {recurringPublishedMessage ? <p className="muted">{recurringPublishedMessage}</p> : null}

              <div className="event-detail-subpanel">
                <h3>Facebook</h3>
                <input
                  value={facebookLinkDraft}
                  onChange={(event) => setFacebookLinkDraft(event.target.value)}
                  placeholder="https://facebook.com/events/..."
                />
                <div className="event-inline-actions">
                  <button
                    type="button"
                    className="button button-small"
                    disabled={saveFormMutation.isPending || facebookLinkDraft.trim() === eventForm.facebook_url.trim()}
                    onClick={handleSaveFacebookLink}
                  >
                    {saveFormMutation.isPending && pendingSaveIntentRef.current === "facebook" ? "Enregistrement..." : "Enregistrer"}
                  </button>
                  {eventForm.facebook_url ? (
                    <a href={eventForm.facebook_url} target="_blank" rel="noreferrer" className="external-link">
                      Ouvrir la page Facebook
                    </a>
                  ) : (
                    <span className="muted">Aucun lien Facebook rattaché pour l’instant.</span>
                  )}
                </div>
              </div>

              <div className="event-detail-subpanel">
                <h3>Connecteurs</h3>
                {connectorStatus.message ? <p className={statusClassName(connectorStatus)}>{connectorStatus.message}</p> : null}
                {RECURRING_EVENTS_V1_ENABLED && hasEventRecurrence(event?.recurrence) ? (
                  <p className="muted">Les synchronisations Odoo/CalDAV d'une série récurrente seront activées dans une prochaine itération.</p>
                ) : null}
                {visibleConnectors.length > 0 ? (
                  <div className="detail-connector-grid">
                    {visibleConnectors.map((connector) => (
                      <div key={connector.connector_key} className="detail-connector-card">
                        <span className={connectorStatusClassName(connector.status)}>
                          {connector.label} · {connectorStatusLabel(connector.status)}
                        </span>
                        <p>ID externe: {connector.external_id || "-"}</p>
                        <p>Dernière synchro: {connector.last_synced_at || "-"}</p>
                        {connector.external_url ? (
                          <p>
                            <a href={connector.external_url} target="_blank" rel="noreferrer" className="external-link">
                              Ouvrir la destination
                            </a>
                          </p>
                        ) : null}
                        {connector.last_error ? <p className="muted">{connector.last_error}</p> : null}
                        {connector.connector_key === "odoo" || connector.connector_key === "caldav" ? (
                          <button
                            type="button"
                            className="button button-small"
                            disabled={
                              syncConnectorMutation.isPending ||
                              event!.publication_status === "draft" ||
                              hasEventRecurrence(event!.recurrence)
                            }
                            onClick={() => syncConnectorMutation.mutate(connector.connector_key)}
                          >
                            <IconLabel
                              icon="refresh"
                              label={
                                activeSyncConnectorKey === connector.connector_key && syncConnectorMutation.isPending
                                  ? "Synchro..."
                                  : connector.status === "synced"
                                    ? "Resynchroniser"
                                    : connector.connector_key === "odoo"
                                      ? "Synchroniser Odoo"
                                      : "Synchroniser CalDAV"
                              }
                            />
                          </button>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="muted">Aucun connecteur actif configuré pour cet évènement.</p>
                )}
              </div>
            </section>

            <section className="event-detail-sidebar-panel">
              <div className="event-detail-sidebar-head">
                <h2>Suivi</h2>
              </div>
              <div className="event-detail-side-meta">
                <div className="event-detail-info-item">
                  <strong>Statut</strong>
                  <CopyableDetailValue value={publicationStatusLabel(event!.publication_status)} />
                </div>
                <div className="event-detail-info-item">
                  <strong>Révision</strong>
                  <CopyableDetailValue value={String(event!.revision)} />
                </div>
                <div className="event-detail-info-item">
                  <strong>Créé le</strong>
                  <CopyableDetailValue value={event!.created_at || ""} />
                </div>
                <div className="event-detail-info-item">
                  <strong>Mis à jour le</strong>
                  <CopyableDetailValue value={event!.updated_at || ""} />
                </div>
              </div>
            </section>
          </aside>
        ) : null}
        {isEditingForm ? (
          <aside className="event-detail-side event-edit-help-side">
            <section className="event-detail-sidebar-panel">
              <div className="event-detail-sidebar-head">
                <h2>Aide contextuelle</h2>
              </div>
              <div className="event-detail-side-meta">
                <div className="event-detail-info-item">
                  <strong>Statut initial</strong>
                  <span>
                    {isOccurrenceEditMode
                      ? "Vous décalez ici l'horaire d'une occurrence. Le choix de portée intervient après validation."
                      : mode === "create"
                      ? "L’évènement sera enregistré en brouillon, puis publié depuis la fiche détail."
                      : "La diffusion et le lien Facebook se gèrent depuis la vue détail, pas depuis ce formulaire."}
                  </span>
                </div>
                <div className="event-detail-info-item">
                  <strong>Champs utiles pour publier</strong>
                  <span>{isOccurrenceEditMode ? "Date, heure de début, heure de fin et fuseau horaire." : "Nom d'affichage, date, heure de début et fuseau horaire."}</span>
                </div>
                <div className="event-detail-info-item">
                  <strong>Organisation</strong>
                  <span>Renseignez d’abord le contenu, puis finalisez la diffusion une fois la fiche enregistrée.</span>
                </div>
              </div>
            </section>
          </aside>
        ) : null}
      </main>
    </EditorPage>
  );
}

export function createEventPlannerRoutes(routes: EventPlannerRouteConfig = EVENT_PLANNER_STANDALONE_ROUTES) {
  return [
    {
      path: routes.listPath,
      element: withEventPlannerRouteConfig(<EventListPage />, routes),
    },
    {
      path: routes.createPath,
      element: withEventPlannerRouteConfig(<EventEditorPage mode="create" />, routes),
    },
    {
      path: routes.settingsPath,
      element: withEventPlannerRouteConfig(<EventSettingsPage />, routes),
    },
    {
      path: routes.detailPattern,
      element: withEventPlannerRouteConfig(<EventEditorPage mode="detail" />, routes),
    },
  ];
}

export function createHostedEventPlannerRoutes() {
  return createEventPlannerRoutes(EVENT_PLANNER_HOSTED_ROUTES).filter(
    (route) => route.path !== EVENT_PLANNER_HOSTED_ROUTES.settingsPath
  );
}

export function HostedEventSettingsPage() {
  return withEventPlannerRouteConfig(<EventSettingsPage embedded />, EVENT_PLANNER_HOSTED_ROUTES);
}

const router = createBrowserRouter(createEventPlannerRoutes(), {
  basename: resolveMountedBasename("event-planner"),
});

export function App() {
  return <RouterProvider router={router} />;
}
